"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ThemeProvider } from "@/components/theme-provider";
import { useTheme } from "next-themes";
import {
  Download,
  ArrowLeft,
  Plus,
  Trash2,
  Move,
  Save,
  Grid,
  Play,
  Pause,
  ZoomIn,
  ZoomOut,
  LocateFixed,
  Maximize,
  Upload,
  Info,
  History,
  Gamepad2,
  Copy,
  Clipboard,
  ChevronDown,
  Pencil,
  Star,
  X,
  Check,
  ScreenShare,
  ScreenShareOff,
  Monitor,
  Hand,
  MousePointer,
} from "lucide-react";
import { useLanguage } from "@/lib/i18n/language-provider";
import { AppLogo } from "@/components/app-logo";
import { useIsMobile } from "@/hooks/use-mobile";
import { useDelayedGridCounter } from "@/hooks/use-delayed-grid-counter";
import { BurgerMenu } from "@/components/ui/burger-menu";
import useElectron from "@/hooks/useElectron";
import { useConfig } from "@/lib/config-provider";
import Image from "next/image";
import { LanguageSwitcher } from "@/components/language-switcher";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { drawStandardPattern } from "@/lib/standard-patterns";
import { renderPixelMapMode } from "@/lib/patterns/render-overlays";
import { useCallback as useMemoizedCallback } from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MathInput } from "@/app/components/math-input";
import Link from "next/link";
import SmartLink from "@/components/smart-link";
import { SnakePosition, SnakeGameState, useSnakeGame } from "@/components/ui/snake-game";
import { useAuth } from "@/lib/auth-provider";
import { Paywall } from "@/components/Paywall";
import { useWebCodecsVideoExport } from "@/hooks/use-webcodecs-video-export";
import { FfmpegExportDialog } from "@/components/ffmpeg-export-dialog";
import useTauri from "@/hooks/useTauri";
import { Zap, CheckCircle, Video, AlertCircle, Loader2, MonitorCog } from "lucide-react";
import { FuturisticMessage } from "@/components/futuristic-message";
import { getApiUrl } from "@/lib/api-url";
import { captureEvent } from "@/lib/posthog-helper";

// Précharger l'image du logo
const logoImageUrl = typeof window !== 'undefined' && window.location?.protocol === 'file:' ? './icon_full_res.png' : '/icon_full_res.png';

// Types pour éléments texte et logo (comme la page principale)
interface TextElement {
  id: string;
  text: string;
  x: number;
  y: number;
  fontSize: number;
  fontFamily: string;
  fontWeight: number;
  fontStyle: string;
  textAlign: string;
  color: string;
  strokeWidth?: number;
}

interface LogoElement {
  id: string;
  image: HTMLImageElement;
  x: number;
  y: number;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
  opacity: number;
}

const patternTypes = [
  { value: "snakeGame", label: "🐍 Snake Game" },
  { value: "gradient", label: "Dégradé" },
  { value: "gradient4", label: "Dégradé 4 Couleurs" },
  { value: "solid", label: "Couleur Unie" },
  { value: "checkerboard", label: "Damier" },
  { value: "rainbow", label: "Arc-en-ciel" },
  { value: "colorBars", label: "Barres Colorées" },
  { value: "pulse", label: "Pulsation" },
  { value: "spiral", label: "Spirale" },
  { value: "orangeTeal", label: "Orange & Teal" },
  { value: "magentaSummer", label: "Magenta Summer" },
  { value: "summer", label: "Summer" },
  { value: "bluePurple", label: "Blue Purple" },
  // Motifs conservés et améliorés
  { value: "plasma", label: "Plasma" },
  { value: "neon", label: "Néon" },
  { value: "matrix", label: "Matrix" },
  { value: "water", label: "Eau" },
  { value: "laser", label: "Laser" },
  { value: "hypnotic", label: "Hypnotique" },
  { value: "diamond", label: "Diamant" },
  { value: "galaxy", label: "Galaxie" },
  { value: "lightning", label: "Éclair" },
  { value: "cosmic", label: "Cosmique" },
  { value: "vortex", label: "Vortex" },
  { value: "crystalline", label: "Cristallin" },
  { value: "aurora", label: "Aurore" },
  { value: "cyberpunk", label: "Cyberpunk" },
  { value: "quantum", label: "Quantique" },
  { value: "hologram", label: "Hologramme" },
  { value: "neural", label: "Réseau Neural" },
  { value: "earthquake", label: "Séisme" },
  { value: "sunset", label: "Coucher de Soleil" },
  { value: "radar", label: "Radar" },
  { value: "heartbeat", label: "Battement Cardiaque" },
  { value: "virus", label: "Virus" },
  { value: "electron", label: "Électron" },
  { value: "proton", label: "Proton" },
  { value: "neutron", label: "Neutron" },
  { value: "frequency", label: "Fréquence" },
  { value: "amplitude", label: "Amplitude" },
  { value: "lens", label: "Lentille" },
  { value: "holodeck", label: "Holodeck" },
  { value: "warp", label: "Distorsion" },
  { value: "portal", label: "Portail" },
  { value: "paradox", label: "Paradoxe" },
  { value: "pi", label: "Pi" },
  { value: "order", label: "Ordre" },
  { value: "velocity", label: "Vélocité" },
  { value: "force", label: "Force" },
];

// Fontes, styles et alignements comme sur la page principale
const fontFamilies = [
  { name: "Arial", value: "Arial, sans-serif" },
  { name: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { name: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { name: "Courier New", value: "'Courier New', Courier, monospace" },
  { name: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { name: "Georgia", value: "Georgia, 'Times New Roman', serif" },
  { name: "Palatino", value: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" },
  { name: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { name: "Trebuchet MS", value: "'Trebuchet MS', Helvetica, sans-serif" },
  { name: "Lucida Console", value: "'Lucida Console', Monaco, monospace" },
  { name: "Impact", value: "Impact, Charcoal, sans-serif" },
  { name: "Comic Sans MS", value: "'Comic Sans MS', cursive" },
  { name: "Roboto", value: "'Roboto', sans-serif" },
  { name: "Open Sans", value: "'Open Sans', sans-serif" },
  { name: "Lato", value: "'Lato', sans-serif" },
  { name: "Montserrat", value: "'Montserrat', sans-serif" },
  { name: "Oswald", value: "'Oswald', sans-serif" },
  { name: "Source Sans Pro", value: "'Source Sans Pro', sans-serif" },
  { name: "Raleway", value: "'Raleway', sans-serif" },
  { name: "PT Sans", value: "'PT Sans', sans-serif" },
  { name: "Ubuntu", value: "'Ubuntu', sans-serif" },
  { name: "Nunito", value: "'Nunito', sans-serif" },
];

const fontStyles = [
  { name: "Normal", value: "normal" },
  { name: "Italique", value: "italic" },
  { name: "Oblique", value: "oblique" },
];

const textAlignments = [
  { name: "Gauche", value: "left" },
  { name: "Centre", value: "center" },
  { name: "Droite", value: "right" },
];

// Types pour les grilles
interface GridConfig {
  id: string;
  name: string;
  width: number;
  height: number;
  gridSize: number;
  x: number;
  y: number;
  patternType: string;
  backgroundColor: string;
  showGrid: boolean;
  animationSpeed: number;
  sonarSpeed: number;
  patternSeed: number; // Graine de motif pour décaler le départ de l'animation (comme grid/page.tsx)
  gradientLoopDuration: number; // Durée de la boucle du dégradé en secondes (3, 5, ou 10)
  showSonar: boolean;
  sonarRevealMode: boolean; // Mode sonar avancé (révélation)
  rampDirection: "horizontal" | "vertical";
  standardPatternType: string;
  showLogo: boolean;
  showName: boolean;
  nameOffsetX?: number; // Offset X relatif à la grille pour la position du nom (si undefined, utilise la position par défaut)
  nameOffsetY?: number; // Offset Y relatif à la grille pour la position du nom (si undefined, utilise la position par défaut)
  showDimensions: boolean;
  dimensionsFontSize?: number;
  showLEDNumbers: boolean;
  showTechnicalDetails: boolean; // Nouvelle option unifiée
  showDefaultLogo: boolean; // Option pour afficher le logo VisualGrid au centre
  dalleConfigMode: "simple" | "custom";
  dalleWidth: number;
  dalleHeight: number;
  borderWidth: number;
  logoScale: number;
  textScale: number;
  activePatternMode: "custom" | "solid" | "standard";
  textElements?: TextElement[];
  logoElements?: LogoElement[];
  enableAnamorphosis: boolean; // Mode anamorphose pour compensation LST (par mire)
  pixelMapMode?: {
    enabled: boolean;
    mainScreenText: string;
    posX: number;
    posY: number;
    resW: number;
    resH: number;
    panelW: number;
    panelH: number;
    size: number;
  }; // Mode Pixel Map pour les utilisateurs Pro
  // Propriétés Snake Game
  snakeGameState?: {
    snake: SnakePosition[];
    food: SnakePosition;
    direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
    isGameOver: boolean;
    isVictory: boolean;
    score: number;
    isPlaying: boolean;
    visitedCells: Set<string>;
    gameStartDimensions?: {
      cols: number;
      rows: number;
      totalPanels: number;
      gridSize: number;
      gridWidth: number;
      gridHeight: number;
    };
  };
  // Contrôles pour le mode multijoueur Snake
  snakeControlSet?: 1 | 2 | 3 | 4; // Grille 1: ZQSD, 2: Flèches, 3: IKJL, 4: TGFH
}

// Type pour la configuration du canvas
interface CanvasConfig {
  width: number;
  height: number;
  allowFullscreenScroll: boolean;
  stretchFullscreen: boolean;
  canvasPattern: string; // 'none' | any patternType value
  canvasAnimationSpeed: number;
  canvasPatternSeed: number;
  canvasGradientLoopDuration: number;
}

export default function MultiGridPage() {
  // État pour éviter les problèmes d'hydration
  const [mounted, setMounted] = useState(false);
  
  // Auth check - require premium (only if Supabase is configured)
  const { isPro, loading: authLoading } = useAuth();
  
  // Check if Supabase is configured
  const supabaseConfigured = !!(typeof window !== 'undefined' && process.env.NEXT_PUBLIC_SUPABASE_URL);
  
  // Hooks
  const isMobile = useIsMobile();
  const gridCounter = useDelayedGridCounter({ mode: 'multiGridMode' });
  
  // Références
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const draggedGridRef = useRef<string | null>(null);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // État pour tracker si le canvas est monté (pour forcer le rendu après montage)
  const [canvasMounted, setCanvasMounted] = useState(false);
  // États - utilise maintenant le contexte global pour la configuration du canvas
  const { multiGridConfig: canvasConfig, setMultiGridConfig: setCanvasConfig } = useConfig();
  const [grids, setGrids] = useState<GridConfig[]>([]);
  const [selectedGridId, setSelectedGridId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Nouveaux états pour les fonctionnalités
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [activeTool, setActiveTool] = useState<'arrow' | 'hand'>('arrow');
  const [isAnimating, setIsAnimating] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  // Ref vers la fonction de rendu pour une boucle stable
  const renderCanvasRef = useRef<(opts?: { forExport?: boolean; exportTime?: number }) => void>(() => {});
  // Ref pour le pan avec clic molette
  const panDragRef = useRef<{ startX: number; startY: number; startOffsetX: number; startOffsetY: number } | null>(null);

  // État pour sauvegarder les valeurs UI avant désactivation automatique pour mires standards (par grille)
  const [previousGridUIStates, setPreviousGridUIStates] = useState<{
    [gridId: string]: {
      showTechnicalDetails: boolean;
      showGrid: boolean;
      showLogo: boolean;
      showLEDNumbers: boolean;
      showDimensions: boolean;
      showSonar: boolean;
    }
  }>({});

  // Hooks
  const { theme, setTheme } = useTheme();
  const { t, language } = useLanguage();
  const { isElectronApp: isElectronAppCheck, exportCanvas: exportCanvasElectron, saveConfig: saveConfigElectron, loadConfig: loadConfigElectron, importConfigFile: importConfigFileElectron, exportConfigFile: exportConfigFileElectron, savedConfigs: savedConfigsElectron, loadConfigList: loadConfigListElectron, deleteConfig: deleteConfigElectron } = useElectron();
  const { isTauriApp, exportCanvas: exportCanvasTauri, saveConfig: saveConfigTauri, loadConfig: loadConfigTauri, importConfigFile: importConfigFileTauri, exportConfigFile: exportConfigFileTauri, savedConfigs: savedConfigsTauri, loadConfigList: loadConfigListTauri, deleteConfig: deleteConfigTauri, getDisplays: getTauriDisplays, openExternalDisplay: openTauriExternalDisplay, closeExternalDisplay: closeTauriExternalDisplay } = useTauri();
  
  // Utiliser les fonctions Tauri si on est sur Tauri, sinon Electron/Web
  const exportCanvas = isTauriApp ? exportCanvasTauri : exportCanvasElectron;
  const saveConfig = isTauriApp ? saveConfigTauri : saveConfigElectron;
  const loadConfig = isTauriApp ? loadConfigTauri : loadConfigElectron;
  const importConfigFile = isTauriApp ? importConfigFileTauri : importConfigFileElectron;
  const exportConfigFile = isTauriApp ? exportConfigFileTauri : exportConfigFileElectron;
  const savedConfigs = isTauriApp ? savedConfigsTauri : savedConfigsElectron;
  const loadConfigList = isTauriApp ? loadConfigListTauri : loadConfigListElectron;
  const deleteConfig = isTauriApp ? deleteConfigTauri : deleteConfigElectron;
  const isElectronApp = !isTauriApp && isElectronAppCheck;
  const isDesktopApp = isTauriApp || isElectronAppCheck;

  // États pour l'affichage multi-écran
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [externalDisplayWindow, setExternalDisplayWindow] = useState<Window | { id: any; isElectron?: boolean; isTauri?: boolean; close: () => any } | null>(null);
  const [isExternalDisplayConnected, setIsExternalDisplayConnected] = useState(false);
  const [isExternalDisplayFullscreen, setIsExternalDisplayFullscreen] = useState(false);
  const [availableScreens, setAvailableScreens] = useState<any[]>([]);
  const [selectedScreenIndex, setSelectedScreenIndex] = useState(0);
  const [isScreensLoaded, setIsScreensLoaded] = useState(false);
  const [isExternalDisplay, setIsExternalDisplay] = useState(false);
  const isExternalDisplayRef = useRef(false);
  const [disablePreviewWhenExternal, setDisablePreviewWhenExternal] = useState(false);
  const defaultLogoRef = useRef<HTMLImageElement | null>(null);
  
  // Marquer comme monté après le premier rendu côté client
  useEffect(() => {
    setMounted(true);
  }, []);

  // Gérer les paramètres d'URL et le mode d'affichage externe
  useEffect(() => {
    if (typeof window === "undefined" || !mounted) return;

    const urlParams = new URLSearchParams(window.location.search);
    const forceExternal = urlParams.get("forceExternal") === "true";

    isExternalDisplayRef.current = forceExternal;

    // Envoyer un signal READY quand la page externe est chargée
    if (forceExternal) {
      setTimeout(() => {
        const channel = new BroadcastChannel('visualgrid-sync');
        channel.postMessage({
          type: 'EXTERNAL_DISPLAY_READY',
          data: { timestamp: Date.now() }
        });
        channel.close();
      }, 1000);
    }

    setIsExternalDisplay(forceExternal);

    // Si on est en mode externe, mettre automatiquement en plein écran
    if (forceExternal) {
      // Attendre un peu pour que la page soit chargée
      setTimeout(() => {
        if (fullscreenContainerRef.current) {
          fullscreenContainerRef.current.requestFullscreen().catch((error) => {
            // console.error('Erreur lors de la mise en plein écran:', error);
          });
        }
      }, 500);
    }
  }, [mounted]);

  // Charger le logo par défaut (similaire à la page Grid)
  useEffect(() => {
    if (!mounted) return;

    const loadDefaultLogo = () => {
      // Utiliser document.createElement pour éviter le conflit avec l'import Image de Next.js
      const logo = document.createElement('img');
      logo.onload = () => {
        defaultLogoRef.current = logo;
        // Redessiner le canvas si on est en mode externe
        if (isExternalDisplay && renderCanvasRef.current) {
          renderCanvasRef.current();
        }
      };

      // Déterminer le chemin correct selon l'environnement
      let logoPath;
      if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
        logoPath = 'icon_full_res.png';
      } else {
        logoPath = '/icon_full_res.png';
      }

      logo.src = logoPath;
    };

    loadDefaultLogo();
  }, [mounted, isExternalDisplay]);

  // Détecter les changements de plein écran et envoyer l'état
  useEffect(() => {
    if (!isExternalDisplay) return;

    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isFullscreen);
      
      // Envoyer l'état du plein écran à la page principale
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'EXTERNAL_DISPLAY_STATE',
          data: { isFullscreen }
        });
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    // Envoyer l'état initial
    handleFullscreenChange();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isExternalDisplay]);

  // Gérer CTRL+W pour fermer la fenêtre externe
  useEffect(() => {
    if (typeof window === "undefined" || !mounted) return;

    const urlParams = new URLSearchParams(window.location.search);
    const forceExternal = urlParams.get("forceExternal") === "true";

    if (!forceExternal) return;

    const handleKeyDown = async (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
        e.preventDefault();
        e.stopPropagation();

        if (isTauriApp && closeTauriExternalDisplay) {
          try {
            const { getCurrentWebviewWindow } = await import('@tauri-apps/api/window');
            const currentWindow = getCurrentWebviewWindow();
            if (currentWindow) {
              const windowLabel = currentWindow.label();
              if (windowLabel.startsWith('external_')) {
                await closeTauriExternalDisplay(windowLabel);
              } else {
                await currentWindow.close();
              }
            }
          } catch (error) {
            // console.error('Erreur lors de la fermeture Tauri:', error);
          }
        } else if (isElectronApp && (window as any).electronAPI) {
          try {
            window.close();
          } catch (error) {
            // console.error('Erreur lors de la fermeture Electron:', error);
          }
        } else {
          window.close();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mounted, isTauriApp, isElectronApp, closeTauriExternalDisplay]);

  // Détecter les écrans disponibles
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const detectScreens = async () => {
      const screens = [];
      let useTauriAPI = false;
      let useElectronAPI = false;

      // Priorité 1: API Tauri
      if (isTauriApp && getTauriDisplays) {
        try {
          const tauriDisplays = await getTauriDisplays();
          if (Array.isArray(tauriDisplays) && tauriDisplays.length > 0) {
            tauriDisplays.forEach((screen: any) => {
              screens.push({
                id: screen.id,
                name: screen.name || (screen.is_primary ? t("external.screenName") : t("external.screenNumber", { number: screen.id })),
                width: screen.width,
                height: screen.height,
                left: screen.left,
                top: screen.top,
                isPrimary: screen.is_primary || false,
                scaleFactor: screen.scale_factor || 1,
                source: 'tauri'
              });
            });
            useTauriAPI = true;
          }
        } catch (error) {
          // console.error('Erreur lors de la récupération des écrans Tauri:', error);
        }
      }

      // Priorité 2: API Electron
      if (!useTauriAPI && isElectronApp && typeof window !== 'undefined' && (window as any).electronAPI) {
        try {
          const electronScreens = await (window as any).electronAPI.getDisplays();
          electronScreens.forEach((screen: any) => {
            screens.push({
              id: screen.id,
              name: screen.name || (screen.isPrimary ? t("external.screenName") : t("external.screenNumber", { number: screen.id })),
              width: screen.width,
              height: screen.height,
              left: screen.left,
              top: screen.top,
              isPrimary: screen.isPrimary,
              scaleFactor: screen.scaleFactor || 1,
              source: 'electron'
            });
          });
          useElectronAPI = true;
          if ((window as any).electronAPI.onDisplayChanged) {
            (window as any).electronAPI.onDisplayChanged(() => {
              detectScreens();
            });
          }
        } catch (error) {
        }
      }

      // Priorité 3: API Screen Details Web
      if (!useTauriAPI && !useElectronAPI && 'getScreenDetails' in window && window.isSecureContext) {
        try {
          const screenDetails = await (window as any).getScreenDetails();
          screenDetails.screens.forEach((screen: any, index: number) => {
            screens.push({
              id: `web-screen-${index}`,
              name: screen.isPrimary ? t("external.screenName") : t("external.screenNumber", { number: index + 1 }),
              width: screen.width,
              height: screen.height,
              left: screen.left,
              top: screen.top,
              isPrimary: screen.isPrimary,
              source: 'web-api'
            });
          });
          screenDetails.addEventListener('screenschange', () => {
            detectScreens();
          });
        } catch (error) {
        }
      }

      // Fallback: Écran principal uniquement
      if (screens.length === 0) {
        screens.push({
          id: 'primary-fallback',
          name: t("external.screenName"),
          width: window.screen.width,
          height: window.screen.height,
          left: 0,
          top: 0,
          isPrimary: true,
          source: 'fallback'
        });
      }

      setAvailableScreens(screens);
      setIsScreensLoaded(true);
    };

    const timer = setTimeout(detectScreens, 100);
    return () => clearTimeout(timer);
  }, [isElectronApp, isTauriApp, getTauriDisplays, t]);
  
  // Fonction pour initialiser une configuration vierge par défaut
  const initializeDefaultEmptyConfig = useCallback(async () => {
    try {
      // Vérifier si une config vierge existe déjà
      const emptyConfigExists = savedConfigs.some(c => c.name === 'empty' || c.name === 'vide' || c.displayName?.toLowerCase().includes('vierge'));
      
      if (!emptyConfigExists && savedConfigs.length === 0) {
        // Créer une configuration vierge par défaut
        const emptyConfig = {
          version: "1.0.0",
          timestamp: new Date().toISOString(),
          canvas: {
            width: 1920,
            height: 1080,
            allowFullscreenScroll: false,
            stretchFullscreen: false,
            canvasPattern: 'none',
            canvasAnimationSpeed: 1,
            canvasPatternSeed: 0,
            canvasGradientLoopDuration: 10,
          },
          grids: [],
          multiGrid: true,
        };

        const result = await saveConfig(emptyConfig, {
          name: 'empty',
          displayName: t("config.emptyConfigName") || "Configuration vierge",
          makeDefault: false // Ne pas la définir comme défaut automatiquement, l'utilisateur peut le faire
        });

        if (result && result.success) {
          // Recharger la liste des configs
          await loadConfigList();
        }
      }
    } catch (error) {
      // Ignorer les erreurs silencieusement
    }
  }, [savedConfigs, saveConfig, loadConfigList, t]);

  // Charger la liste des configurations au démarrage (une seule fois après montage)
  useEffect(() => {
    if (mounted) {
      loadConfigList();
    }
  }, [mounted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialiser une config vierge si aucune config n'existe
  useEffect(() => {
    if (mounted && savedConfigs.length === 0) {
      // Attendre un peu pour s'assurer que loadConfigList a fini
      const timer = setTimeout(() => {
        initializeDefaultEmptyConfig();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, savedConfigs.length, initializeDefaultEmptyConfig]);
  
  // État pour le dialogue d'export vidéo
  const [showExportDialog, setShowExportDialog] = useState(false);
  
  // État pour le dialogue d'export PNG
  const [showExportPngDialog, setShowExportPngDialog] = useState(false);

  // État pour le copier/coller de grilles
  const [copiedGrid, setCopiedGrid] = useState<GridConfig | null>(null);

  // États pour la progression de l'export des grilles individuelles
  const [isExportingGrids, setIsExportingGrids] = useState(false);
  const [gridsExportProgress, setGridsExportProgress] = useState(0);
  const [currentGridExporting, setCurrentGridExporting] = useState(0);
  const [totalGridsToExport, setTotalGridsToExport] = useState(0);

  // États pour le dialogue de sauvegarde de configuration
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [configDisplayName, setConfigDisplayName] = useState("");
  const [makeDefaultConfig, setMakeDefaultConfig] = useState(false);
  const [isSaveAs, setIsSaveAs] = useState(false); // Pour différencier Save et Save As dans le dialogue
  
  // États pour tracker la configuration actuellement chargée
  const [currentConfigName, setCurrentConfigName] = useState<string | null>(null);
  const [currentConfigDisplayName, setCurrentConfigDisplayName] = useState<string | null>(null);
  
  // État pour le dialogue d'import des configs sauvegardées
  const [showImportDialog, setShowImportDialog] = useState(false);
  
  // États pour le renommage de config
  const [renamingConfigName, setRenamingConfigName] = useState<string | null>(null);
  const [newConfigDisplayName, setNewConfigDisplayName] = useState("");
  
  // États pour l'édition des noms de grilles
  const [editingGridId, setEditingGridId] = useState<string | null>(null);
  const [editingGridName, setEditingGridName] = useState("");
  const [showEditNameDialog, setShowEditNameDialog] = useState(false);
  const [editingGridIdFromCanvas, setEditingGridIdFromCanvas] = useState<string | null>(null);
  
  // Recharger la liste des configs quand le dialogue d'import s'ouvre
  useEffect(() => {
    if (showImportDialog) {
      loadConfigList();
    }
  }, [showImportDialog]);

  // Fonction pour mettre à jour une grille (déplacée ici pour être accessible dans useMultiSnakeGame)
  const updateGrid = useCallback((id: string, updates: Partial<GridConfig>) => {
    setGrids((prev) =>
      prev.map((grid) => {
        if (grid.id !== id) return grid;

        // Créer une copie des mises à jour
        const sanitizedUpdates = { ...updates };

        // S'assurer que les dimensions et positions sont des nombres entiers
        if ("width" in sanitizedUpdates) {
          sanitizedUpdates.width = Math.floor(sanitizedUpdates.width as number);
        }
        if ("height" in sanitizedUpdates) {
          sanitizedUpdates.height = Math.floor(
            sanitizedUpdates.height as number,
          );
        }
        if ("x" in sanitizedUpdates) {
          sanitizedUpdates.x = Math.floor(sanitizedUpdates.x as number);
        }
        if ("y" in sanitizedUpdates) {
          sanitizedUpdates.y = Math.floor(sanitizedUpdates.y as number);
        }
        if ("gridSize" in sanitizedUpdates) {
          sanitizedUpdates.gridSize = Math.floor(
            sanitizedUpdates.gridSize as number,
          );
        }
        if ("dalleWidth" in sanitizedUpdates) {
          sanitizedUpdates.dalleWidth = Math.floor(
            sanitizedUpdates.dalleWidth as number,
          );
        }
        if ("dalleHeight" in sanitizedUpdates) {
          sanitizedUpdates.dalleHeight = Math.floor(
            sanitizedUpdates.dalleHeight as number,
          );
        }

        // Auto-disable grid, logo, sonar, and name when Snake mode is selected (like main page)
        if (sanitizedUpdates.patternType === 'snakeGame') {
          sanitizedUpdates.showTechnicalDetails = false;
          sanitizedUpdates.showGrid = false;
          sanitizedUpdates.showLogo = false;
          sanitizedUpdates.showSonar = false;
          sanitizedUpdates.showName = false;
        }

        const nextGrid = { ...grid, ...sanitizedUpdates };
        const pixelMapMode = sanitizedUpdates.pixelMapMode ?? grid.pixelMapMode;

        if (pixelMapMode) {
          const shouldSyncPixelMapMetrics = !sanitizedUpdates.pixelMapMode || [
            "name",
            "width",
            "height",
            "gridSize",
            "dalleConfigMode",
            "dalleWidth",
            "dalleHeight",
          ].some((key) => key in sanitizedUpdates);
          const nextPixelMapMode = {
            ...pixelMapMode,
            mainScreenText: sanitizedUpdates.name
              ? sanitizedUpdates.name
              : pixelMapMode.mainScreenText,
            ...(shouldSyncPixelMapMetrics
              ? {
                  resW: nextGrid.width,
                  resH: nextGrid.height,
                  posX: nextGrid.x,
                  posY: nextGrid.y,
                  panelW: nextGrid.dalleConfigMode === "custom" && nextGrid.dalleWidth > 0
                    ? nextGrid.dalleWidth
                    : nextGrid.gridSize,
                  panelH: nextGrid.dalleConfigMode === "custom" && nextGrid.dalleHeight > 0
                    ? nextGrid.dalleHeight
                    : nextGrid.gridSize,
                }
              : {}),
          };

          sanitizedUpdates.pixelMapMode = nextPixelMapMode;

          if (nextPixelMapMode.enabled) {
            sanitizedUpdates.showDefaultLogo = false;
            sanitizedUpdates.showName = false;
          }
        }

        return { ...grid, ...sanitizedUpdates };
      }),
    );
    
    // Signaler une modification de grille
    gridCounter.onActivity(false); // false = modification
  }, [gridCounter]);

  // Hook Snake Game multijoueur
  const useMultiSnakeGame = () => {
    // Initialiser les instances Snake pour les grilles ayant le motif snakeGame
    const snakeInstances = grids.filter(grid => grid.patternType === 'snakeGame');
    
    // Initialiser un Snake Game pour une grille spécifique
    const initializeSnakeForGrid = useCallback((gridId: string) => {
      const grid = grids.find(g => g.id === gridId);
      if (!grid || grid.patternType !== 'snakeGame') return;

      const cols = Math.floor(grid.width / grid.gridSize);
      const rows = Math.floor(grid.height / grid.gridSize);
      
      const initialSnake = [{ x: Math.floor(cols / 2), y: Math.floor(rows / 2) }];
      
      // Générer nouvelle nourriture qui ne soit pas sur le serpent
      let initialFood: SnakePosition;
      do {
        initialFood = {
          x: Math.floor(Math.random() * cols),
          y: Math.floor(Math.random() * rows)
        };
      } while (initialSnake.some(segment => segment.x === initialFood.x && segment.y === initialFood.y));

      const newSnakeState: SnakeGameState = {
        snake: initialSnake,
        food: initialFood,
        direction: 'RIGHT',
        isGameOver: false,
        isVictory: false,
        score: 0,
        isPlaying: false,
        visitedCells: new Set([`${Math.floor(cols / 2)},${Math.floor(rows / 2)}`]),
        gameStartDimensions: {
          cols,
          rows,
          totalPanels: cols * rows,
          gridSize: grid.gridSize,
          gridWidth: grid.width,
          gridHeight: grid.height
        }
      };

      updateGrid(gridId, { snakeGameState: newSnakeState });
    }, [grids, updateGrid]);

    // Démarrer le jeu pour une grille spécifique
    const startSnakeGame = useCallback((gridId: string) => {
      const grid = grids.find(g => g.id === gridId);
      if (!grid || !grid.snakeGameState) return;

      updateGrid(gridId, {
        snakeGameState: {
          ...grid.snakeGameState,
          isPlaying: true,
          isGameOver: false,
          isVictory: false
        }
      });
    }, [grids, updateGrid]);

    // Arrêter le jeu pour une grille spécifique
    const stopSnakeGame = useCallback((gridId: string) => {
      const grid = grids.find(g => g.id === gridId);
      if (!grid || !grid.snakeGameState) return;

      updateGrid(gridId, {
        snakeGameState: {
          ...grid.snakeGameState,
          isPlaying: false
        }
      });
    }, [grids, updateGrid]);

    // Réinitialiser le jeu pour une grille spécifique
    const resetSnakeGame = useCallback((gridId: string) => {
      const grid = grids.find(g => g.id === gridId);
      if (!grid || grid.patternType !== 'snakeGame') {
        return;
      }

      const cols = Math.floor(grid.width / grid.gridSize);
      const rows = Math.floor(grid.height / grid.gridSize);
      
      const initialSnake = [{ x: Math.floor(cols / 2), y: Math.floor(rows / 2) }];
      
      // Générer nouvelle nourriture qui ne soit pas sur le serpent
      let initialFood: SnakePosition;
      do {
        initialFood = {
          x: Math.floor(Math.random() * cols),
          y: Math.floor(Math.random() * rows)
        };
      } while (initialSnake.some(segment => segment.x === initialFood.x && segment.y === initialFood.y));
      
      // Réinitialisation complète de l'état (comme dans la page principale)
      const resetState: SnakeGameState = {
        snake: initialSnake,
        food: initialFood,
        direction: 'RIGHT',
        isGameOver: false,
        isVictory: false,
        score: 0,
        isPlaying: false,
        visitedCells: new Set([`${Math.floor(cols / 2)},${Math.floor(rows / 2)}`]),
        // Conserver les dimensions de la partie précédente si elles existent
        gameStartDimensions: grid.snakeGameState?.gameStartDimensions
      };

      updateGrid(gridId, { 
        snakeGameState: resetState
      });
    }, [grids, updateGrid]);

    // Changer la direction pour une grille spécifique (IMMÉDIAT - pas de buffering)
    const changeSnakeDirection = useCallback((gridId: string, newDirection: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT') => {
      setGrids(prevGrids => 
        prevGrids.map(grid => {
          if (grid.id !== gridId || !grid.snakeGameState || !grid.snakeGameState.isPlaying) {
            return grid;
          }

          const currentDirection = grid.snakeGameState.direction;
          
          // Empêcher les demi-tours
          const opposites = {
            'UP': 'DOWN',
            'DOWN': 'UP',
            'LEFT': 'RIGHT',
            'RIGHT': 'LEFT'
          };

          if (opposites[currentDirection] !== newDirection) {
            // Appliquer la direction IMMÉDIATEMENT - pas de buffering
            return {
              ...grid,
              snakeGameState: {
                ...grid.snakeGameState,
                direction: newDirection
              }
            };
          }
          
          return grid;
        })
      );
    }, []);

    return {
      snakeInstances,
      initializeSnakeForGrid,
      startSnakeGame,
      stopSnakeGame,
      resetSnakeGame,
      changeSnakeDirection
    };
  };

  const multiSnake = useMultiSnakeGame();

  // Fonctions globales pour tous les jeux Snake
  const startAllSnakeGames = useCallback(() => {
    const snakeGrids = grids.filter(grid => grid.patternType === 'snakeGame');
    snakeGrids.forEach(grid => {
      // TOUJOURS réinitialiser avant de démarrer (comportement du bouton vert flottant)
      multiSnake.resetSnakeGame(grid.id);
      multiSnake.startSnakeGame(grid.id);
    });
    if (snakeGrids.length > 0) {
      toast.success(t("toast.snakeGamesStarted", { count: snakeGrids.length }));
    }
  }, [grids, multiSnake, t]);

  const stopAllSnakeGames = useCallback(() => {
    const snakeGrids = grids.filter(grid => grid.patternType === 'snakeGame');
    snakeGrids.forEach(grid => {
      multiSnake.stopSnakeGame(grid.id);
    });
    if (snakeGrids.length > 0) {
      toast.success(t("toast.snakeGamesStopped", { count: snakeGrids.length }));
    }
  }, [grids, multiSnake, t]);

  const resetAndStartAllSnakeGames = useCallback(() => {
    // Le bouton vert flottant et Espace : RESET FORCÉ de toutes les parties Snake
    const snakeGrids = grids.filter(grid => grid.patternType === 'snakeGame');
    
    // RESET DIRECT ET FORCÉ de toutes les grilles Snake
    setGrids(prevGrids => 
      prevGrids.map(grid => {
        if (grid.patternType !== 'snakeGame') return grid;
        
        const cols = Math.floor(grid.width / grid.gridSize);
        const rows = Math.floor(grid.height / grid.gridSize);
        const centerX = Math.floor(cols / 2);
        const centerY = Math.floor(rows / 2);
        
        // Générer nouvelle nourriture
        let newFood: SnakePosition;
        do {
          newFood = {
            x: Math.floor(Math.random() * cols),
            y: Math.floor(Math.random() * rows)
          };
        } while (newFood.x === centerX && newFood.y === centerY);
        
        // ÉTAT COMPLÈTEMENT NEUF pour cette grille
        const freshSnakeState: SnakeGameState = {
          snake: [{ x: centerX, y: centerY }],
          food: newFood,
          direction: 'RIGHT',
          isGameOver: false,
          isVictory: false,
          score: 0,
          isPlaying: true, // DÉMARRAGE IMMÉDIAT
          visitedCells: new Set([`${centerX},${centerY}`]),
          gameStartDimensions: {
            cols,
            rows,
            totalPanels: cols * rows,
            gridSize: grid.gridSize,
            gridWidth: grid.width,
            gridHeight: grid.height
          }
        };
        
        return {
          ...grid,
          snakeGameState: freshSnakeState
        };
      })
    );
    
    if (snakeGrids.length > 0) {
      toast.success(t("toast.snakeGamesStarted", { count: snakeGrids.length }));
    }
  }, [grids, t]);

  // Fonction pour déplacer tous les serpents actifs - VERSION STABLE SANS USECALLBACK
  const moveAllSnakes = () => {
    setGrids(currentGrids => {
      const activeSnakeGrids = currentGrids.filter(grid => 
        grid.patternType === 'snakeGame' && 
        grid.snakeGameState && 
        grid.snakeGameState.isPlaying && 
        !grid.snakeGameState.isGameOver && 
        !grid.snakeGameState.isVictory
      );
      
      if (activeSnakeGrids.length === 0) {
        return currentGrids; // Pas de grilles actives, on ne fait rien
      }
      
      return currentGrids.map(grid => {
        if (grid.patternType !== 'snakeGame' || !grid.snakeGameState || !grid.snakeGameState.isPlaying || grid.snakeGameState.isGameOver || grid.snakeGameState.isVictory) {
          return grid;
        }
        const state = grid.snakeGameState;
        const cols = Math.floor(grid.width / grid.gridSize);
        const rows = Math.floor(grid.height / grid.gridSize);
        
        // Plus besoin de buffering - direction appliquée immédiatement lors du changement
        const currentDirection = state.direction;
        
        // Calculer la nouvelle position de la tête
        const head = state.snake[0];
        let newHead: SnakePosition;

        switch (currentDirection) {
          case 'UP':
            newHead = { x: head.x, y: head.y - 1 };
            break;
          case 'DOWN':
            newHead = { x: head.x, y: head.y + 1 };
            break;
          case 'LEFT':
            newHead = { x: head.x - 1, y: head.y };
            break;
          case 'RIGHT':
            newHead = { x: head.x + 1, y: head.y };
            break;
        }

        // Vérifier les collisions
        const hasCollision = 
          newHead.x < 0 || newHead.x >= cols || 
          newHead.y < 0 || newHead.y >= rows ||
          state.snake.some(segment => segment.x === newHead.x && segment.y === newHead.y);

        if (hasCollision) {
          return {
            ...grid,
            snakeGameState: {
              ...state,
              direction: currentDirection,
              isGameOver: true,
              isPlaying: false
            }
          };
        }

        // Nouveau serpent avec la nouvelle tête
        const newSnake = [newHead, ...state.snake];
        let newFood = state.food;
        let newScore = state.score;
        
        // Ajouter la nouvelle position aux cases visitées
        const newVisitedCells = new Set(state.visitedCells);
        newVisitedCells.add(`${newHead.x},${newHead.y}`);

        // Vérifier si la nourriture a été mangée
        if (newHead.x === state.food.x && newHead.y === state.food.y) {
          // Générer nouvelle nourriture
          let newFoodPos: SnakePosition;
          do {
            newFoodPos = {
              x: Math.floor(Math.random() * cols),
              y: Math.floor(Math.random() * rows)
            };
          } while (newSnake.some(segment => segment.x === newFoodPos.x && segment.y === newFoodPos.y));
          
          newFood = newFoodPos;
          newScore += 10;
          
          // Vérifier la victoire
          if (newSnake.length >= cols * rows) {
            return {
              ...grid,
              snakeGameState: {
                ...state,
                snake: newSnake,
                food: newFood,
                score: newScore,
                direction: currentDirection,
                isVictory: true,
                isPlaying: false,
                visitedCells: newVisitedCells
              }
            };
          }
        } else {
          // Enlever la queue si pas de nourriture mangée
          newSnake.pop();
        }

        // Retourner la grille mise à jour avec le nouvel état Snake
        return {
          ...grid,
          snakeGameState: {
            ...state,
            snake: newSnake,
            food: newFood,
            score: newScore,
            direction: currentDirection,
            visitedCells: newVisitedCells
          }
        };
      });
    });
  };

  // Ref pour la boucle Snake
  const snakeGameLoopRef = useRef<NodeJS.Timeout | null>(null);

  // TIMER GLOBAL UNIQUE - Se lance au chargement et ne s'arrête JAMAIS
  useEffect(() => {
    // Timer unique qui tourne toujours - pas de dépendances !
    snakeGameLoopRef.current = setInterval(() => {
      moveAllSnakes(); // Appel direct - fonction stable
    }, 400); // 400ms pour une vitesse plus confortable
    
    return () => {
      if (snakeGameLoopRef.current) {
        clearInterval(snakeGameLoopRef.current);
        snakeGameLoopRef.current = null;
      }
    };
  }, []); // AUCUNE dépendance - timer permanent !

  // Ref pour éviter de charger plusieurs fois la config par défaut
  const defaultConfigLoadedRef = useRef(false);
  // État pour forcer le re-render après chargement de la config par défaut
  const [configLoadTrigger, setConfigLoadTrigger] = useState(0);
  
  // Charger la configuration MultiGrid par défaut au démarrage (seulement après montage)
  useEffect(() => {
    if (!mounted || defaultConfigLoadedRef.current) return; // Attendre le montage et éviter les chargements multiples
    
    const loadDefaultMultiGridConfig = async () => {
      // Trouver la configuration par défaut qui est une config MultiGrid
      const defaultConfig = savedConfigs.find(c => c.isDefault);
      
      if (defaultConfig) {
        defaultConfigLoadedRef.current = true; // Marquer comme chargé pour éviter les rechargements
        
        try {
          const result = await loadConfig(defaultConfig.name);
          if (result && result.success && result.config) {
            const loadedConfig = result.config;
            
            // Vérifier que c'est bien une config MultiGrid
            if (loadedConfig.multiGrid && loadedConfig.canvas && Array.isArray(loadedConfig.grids)) {
              setCanvasConfig(loadedConfig.canvas);
              
              // Reconstruire les grids avec les HTMLImageElement à partir des dataUrl
              const reconstructedGrids = await reconstructGrids(loadedConfig.grids);
              setGrids(reconstructedGrids);
              
              if (reconstructedGrids.length > 0) {
                setSelectedGridId(reconstructedGrids[0].id);
              }
              
              // Déclencher un re-render pour forcer la mise à jour du canvas
              // Le useEffect qui dépend de renderCanvas s'occupera du rendu
              setConfigLoadTrigger(prev => prev + 1);
              
              const configDisplayName = defaultConfig.displayName || defaultConfig.name || "Configuration par défaut";
              
              // Définir la configuration actuellement chargée
              setCurrentConfigName(defaultConfig.name);
              setCurrentConfigDisplayName(configDisplayName);
              
              toast.success((t("toast.configLoaded") || "Configuration \"{name}\" chargée").replace("{name}", configDisplayName));
            }
          }
        } catch (error) {
          // console.error("Erreur lors du chargement de la config par défaut:", error);
          defaultConfigLoadedRef.current = false; // Réinitialiser en cas d'erreur pour réessayer
        }
      }
    };
    
    // Charger seulement si savedConfigs est disponible et qu'on n'a pas déjà chargé
    if (savedConfigs.length > 0 && !defaultConfigLoadedRef.current) {
      loadDefaultMultiGridConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, savedConfigs.length]); // Seulement dépendre de mounted et de la longueur de savedConfigs

  // États pour Logos / Textes (sélections et upload)
  const [selectedLogoId, setSelectedLogoId] = useState<string | null>(null);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [logoSizeErrors, setLogoSizeErrors] = useState<{width?: string, height?: string}>({});
  const [tempLogoSizeInputs, setTempLogoSizeInputs] = useState<{width?: string, height?: string}>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Nettoyer les erreurs et valeurs temporaires des logos quand on change de logo sélectionné
  useEffect(() => {
    setLogoSizeErrors({});
    setTempLogoSizeInputs({});
  }, [selectedLogoId]);
  // Drag d'éléments (logo/texte/nom)
  const elementDragRef = useRef<{
    type: 'logo' | 'text' | 'name' | 'logo-resize';
    gridId: string;
    id?: string; // Optionnel pour le nom (pas d'id nécessaire)
    startMouseX: number;
    startMouseY: number;
    startX: number;
    startY: number;
    startWidth?: number; // Pour le redimensionnement de logo
    startHeight?: number; // Pour le redimensionnement de logo
    originalWidth?: number; // Pour le redimensionnement de logo (ratio d'aspect)
    originalHeight?: number; // Pour le redimensionnement de logo (ratio d'aspect)
  } | null>(null);

  // Generate dynamic gradient colors based on time and pattern seed
  // IDENTIQUE à app/grid/page.tsx pour assurer des boucles parfaites et cohérentes
  const generateGradientColors = useMemoizedCallback((time: number, patternSeed: number = 0, loopDuration: number = 10) => {
    // Utiliser patternSeed pour décaler le point de départ de l'animation
    const seed = patternSeed * 100; // Multiplier pour avoir un effet plus visible

    // Fréquences harmoniques pour une vraie boucle
    // Fréquence de base: 2π/(loopDuration*1000)
    // Pour 10s: 2π/10000 ≈ 0.0006283
    // Pour 5s: 2π/5000 ≈ 0.001256
    // Pour 3s: 2π/3000 ≈ 0.002094
    const baseFreq = (2 * Math.PI) / (loopDuration * 1000);

    // Couche 1: fréquence 1x (période = loopDuration)
    const r1 = Math.sin(time * baseFreq + seed) * 127 + 128;
    const g1 = Math.sin(time * baseFreq + seed + 2) * 127 + 128;
    const b1 = Math.sin(time * baseFreq + seed + 4) * 127 + 128;

    // Couche 2: fréquence 2x (période = loopDuration/2)
    const r2 = Math.sin(time * baseFreq * 2 + seed + 1) * 127 + 128;
    const g2 = Math.sin(time * baseFreq * 2 + seed + 3) * 127 + 128;
    const b2 = Math.sin(time * baseFreq * 2 + seed + 5) * 127 + 128;

    // Couche 3: fréquence 3x (période = loopDuration/3)
    const r3 = Math.sin(time * baseFreq * 3 + seed + 2) * 127 + 128;
    const g3 = Math.sin(time * baseFreq * 3 + seed + 4) * 127 + 128;
    const b3 = Math.sin(time * baseFreq * 3 + seed + 6) * 127 + 128;

    // Couche 4: fréquence 4x (période = loopDuration/4)
    const r4 = Math.sin(time * baseFreq * 4 + seed + 3) * 127 + 128;
    const g4 = Math.sin(time * baseFreq * 4 + seed + 5) * 127 + 128;
    const b4 = Math.sin(time * baseFreq * 4 + seed + 1) * 127 + 128;

    return [
      `rgb(${Math.floor(r1)}, ${Math.floor(g1)}, ${Math.floor(b1)})`,
      `rgb(${Math.floor(r2)}, ${Math.floor(g2)}, ${Math.floor(b2)})`,
      `rgb(${Math.floor(r3)}, ${Math.floor(g3)}, ${Math.floor(b3)})`,
      `rgb(${Math.floor(r4)}, ${Math.floor(g4)}, ${Math.floor(b4)})`,
    ];
  }, []);

  const parseColor = (colorStr: string) => {
    const match = colorStr.match(/rgb\((\d+), (\d+), (\d+)\)/);
    return match
      ? [
          Number.parseInt(match[1]),
          Number.parseInt(match[2]),
          Number.parseInt(match[3]),
        ]
      : [0, 0, 0];
  };

  // Fonction pour ajouter une nouvelle grille
  const addNewGrid = async () => {
    const newId = `grid-${Date.now()}`;
    const gridName = `${t("multigrid.gridDefaultName") || "Grille"} ${grids.length + 1}`;
    const newGrid: GridConfig = {
      id: newId,
      name: gridName,
      width: 1920, // Résolution par défaut 1920x1080
      height: 1080, // Résolution par défaut 1920x1080
      gridSize: 192, // Taille de grille par défaut à 192px
      x: 50,
      y: 50,
      patternType: "gradient",
      backgroundColor: "#000000",
      showGrid: true,
      animationSpeed: 1.0, // Vitesse d'animation à 1.0x par défaut
      sonarSpeed: 1.0, // Vitesse du sonar à 1.0x par défaut
      patternSeed: Math.random(), // Graine aléatoire pour décaler le point de départ du motif
      gradientLoopDuration: 10, // Durée de la boucle du dégradé en secondes (3, 5, ou 10)
      showSonar: true,
      rampDirection: "horizontal",
      standardPatternType: "none",
      showLogo: true,
      showName: false, // Désactivé par défaut (Pixel Map affiche le nom)
      showDimensions: true, // Activé par défaut
      showLEDNumbers: true, // Activé par défaut
      showTechnicalDetails: true, // Nouvelle option unifiée
      showDefaultLogo: false, // Ne pas afficher le logo VisualGrid par défaut (Pixel Map activé)
      sonarRevealMode: false, // Mode sonar avancé désactivé par défaut
      dalleConfigMode: "simple",
      dalleWidth: 0,
      dalleHeight: 0,
      borderWidth: 2,
      logoScale: 1.0, // Échelle du logo par défaut
      textScale: 1.0, // Échelle du texte par défaut
      activePatternMode: "custom", // Mode de motif actif par défaut: 'custom', 'solid', 'standard'
      textElements: [],
      logoElements: [],
      enableAnamorphosis: false, // Mode anamorphose désactivé par défaut
      pixelMapMode: {
        enabled: true,
        mainScreenText: gridName,
        posX: 0,
        posY: 0,
        resW: 1920,
        resH: 1080,
        panelW: 192,
        panelH: 192,
        size: 1.0
      }, // Mode Pixel Map activé par défaut
      // Initialiser les propriétés Snake Game
      snakeGameState: undefined,
      // Assigner automatiquement un set de contrôles pour Snake
      snakeControlSet: (grids.length % 4 + 1) as 1 | 2 | 3 | 4,
    };

    setGrids((prev) => [...prev, newGrid]);
    setSelectedGridId(newId);
    toast.success(t("multigrid.newGrid") || "Nouvelle grille ajoutée");
    
    // Démarrer le timer de comptage pour la nouvelle grille
    gridCounter.onActivity(true); // true = nouvelle génération
  };

  // Fonction pour supprimer une grille
  const deleteGrid = (id: string) => {
    setGrids((prev) => prev.filter((grid) => grid.id !== id));
    if (selectedGridId === id) {
      setSelectedGridId(null);
    }
    toast.success(t("multigrid.gridDeleted") || "Grille supprimée");
  };

  // Fonction pour dupliquer une grille
  const duplicateGrid = (id: string) => {
    const gridToDuplicate = grids.find((g) => g.id === id);
    if (!gridToDuplicate) return;

    const newGrid: GridConfig = {
      ...gridToDuplicate,
      id: Date.now().toString(),
      name: `${gridToDuplicate.name} (copie)`,
      x: gridToDuplicate.x + 20, // Décaler légèrement pour voir la copie
      y: gridToDuplicate.y + 20,
      // Cloner les éléments texte et logo
      textElements: gridToDuplicate.textElements ? [...gridToDuplicate.textElements] : [],
      logoElements: gridToDuplicate.logoElements ? [...gridToDuplicate.logoElements] : [],
      // Réinitialiser le Snake Game si présent
      snakeGameState: undefined,
    };

    setGrids((prev) => [...prev, newGrid]);
    setSelectedGridId(newGrid.id);
    toast.success(t("multigrid.gridDuplicated") || "Grille dupliquée");
    gridCounter.onActivity(true); // true = ajout
  };

  // Fonction pour copier une grille (CTRL+C)
  const copyGrid = (id: string) => {
    const gridToCopy = grids.find((g) => g.id === id);
    if (!gridToCopy) return;

    setCopiedGrid(gridToCopy);
    toast.success(t("multigrid.gridCopied") || "Grille copiée");
  };

  // Fonction pour coller une grille (CTRL+V)
  const pasteGrid = () => {
    if (!copiedGrid) {
      toast.error(t("multigrid.noCopiedGrid") || "Aucune grille copiée");
      return;
    }

    const newGrid: GridConfig = {
      ...copiedGrid,
      id: Date.now().toString(),
      name: `${copiedGrid.name} (copie)`,
      x: Math.min(canvasConfig.width - copiedGrid.width, copiedGrid.x + 40), // Décaler et éviter les débordements
      y: Math.min(canvasConfig.height - copiedGrid.height, copiedGrid.y + 40),
      // Cloner les éléments texte et logo
      textElements: copiedGrid.textElements ? [...copiedGrid.textElements] : [],
      logoElements: copiedGrid.logoElements ? [...copiedGrid.logoElements] : [],
      // Réinitialiser le Snake Game si présent
      snakeGameState: undefined,
    };

    setGrids((prev) => [...prev, newGrid]);
    setSelectedGridId(newGrid.id);
    toast.success(t("multigrid.gridPasted") || "Grille collée");
    gridCounter.onActivity(true); // true = ajout
  };

  // Fonction pour dessiner les lignes de grille et de dalle
  const drawGridLines = (ctx: CanvasRenderingContext2D, grid: GridConfig) => {
    if (!grid.showGrid) return;

    const isCustom =
      grid.dalleConfigMode === "custom" &&
      grid.dalleWidth > 0 &&
      grid.dalleHeight > 0;
    const isSimple = grid.dalleConfigMode === "simple" && grid.gridSize > 0;

    if (!isSimple && !isCustom) return;

    const panelWidth = isCustom ? grid.dalleWidth : grid.gridSize;
    const panelHeight = isCustom ? grid.dalleHeight : grid.gridSize;
    const gridLineWidth = grid.borderWidth || 2; // Utiliser l'épaisseur de bordure configurée

    // Calculer le nombre de panneaux en largeur et hauteur
    const panelsX = Math.ceil(grid.width / panelWidth);
    const panelsY = Math.ceil(grid.height / panelHeight);

    // Dessiner les cellules noircies
    for (let col = 0; col < panelsX; col++) {
      for (let row = 0; row < panelsY; row++) {
        // Calculer les coordonnées exactes du panneau
        const x = Math.floor(grid.x + col * panelWidth);
        const y = Math.floor(grid.y + row * panelHeight);

        // Calculer la taille réelle du panneau (pour gérer les bords)
        const actualPanelWidth = Math.min(panelWidth, grid.x + grid.width - x);
        const actualPanelHeight = Math.min(
          panelHeight,
          grid.y + grid.height - y,
        );

        // Position et taille de la cellule intérieure (la partie noircie)
        // Utiliser des valeurs entières pour éviter les problèmes de rendu
        const cellX = Math.floor(x + gridLineWidth / 2);
        const cellY = Math.floor(y + gridLineWidth / 2);
        const cellWidth = Math.floor(actualPanelWidth - gridLineWidth);
        const cellHeight = Math.floor(actualPanelHeight - gridLineWidth);

        if (cellWidth > 0 && cellHeight > 0) {
          // Effet damier: un panneau sur deux est plus sombre
          const isEven = (row + col) % 2 === 0;
          ctx.fillStyle = isEven
            ? "rgba(0, 0, 0, 0.90)"
            : "rgba(0, 0, 0, 0.75)";

          // Dessiner le fond de la dalle (la partie noircie)
          ctx.fillRect(cellX, cellY, cellWidth, cellHeight);
        }
      }
    }

    // Dessiner les lignes de la grille par-dessus avec une précision au pixel près
    ctx.strokeStyle = "rgba(0, 0, 0, 0.0)"; // Noir presque opaque LAISSER NOIR 0.0
    ctx.lineWidth = gridLineWidth;

    // Dessiner un rectangle noir autour de la grille entière
    // Utiliser des valeurs entières pour éviter les problèmes de rendu
    ctx.strokeRect(
      Math.floor(grid.x) + 0.5,
      Math.floor(grid.y) + 0.5,
      Math.floor(grid.width),
      Math.floor(grid.height),
    );

    // Lignes verticales - ajuster pour qu'elles soient exactement sur les limites des panneaux
    for (let i = 1; i < panelsX; i++) {
      const x = Math.floor(grid.x + i * panelWidth) + 0.5; // +0.5 pour un trait net d'1px
      ctx.beginPath();
      ctx.moveTo(x, Math.floor(grid.y));
      ctx.lineTo(x, Math.floor(grid.y + grid.height));
      ctx.stroke();
    }

    // Lignes horizontales - ajuster pour qu'elles soient exactement sur les limites des panneaux
    for (let j = 1; j < panelsY; j++) {
      const y = Math.floor(grid.y + j * panelHeight) + 0.5; // +0.5 pour un trait net d'1px
      ctx.beginPath();
      ctx.moveTo(Math.floor(grid.x), y);
      ctx.lineTo(Math.floor(grid.x + grid.width), y);
      ctx.stroke();
    }
  };

  // Fonction pour dessiner la numérotation des LEDs
  const drawLedNumberingForGrid = (
    ctx: CanvasRenderingContext2D,
    grid: GridConfig,
    enableAnamorphosis = false
  ) => {
    if (!grid.showLEDNumbers) return;

    // Déterminer la taille des panneaux en fonction du mode de configuration
    const isCustom =
      grid.dalleConfigMode === "custom" &&
      grid.dalleWidth > 0 &&
      grid.dalleHeight > 0;
    const isSimple = grid.dalleConfigMode === "simple" && grid.gridSize > 0;

    if (!isSimple && !isCustom) return;

    const panelWidth = isCustom ? grid.dalleWidth : grid.gridSize;
    const panelHeight = isCustom ? grid.dalleHeight : grid.gridSize;

    // Calculer le nombre de panneaux
    const panelsX = Math.ceil(grid.width / panelWidth);
    const panelsY = Math.ceil(grid.height / panelHeight);

    // Déterminer la taille de la police en fonction de la taille des panneaux
    // Utiliser la même formule que le mode classique
    const fontSize = Math.max(
      16,
      Math.min(panelWidth / 4, panelHeight / 4, 48),
    );

    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255, 255, 255, 1)";

    // Ajouter une ombre pour améliorer la lisibilité
    ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    // Calculer la surface minimale pour afficher un numéro
    const normalPanelArea = panelWidth * panelHeight;
    const minimumPanelArea = normalPanelArea * 0.45;

    // Numérotation (de gauche à droite, de haut en bas)
    for (let row = 0; row < panelsY; row++) {
      for (let col = 0; col < panelsX; col++) {
        // Utiliser des coordonnées entières pour une précision au pixel près
        const x = Math.floor(grid.x + col * panelWidth);
        const y = Math.floor(grid.y + row * panelHeight);

        // Calculer la taille réelle du panneau (pour gérer les bords)
        const actualPanelWidth = Math.min(panelWidth, grid.x + grid.width - x);
        const actualPanelHeight = Math.min(
          panelHeight,
          grid.y + grid.height - y,
        );

        if (actualPanelWidth > 0 && actualPanelHeight > 0) {
          const actualPanelArea = actualPanelWidth * actualPanelHeight;

          if (actualPanelArea >= minimumPanelArea) {
            const panelNumber = `${row + 1}.${col + 1}`;
            // Centrer parfaitement le texte dans le panneau
            const centerX = Math.floor(x + actualPanelWidth / 2);
            const centerY = Math.floor(y + actualPanelHeight / 2);

            if (enableAnamorphosis) {
              ctx.save()
              ctx.translate(centerX, centerY)
              ctx.scale(1, 0.5)
              ctx.fillText(panelNumber, 0, 0)
              ctx.restore()
            } else {
              ctx.fillText(panelNumber, centerX, centerY);
            }
          }
        }
      }
    }

    // Réinitialiser les ombres
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  };

  // Fonction de rendu du canvas
  const renderCanvas = useCallback(
    (options?: { forExport?: boolean; exportTime?: number }) => {
      // Si l'aperçu est désactivé et qu'un affichage externe est connecté, ne pas dessiner
      if (disablePreviewWhenExternal && isExternalDisplayConnected && !options?.forExport) {
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Définir la taille du canvas
      canvas.width = canvasConfig.width;
      canvas.height = canvasConfig.height;

      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Effacer le canvas
      ctx.fillStyle = "#333333"; // Couleur de fond du canvas
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Utiliser performance.now() directement pour un timing plus précis
      // Sauf si un temps d'export est fourni (pour l'export vidéo)
      const elapsedTime = options?.exportTime !== undefined
        ? options.exportTime
        : isAnimating
        ? performance.now() - startTimeRef.current
        : 0;

      // Flag pour savoir si on est en mode export (important pour le sonar)
      const isExporting = options?.forExport === true;
      // Pour les exports PNG statiques, on ne veut pas de sonar
      const isStaticPngExport = isExporting && options?.exportTime === undefined;

      // ─── Motif de canvas (couvre tout le canvas, les grilles laissent passer) ───
      const hasCanvasPattern = canvasConfig.canvasPattern && canvasConfig.canvasPattern !== 'none';
      if (hasCanvasPattern) {
        const cW = canvasConfig.width;
        const cH = canvasConfig.height;
        const cSpeed = canvasConfig.canvasAnimationSpeed ?? 1;
        const cSeed = canvasConfig.canvasPatternSeed ?? 0;
        const cLoop = canvasConfig.canvasGradientLoopDuration ?? 10;
        const cColors = generateGradientColors(elapsedTime * cSpeed, cSeed, cLoop);
        const cPattern = canvasConfig.canvasPattern;

        ctx.save();
        // Draw pattern covering entire canvas
        if (cPattern === 'gradient') {
          const g = ctx.createLinearGradient(0, 0, cW, cH);
          g.addColorStop(0, cColors[0]); g.addColorStop(0.5, cColors[1]); g.addColorStop(1, cColors[2]);
          ctx.fillStyle = g; ctx.fillRect(0, 0, cW, cH);
        } else if (cPattern === 'solid') {
          ctx.fillStyle = cColors[0]; ctx.fillRect(0, 0, cW, cH);
        } else if (cPattern === 'checkerboard') {
          const cell = Math.max(1, Math.round(cW / 16));
          for (let i = 0; i < Math.ceil(cW / cell); i++) {
            for (let j = 0; j < Math.ceil(cH / cell); j++) {
              ctx.fillStyle = (i + j) % 2 === 0 ? cColors[0] : cColors[1];
              ctx.fillRect(i * cell, j * cell, cell, cell);
            }
          }
        } else if (cPattern === 'rainbow') {
          const time = elapsedTime * cSpeed * 0.001;
          const rainbowColors = ['#FF0000','#FF8000','#FFFF00','#00FF00','#0080FF','#0000FF','#8000FF'];
          const rg = ctx.createLinearGradient(0, 0, cW, 0);
          const offset = (time * 0.5) % 1;
          for (let i = 0; i < rainbowColors.length * 2; i++) {
            rg.addColorStop(((i / (rainbowColors.length * 2)) + offset) % 1, rainbowColors[i % rainbowColors.length]);
          }
          ctx.fillStyle = rg; ctx.fillRect(0, 0, cW, cH);
        } else if (cPattern === 'colorBars') {
          const numBars = 8;
          const barW = cW / numBars;
          const time = elapsedTime * cSpeed * 0.001;
          for (let i = 0; i < numBars; i++) {
            ctx.fillStyle = `hsl(${(i * 45 + time * 30) % 360}, 80%, 60%)`;
            ctx.fillRect(i * barW, 0, barW, cH);
          }
        } else if (cPattern === 'gradient4') {
          const imageData = ctx.createImageData(cW, cH);
          const d = imageData.data;
          const parseRgb = (s: string) => { const m = s.match(/rgb\((\d+), (\d+), (\d+)\)/); return m ? [+m[1],+m[2],+m[3]] : [0,0,0]; };
          const [r1,g1,b1] = parseRgb(cColors[0]);
          const [r2,g2,b2] = parseRgb(cColors[1]);
          const [r3,g3,b3] = parseRgb(cColors[2]);
          const [r4,g4,b4] = parseRgb(cColors[3]);
          for (let cy = 0; cy < cH; cy++) {
            for (let cx = 0; cx < cW; cx++) {
              const xr = cx / cW; const yr = cy / cH;
              const idx = (cy * cW + cx) * 4;
              d[idx]   = (r1 + (r2-r1)*xr + ((r3 + (r4-r3)*xr) - (r1 + (r2-r1)*xr))*yr) | 0;
              d[idx+1] = (g1 + (g2-g1)*xr + ((g3 + (g4-g3)*xr) - (g1 + (g2-g1)*xr))*yr) | 0;
              d[idx+2] = (b1 + (b2-b1)*xr + ((b3 + (b4-b3)*xr) - (b1 + (b2-b1)*xr))*yr) | 0;
              d[idx+3] = 255;
            }
          }
          ctx.putImageData(imageData, 0, 0);
        } else {
          // For all other patterns, use grid-based draw helper with full canvas dimensions
          // Create a synthetic grid config to reuse the existing pattern draw code
          ctx.save();
          ctx.translate(0, 0);
          drawStandardPattern(ctx, cW, cH, elapsedTime, cPattern, { direction: 'horizontal', gridSize: 0, animationSpeed: cSpeed });
          ctx.restore();
        }
        ctx.restore();

        // Now cut out (reveal) the canvas pattern through the grids by drawing a dark overlay everywhere EXCEPT the grids
        // i.e., mask: fill dark outside grids
        ctx.save();
        ctx.fillStyle = '#333333';
        ctx.beginPath();
        // Full canvas rect
        ctx.rect(0, 0, cW, cH);
        // Subtract grid regions using even-odd rule — grids punch holes through the dark overlay
        grids.forEach(g => { ctx.rect(g.x, g.y, g.width, g.height); });
        ctx.fill('evenodd');
        ctx.restore();
      }

      // Dessiner chaque grille
      grids.forEach((grid) => {
        ctx.save();

        // Appliquer un effet visuel pendant le glisser-déposer
        if (isDragging && grid.id === draggedGridRef.current) {
          ctx.globalAlpha = 0.7; // Rendre la grille semi-transparente
          ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
          ctx.shadowBlur = 15;
          ctx.shadowOffsetX = 5;
          ctx.shadowOffsetY = 5;
        }

        ctx.beginPath();
        ctx.rect(grid.x, grid.y, grid.width, grid.height);
        ctx.clip();

        // Dessiner le pattern en fonction du mode actif
        // Si un motif de canvas est actif, les grilles sont transparentes (elles laissent passer le motif du canvas)
        if (hasCanvasPattern) {
          // Grille transparente : ne pas dessiner le pattern de la grille
          // On n'applique que la couleur de fond si la grille est en mode solid (pour un effet opaque)
          // Par défaut, on ne remplit rien pour laisser le motif du canvas visible
        } else if (
          grid.activePatternMode === "standard" &&
          grid.standardPatternType !== "none"
        ) {
          // Translater le contexte à la position de la grille
          ctx.save();
          ctx.translate(grid.x, grid.y);

          drawStandardPattern(
            ctx,
            grid.width,
            grid.height,
            elapsedTime,
            grid.standardPatternType,
            {
              direction: grid.rampDirection,
              gridSize: grid.gridSize,
              animationSpeed: grid.animationSpeed,
            },
          );

          ctx.restore();
        } else if (grid.activePatternMode === "solid") {
          // Mode couleur unie
          ctx.fillStyle = grid.backgroundColor;
          ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
        } else if (grid.activePatternMode === "custom") {
          // Mode motif personnalisé
          const colors = generateGradientColors(
            elapsedTime * grid.animationSpeed,
            grid.patternSeed ?? 0,
            grid.gradientLoopDuration ?? 10
          );

          ctx.save();
          ctx.beginPath();
          ctx.rect(grid.x, grid.y, grid.width, grid.height);
          ctx.clip();

          if (grid.patternType === "gradient") {
            const gradient = ctx.createLinearGradient(
              grid.x,
              grid.y,
              grid.x + grid.width,
              grid.y + grid.height,
            );
            gradient.addColorStop(0, colors[0]);
            gradient.addColorStop(0.5, colors[1]);
            gradient.addColorStop(1, colors[2]);
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
          } else if (grid.patternType === "solid") {
            ctx.fillStyle = grid.backgroundColor;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
          } else if (grid.patternType === "checkerboard") {
            const cellSize =
              grid.gridSize <= 0 ? grid.width / 8 : grid.gridSize;
            const numCols = Math.ceil(grid.width / cellSize);
            const numRows = Math.ceil(grid.height / cellSize);
            for (let i = 0; i < numCols; i++) {
              for (let j = 0; j < numRows; j++) {
                const isEven = (i + j) % 2 === 0;
                ctx.fillStyle = isEven ? colors[0] : colors[1];
                ctx.fillRect(
                  grid.x + i * cellSize,
                  grid.y + j * cellSize,
                  cellSize,
                  cellSize,
                );
              }
            }
          } else if (grid.patternType === "pulse") {
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            const maxRadius = Math.sqrt(
              Math.pow(grid.width / 2, 2) + Math.pow(grid.height / 2, 2),
            );
            const pulseTime = elapsedTime * 0.002;

            const colorPalette = colors.map((c) => parseColor(c));

            for (let i = 0; i < 5; i++) {
              const ringPhase = (i / 5) * Math.PI * 2;
              const ringProgress = (Math.sin(pulseTime + ringPhase) + 1) / 2;
              const radius = ringProgress * maxRadius;
              const opacity = Math.sin(ringProgress * Math.PI);

              const colorIndex = i % colorPalette.length;
              const [r, g, b] = colorPalette[colorIndex];

              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
              ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
              ctx.lineWidth = 3;
              ctx.stroke();
            }
          } else if (grid.patternType === "rainbow") {
            // Arc-en-ciel avec estompage
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const colors = ['#FF0000', '#FF8000', '#FFFF00', '#00FF00', '#0080FF', '#0000FF', '#8000FF'];
            
            // Créer un dégradé horizontal avec toutes les couleurs
            const gradient = ctx.createLinearGradient(grid.x, grid.y, grid.x + grid.width, grid.y);
            
            const offset = (time * 0.5) % 1;
            for (let i = 0; i < colors.length * 2; i++) {
              const pos = (i / (colors.length * 2) + offset) % 1;
              const colorIndex = i % colors.length;
              gradient.addColorStop(pos, colors[colorIndex]);
            }
            
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
          } else if (grid.patternType === "gradient4") {
            // 4-corner gradient - IDENTIQUE à /grid/page.tsx
            const colors = generateGradientColors(
              elapsedTime * grid.animationSpeed,
              grid.patternSeed ?? 0,
              grid.gradientLoopDuration ?? 10
            );
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;

            // Parse colors
            const parseColor = (colorStr: string) => {
              const match = colorStr.match(/rgb\((\d+), (\d+), (\d+)\)/);
              return match ? [Number.parseInt(match[1]), Number.parseInt(match[2]), Number.parseInt(match[3])] : [0, 0, 0];
            };

            const [r1, g1, b1] = parseColor(colors[0]); // Top-left
            const [r2, g2, b2] = parseColor(colors[1]); // Top-right
            const [r3, g3, b3] = parseColor(colors[2]); // Bottom-left
            const [r4, g4, b4] = parseColor(colors[3]); // Bottom-right

            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const xRatio = x / grid.width;
                const yRatio = y / grid.height;

                // Bilinear interpolation
                const topR = r1 + (r2 - r1) * xRatio;
                const topG = g1 + (g2 - g1) * xRatio;
                const topB = b1 + (b2 - b1) * xRatio;

                const bottomR = r3 + (r4 - r3) * xRatio;
                const bottomG = g3 + (g4 - g3) * xRatio;
                const bottomB = b3 + (b4 - b3) * xRatio;

                const finalR = topR + (bottomR - topR) * yRatio;
                const finalG = topG + (bottomG - topG) * yRatio;
                const finalB = topB + (bottomB - topB) * yRatio;

                const index = (y * grid.width + x) * 4;
                data[index] = Math.floor(finalR);
                data[index + 1] = Math.floor(finalG);
                data[index + 2] = Math.floor(finalB);
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "colorBars") {
            // Barres colorées verticales
            const numBars = 8;
            const barWidth = grid.width / numBars;
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            for (let i = 0; i < numBars; i++) {
              const hue = (i * 45 + time * 30) % 360;
              ctx.fillStyle = `hsl(${hue}, 80%, 60%)`;
              ctx.fillRect(
                grid.x + i * barWidth,
                grid.y,
                barWidth,
                grid.height
              );
            }
          } else if (grid.patternType === "spiral") {
            // Spirale colorée animée - version ultra-optimisée
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            const centerX = grid.width / 2;
            const centerY = grid.height / 2;
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            // Optimisation 1: Pas adaptatif selon la taille de la grille
            const totalPixels = grid.width * grid.height;
            const step = totalPixels > 1000000 ? 4 : totalPixels > 250000 ? 3 : 2;
            
            // Optimisation 2: Précalculs constants
            const spiralTightness = 0.05;
            const timeOffset = time * 2;
            const invTwoPi = 1 / (Math.PI * 2);
            
            // Optimisation 3: Table de lookup pour conversion HSL optimisée
            const hslToRgb = (h: number): [number, number, number] => {
              const s = 0.8;
              const l = 0.5;
              const c = (1 - Math.abs(2 * l - 1)) * s;
              const x_val = c * (1 - Math.abs(((h / 60) % 2) - 1));
              const m = l - c / 2;
              
              let r, g, b;
              if (h < 60) {
                [r, g, b] = [c, x_val, 0];
              } else if (h < 120) {
                [r, g, b] = [x_val, c, 0];
              } else if (h < 180) {
                [r, g, b] = [0, c, x_val];
              } else if (h < 240) {
                [r, g, b] = [0, x_val, c];
              } else if (h < 300) {
                [r, g, b] = [x_val, 0, c];
              } else {
                [r, g, b] = [c, 0, x_val];
              }
              
              return [
                ((r + m) * 255) | 0,
                ((g + m) * 255) | 0,
                ((b + m) * 255) | 0
              ];
            };
            
            // Optimisation 4: Boucle optimisée avec stepping
            for (let y = 0; y < grid.height; y += step) {
              const dy = y - centerY;
              const dySquared = dy * dy;
              
              for (let x = 0; x < grid.width; x += step) {
                const dx = x - centerX;
                const distance = Math.sqrt(dx * dx + dySquared);
                const angle = Math.atan2(dy, dx);
                
                const spiralValue = (angle + distance * spiralTightness + timeOffset) % (Math.PI * 2);
                const hue = spiralValue * invTwoPi * 360;
                
                // Optimisation 5: Conversion HSL optimisée
                const [r, g, b] = hslToRgb(hue);
                
                // Optimisation 6: Remplissage par blocs optimisé
                const maxY = Math.min(step, grid.height - y);
                const maxX = Math.min(step, grid.width - x);
                
                for (let blockY = 0; blockY < maxY; blockY++) {
                  const rowIndex = ((y + blockY) * grid.width + x) * 4;
                  for (let blockX = 0; blockX < maxX; blockX++) {
                    const index = rowIndex + blockX * 4;
                    data[index] = r;
                    data[index + 1] = g;
                    data[index + 2] = b;
                    data[index + 3] = 255;
                  }
                }
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "orangeTeal") {
            // Dégradé Orange & Teal animé
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const phase = Math.sin(time) * 0.5 + 0.5;
            
            const gradient = ctx.createLinearGradient(
              grid.x,
              grid.y,
              grid.x + grid.width,
              grid.y + grid.height
            );
            
            const orange = `rgb(${255}, ${120 + phase * 50}, ${20})`;
            const teal = `rgb(${20}, ${128 + phase * 50}, ${128})`;
            
            gradient.addColorStop(0, orange);
            gradient.addColorStop(1, teal);
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
          } else if (grid.patternType === "magentaSummer") {
            // Palette Magenta Summer
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const wave1 = Math.sin(time) * 0.5 + 0.5;
            const wave2 = Math.sin(time + Math.PI / 2) * 0.5 + 0.5;
            
            const gradient = ctx.createRadialGradient(
              grid.x + grid.width * wave1,
              grid.y + grid.height * wave2,
              0,
              grid.x + grid.width / 2,
              grid.y + grid.height / 2,
              Math.max(grid.width, grid.height) / 2
            );
            
            gradient.addColorStop(0, `rgb(${255}, ${20 + wave1 * 100}, ${147})`);
            gradient.addColorStop(0.5, `rgb(${255}, ${105}, ${180})`);
            gradient.addColorStop(1, `rgb(${199}, ${21}, ${133})`);
            
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
          } else if (grid.patternType === "summer") {
            // Palette estivale
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const wave = Math.sin((x + y + time * 50) * 0.02);
                const normalizedWave = (wave + 1) / 2;
                
                // Couleurs estivales : jaune, orange, rouge
                const r = 255;
                const g = 150 + normalizedWave * 105; // 150-255
                const b = normalizedWave * 100; // 0-100
                
                const index = (y * grid.width + x) * 4;
                data[index] = r;
                data[index + 1] = g;
                data[index + 2] = b;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "bluePurple") {
            // Palette Blue Purple
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            const gradient = ctx.createRadialGradient(
              centerX + Math.sin(time) * grid.width * 0.2,
              centerY + Math.cos(time) * grid.height * 0.2,
              0,
              centerX,
              centerY,
              Math.max(grid.width, grid.height) / 2
            );
            
            const intensity = Math.sin(time * 2) * 0.3 + 0.7;
            
            gradient.addColorStop(0, `rgb(${100 * intensity}, ${50 * intensity}, ${255})`);
            gradient.addColorStop(0.5, `rgb(${138 * intensity}, ${43 * intensity}, ${226})`);
            gradient.addColorStop(1, `rgb(${75 * intensity}, ${0}, ${130 * intensity})`);
            
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
          } else if (grid.patternType === "plasma") {
            // Effet plasma
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const value = Math.sin(x * 0.02 + time) + Math.sin(y * 0.02 + time) + 
                             Math.sin((x + y) * 0.01 + time) + Math.sin(Math.sqrt(x*x + y*y) * 0.02 + time);
                const normalized = (value + 4) / 8;
                const index = (y * grid.width + x) * 4;
                data[index] = Math.sin(normalized * Math.PI) * 255;
                data[index + 1] = Math.sin(normalized * Math.PI + 2) * 255;
                data[index + 2] = Math.sin(normalized * Math.PI + 4) * 255;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "neon") {
            // Effet néon
            const time = elapsedTime * grid.animationSpeed * 0.002;
            ctx.fillStyle = '#000011';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let i = 0; i < 5; i++) {
              const intensity = Math.sin(time + i) * 0.5 + 0.5;
              const thickness = 2 + intensity * 8;
              const color = `rgb(${255 * intensity}, ${100 + 155 * intensity}, ${255})`;
              
              ctx.strokeStyle = color;
              ctx.lineWidth = thickness;
              ctx.shadowColor = color;
              ctx.shadowBlur = 20;
              
              ctx.beginPath();
              ctx.moveTo(grid.x, grid.y + grid.height * (i + 1) / 6);
              ctx.lineTo(grid.x + grid.width, grid.y + grid.height * (i + 1) / 6);
              ctx.stroke();
            }
            ctx.shadowBlur = 0;
          } else if (grid.patternType === "matrix") {
            // Effet Matrix
            const time = elapsedTime * grid.animationSpeed * 0.001;
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const cols = Math.floor(grid.width / 20);
            const rows = Math.floor(grid.height / 20);
            
            for (let i = 0; i < cols; i++) {
              for (let j = 0; j < rows; j++) {
                const char = Math.floor(Math.random() * 10);
                const alpha = Math.sin(time + i * j) * 0.5 + 0.5;
                ctx.fillStyle = `rgba(0, 255, 0, ${alpha})`;
                ctx.font = '16px monospace';
                ctx.fillText(char.toString(), grid.x + i * 20, grid.y + j * 20);
              }
            }

          } else if (grid.patternType === "water") {
            // Dégradé animé fluide du bas sombre au haut clair
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            const gradient = ctx.createLinearGradient(grid.x, grid.y + grid.height, grid.x, grid.y);
            const wave = Math.sin(time * 2) * 0.2 + 0.5;
            
            // Bas sombre
            gradient.addColorStop(0, `rgb(0, ${30 + wave * 20}, ${80 + wave * 40})`);
            // Milieu
            gradient.addColorStop(0.5, `rgb(0, ${80 + wave * 40}, ${150 + wave * 50})`);
            // Haut clair
            gradient.addColorStop(1, `rgb(${50 + wave * 50}, ${150 + wave * 80}, ${220 + wave * 35})`);
            
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);

          } else if (grid.patternType === "laser") {
            // Effet laser
            const time = elapsedTime * grid.animationSpeed * 0.003;
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const beamY = grid.y + grid.height / 2 + Math.sin(time) * grid.height * 0.3;
            const gradient = ctx.createLinearGradient(grid.x, beamY - 20, grid.x, beamY + 20);
            gradient.addColorStop(0, 'rgba(255, 0, 0, 0)');
            gradient.addColorStop(0.5, 'rgba(255, 0, 0, 1)');
            gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
            
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, beamY - 20, grid.width, 40);
          } else if (grid.patternType === "hypnotic") {
            // Cercles hypnotiques infinis - cycle plus rapide
            const time = elapsedTime * grid.animationSpeed * 0.002;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            const maxRadius = Math.max(grid.width, grid.height) * 1.5;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Spawner continu de cercles à l'infini - cycle plus rapide
            for (let i = 0; i < 50; i++) {
              const radius = (time * 200 + i * 30) % maxRadius; // Plus rapide: 200 au lieu de 120
              const opacity = Math.max(0, 1 - radius / maxRadius);
              const hue = (time * 80 + i * 7.2) % 360; // Couleurs plus rapides aussi
              
              // Les cercles continuent toujours à spawner
              ctx.strokeStyle = `hsla(${hue}, 80%, 60%, ${opacity})`;
              ctx.lineWidth = 3 + opacity * 6;
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
              ctx.stroke();
            }
          } else if (grid.patternType === "diamond") {
            // Motif diamant avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const size = 50;
            
            // Fond noir
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let x = 0; x < grid.width + size; x += size) {
              for (let y = 0; y < grid.height + size; y += size) {
                const hue = (x + y + time * 100) % 360;
                ctx.fillStyle = `hsl(${hue}, 70%, 50%)`;
                
                ctx.save();
                ctx.translate(grid.x + x, grid.y + y);
                ctx.rotate(Math.PI / 4);
                ctx.fillRect(-size/4, -size/4, size/2, size/2);
                ctx.restore();
              }
            }
          } else if (grid.patternType === "galaxy") {
            // Galaxie spirale réaliste
            const time = elapsedTime * grid.animationSpeed * 0.0003;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            // Fond d'espace profond
            ctx.fillStyle = '#000008';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Bras spiraux
            for (let arm = 0; arm < 4; arm++) {
              for (let i = 0; i < 200; i++) {
                const progress = i / 200;
                const spiralTightness = 3;
                const angle = progress * Math.PI * spiralTightness + time + (arm * Math.PI / 2);
                const radius = progress * Math.min(grid.width, grid.height) / 2;
                
                const x = centerX + Math.cos(angle) * radius;
                const y = centerY + Math.sin(angle) * radius;
                
                if (x >= grid.x && x <= grid.x + grid.width && y >= grid.y && y <= grid.y + grid.height) {
                  const brightness = (1 - progress) * Math.sin(i * 0.1 + time * 2) * 0.5 + 0.5;
                  const size = (1 - progress) * 3 + 1;
                  
                  // Couleurs d'étoiles variées
                  const starType = Math.sin(i * 0.3 + arm) * 0.5 + 0.5;
                  let r, g, b;
                  if (starType < 0.3) {
                    r = 100 + brightness * 155;
                    g = 150 + brightness * 105;
                    b = 255;
                  } else if (starType < 0.7) {
                    r = 255;
                    g = 255;
                    b = 200 + brightness * 55;
                  } else {
                    r = 255;
                    g = 100 + brightness * 100;
                    b = 100;
                  }
                  
                  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${brightness})`;
                  ctx.fillRect(x - size/2, y - size/2, size, size);
                }
              }
            }
            
            // Centre galactique brillant
            const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 50);
            coreGradient.addColorStop(0, `rgba(255, 255, 150, ${Math.sin(time * 3) * 0.3 + 0.7})`);
            coreGradient.addColorStop(1, 'rgba(255, 200, 100, 0)');
            ctx.fillStyle = coreGradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
          } else if (grid.patternType === "lightning") {
            // Éclairs comme tempête, liés au slider animation
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Probabilité d'éclair basée sur la vitesse d'animation
            const lightningChance = grid.animationSpeed * 0.05;
            
            if (Math.random() < lightningChance) {
              ctx.strokeStyle = `rgba(255, 255, 255, ${0.8 + Math.random() * 0.2})`;
              ctx.lineWidth = 2 + Math.random() * 3;
              ctx.shadowColor = '#ffffff';
              ctx.shadowBlur = 10;
              
              ctx.beginPath();
              let x = grid.x + Math.random() * grid.width;
              let y = grid.y;
              ctx.moveTo(x, y);
              
              while (y < grid.y + grid.height) {
                x += (Math.random() - 0.5) * 40;
                y += 15 + Math.random() * 10;
                x = Math.max(grid.x, Math.min(grid.x + grid.width, x));
                ctx.lineTo(x, y);
              }
              ctx.stroke();
              ctx.shadowBlur = 0;
            }
          } else if (grid.patternType === "cosmic") {
            // Effet cosmique
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const distance = Math.sqrt((x - grid.width/2)**2 + (y - grid.height/2)**2);
                const wave = Math.sin(distance * 0.05 + time * 2) * 0.5 + 0.5;
                
                const index = (y * grid.width + x) * 4;
                data[index] = wave * 100;
                data[index + 1] = wave * 50;
                data[index + 2] = wave * 255;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "vortex") {
            // Effet vortex
            const time = elapsedTime * grid.animationSpeed * 0.002;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const dx = x - grid.width/2;
                const dy = y - grid.height/2;
                const distance = Math.sqrt(dx*dx + dy*dy);
                const angle = Math.atan2(dy, dx) + time + distance * 0.01;
                
                const intensity = Math.sin(angle * 3) * 0.5 + 0.5;
                const index = (y * grid.width + x) * 4;
                data[index] = intensity * 255;
                data[index + 1] = intensity * 150;
                data[index + 2] = intensity * 50;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "crystalline") {
            // Motif cristallin avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            // Fond noir
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let i = 0; i < 6; i++) {
              const angle = (i / 6) * Math.PI * 2 + time;
              const gradient = ctx.createRadialGradient(
                grid.x + grid.width/2,
                grid.y + grid.height/2,
                0,
                grid.x + grid.width/2,
                grid.y + grid.height/2,
                Math.max(grid.width, grid.height)
              );
              
              const hue = (time * 50 + i * 60) % 360;
              gradient.addColorStop(0, `hsla(${hue}, 80%, 60%, 0.8)`);
              gradient.addColorStop(1, `hsla(${hue}, 80%, 30%, 0.1)`);
              
              ctx.fillStyle = gradient;
              ctx.save();
              ctx.translate(grid.x + grid.width/2, grid.y + grid.height/2);
              ctx.rotate(angle);
              ctx.fillRect(-grid.width/2, -5, grid.width, 10);
              ctx.restore();
            }
          } else if (grid.patternType === "aurora") {
            // Effet aurore boréale
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000022';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let i = 0; i < 5; i++) {
              const gradient = ctx.createLinearGradient(
                grid.x,
                grid.y + i * grid.height / 5,
                grid.x + grid.width,
                grid.y + (i + 1) * grid.height / 5
              );
              
              const hue1 = (time * 30 + i * 60) % 360;
              const hue2 = (time * 30 + i * 60 + 120) % 360;
              
              gradient.addColorStop(0, `hsla(${hue1}, 70%, 50%, 0.3)`);
              gradient.addColorStop(0.5, `hsla(${hue2}, 70%, 60%, 0.6)`);
              gradient.addColorStop(1, `hsla(${hue1}, 70%, 40%, 0.2)`);
              
              ctx.fillStyle = gradient;
              ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            }
          } else if (grid.patternType === "cyberpunk") {
            // Style cyberpunk
            const time = elapsedTime * grid.animationSpeed * 0.003;
            
            ctx.fillStyle = '#0a0a0a';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Lignes de scan
            for (let y = 0; y < grid.height; y += 4) {
              const intensity = Math.sin(y * 0.1 + time * 5) * 0.5 + 0.5;
              ctx.fillStyle = `rgba(0, 255, 255, ${intensity * 0.2})`;
              ctx.fillRect(grid.x, grid.y + y, grid.width, 2);
            }
            
            // Grille néon
            ctx.strokeStyle = '#ff0080';
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.6;
            
            const gridSize = 30;
            for (let x = 0; x < grid.width; x += gridSize) {
              ctx.beginPath();
              ctx.moveTo(grid.x + x, grid.y);
              ctx.lineTo(grid.x + x, grid.y + grid.height);
              ctx.stroke();
            }
            
            for (let y = 0; y < grid.height; y += gridSize) {
              ctx.beginPath();
              ctx.moveTo(grid.x, grid.y + y);
              ctx.lineTo(grid.x + grid.width, grid.y + y);
              ctx.stroke();
            }
            ctx.globalAlpha = 1;

          } else if (grid.patternType === "hologram") {
            // Effet hologramme
            const time = elapsedTime * grid.animationSpeed * 0.003;
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let y = 0; y < grid.height; y += 2) {
              const phase = Math.sin(y * 0.1 + time * 4) * 0.5 + 0.5;
              const hue = (time * 100 + y) % 360;
              ctx.fillStyle = `hsla(${hue}, 80%, 50%, ${phase * 0.7})`;
              ctx.fillRect(grid.x, grid.y + y, grid.width, 1);
            }
          } else if (grid.patternType === "dna") {
            // Hélice ADN
            const time = elapsedTime * grid.animationSpeed * 0.002;
            ctx.fillStyle = '#000022';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const centerX = grid.x + grid.width / 2;
            for (let y = 0; y < grid.height; y += 5) {
              const progress = y / grid.height;
              const angle1 = progress * Math.PI * 8 + time;
              const angle2 = angle1 + Math.PI;
              
              const x1 = centerX + Math.sin(angle1) * 30;
              const x2 = centerX + Math.sin(angle2) * 30;
              
              ctx.fillStyle = '#00ff88';
              ctx.fillRect(x1 - 2, grid.y + y, 4, 4);
              ctx.fillStyle = '#ff0088';
              ctx.fillRect(x2 - 2, grid.y + y, 4, 4);
              
              // Liaison
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(x1, grid.y + y);
              ctx.lineTo(x2, grid.y + y);
              ctx.stroke();
            }
          } else if (grid.patternType === "neural") {
            // Réseau neural
            const time = elapsedTime * grid.animationSpeed * 0.002;
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const nodes = [];
            for (let i = 0; i < 20; i++) {
              nodes.push({
                x: grid.x + Math.random() * grid.width,
                y: grid.y + Math.random() * grid.height,
                activity: Math.sin(time + i) * 0.5 + 0.5
              });
            }
            
            // Connexions
            for (let i = 0; i < nodes.length; i++) {
              for (let j = i + 1; j < nodes.length; j++) {
                const distance = Math.sqrt((nodes[i].x - nodes[j].x)**2 + (nodes[i].y - nodes[j].y)**2);
                if (distance < 150) {
                  const alpha = (1 - distance/150) * (nodes[i].activity + nodes[j].activity) / 2;
                  ctx.strokeStyle = `rgba(0, 255, 255, ${alpha * 0.5})`;
                  ctx.lineWidth = 1;
                  ctx.beginPath();
                  ctx.moveTo(nodes[i].x, nodes[i].y);
                  ctx.lineTo(nodes[j].x, nodes[j].y);
                  ctx.stroke();
                }
              }
            }
            
            // Nœuds
            nodes.forEach(node => {
              ctx.fillStyle = `rgba(255, 255, 0, ${node.activity})`;
              ctx.fillRect(node.x - 3, node.y - 3, 6, 6);
            });
          } else if (grid.patternType === "lava") {
            // Lave en fusion
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const noise1 = Math.sin((x + time * 60) * 0.02) * Math.sin((y + time * 40) * 0.03);
                const noise2 = Math.sin((x + time * 80) * 0.05) * Math.sin((y + time * 60) * 0.02);
                const lava = (noise1 + noise2 + 2) / 4;
                
                let r, g, b;
                if (lava < 0.4) {
                  r = 100 + lava * 155;
                  g = 0;
                  b = 0;
                } else if (lava < 0.7) {
                  r = 255;
                  g = (lava - 0.4) * 255 / 0.3;
                  b = 0;
                } else {
                  r = 255;
                  g = 255;
                  b = (lava - 0.7) * 255 / 0.3;
                }
                
                const index = (y * grid.width + x) * 4;
                data[index] = r;
                data[index + 1] = g;
                data[index + 2] = b;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "ice") {
            // Cristaux de glace
            const time = elapsedTime * grid.animationSpeed * 0.001;
            ctx.fillStyle = '#001122';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let i = 0; i < 50; i++) {
              const x = grid.x + (Math.sin(time + i) * 0.5 + 0.5) * grid.width;
              const y = grid.y + (Math.cos(time * 0.7 + i) * 0.5 + 0.5) * grid.height;
              const size = Math.sin(time * 2 + i) * 10 + 15;
              const alpha = Math.sin(time * 3 + i) * 0.5 + 0.5;
              
              ctx.fillStyle = `rgba(200, 230, 255, ${alpha * 0.8})`;
              ctx.save();
              ctx.translate(x, y);
              ctx.rotate(time + i);
              ctx.fillRect(-size/2, -2, size, 4);
              ctx.fillRect(-2, -size/2, 4, size);
              ctx.restore();
            }
          } else if (grid.patternType === "smoke") {
            // Fumée tourbillonnante
            const time = elapsedTime * grid.animationSpeed * 0.001;
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let i = 0; i < 100; i++) {
              const progress = i / 100;
              const x = grid.x + grid.width/2 + Math.sin(progress * Math.PI * 4 + time) * progress * 50;
              const y = grid.y + grid.height - progress * grid.height;
              const size = progress * 20 + 5;
              const alpha = (1 - progress) * 0.6;
              
              ctx.fillStyle = `rgba(200, 200, 200, ${alpha})`;
              ctx.fillRect(x - size/2, y - size/2, size, size);
            }

          } else if (grid.patternType === "tornado") {
            // Tornade spiralée
            const time = elapsedTime * grid.animationSpeed * 0.002;
            ctx.fillStyle = '#332211';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const centerX = grid.x + grid.width / 2;
            for (let i = 0; i < 200; i++) {
              const progress = i / 200;
              const angle = progress * Math.PI * 20 + time;
              const radius = progress * 100 * (1 - progress);
              const x = centerX + Math.cos(angle) * radius;
              const y = grid.y + grid.height - progress * grid.height;
              const alpha = (1 - progress) * 0.8;
              
              ctx.fillStyle = `rgba(139, 69, 19, ${alpha})`;
              ctx.fillRect(x - 2, y - 2, 4, 4);
            }
          } else if (grid.patternType === "earthquake") {
            // Secousses sismiques
            const time = elapsedTime * grid.animationSpeed * 0.005;
            ctx.fillStyle = '#220000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let y = 0; y < grid.height; y += 5) {
              const shake = Math.sin(time * 10 + y * 0.1) * 20;
              const intensity = Math.abs(shake) / 20;
              ctx.fillStyle = `rgba(${100 + intensity * 155}, ${intensity * 50}, 0, 0.8)`;
              ctx.fillRect(grid.x + shake, grid.y + y, grid.width, 3);
            }
          } else if (grid.patternType === "volcano") {
            // Éruption volcanique
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            // Lave au sol
            const gradient = ctx.createLinearGradient(grid.x, grid.y, grid.x, grid.y + grid.height);
            gradient.addColorStop(0, '#ff4400');
            gradient.addColorStop(1, '#aa1100');
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Particules éjectées
            for (let i = 0; i < 50; i++) {
              const age = (time + i) % 3;
              const x = grid.x + grid.width/2 + Math.sin(i) * age * 30;
              const y = grid.y + grid.height - age * grid.height/3;
              const size = 5 - age;
              
              if (size > 0) {
                ctx.fillStyle = `rgba(255, ${255 - age * 85}, 0, ${1 - age/3})`;
                ctx.fillRect(x - size/2, y - size/2, size, size);
              }
            }
          } else if (grid.patternType === "ocean") {
            // Océan profond
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const wave1 = Math.sin((x * 0.01) + (time * 1.5));
                const wave2 = Math.sin((y * 0.015) + (time * 1.2));
                const depth = (y / grid.height) * 0.8 + 0.2;
                const movement = (wave1 + wave2) * 0.3 + 0.7;
                
                const r = movement * 10 * depth;
                const g = movement * (50 + depth * 100);
                const b = movement * (100 + depth * 155);
                
                const index = (y * grid.width + x) * 4;
                data[index] = r;
                data[index + 1] = g;
                data[index + 2] = b;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "desert") {
            // Sable du désert
            const time = elapsedTime * grid.animationSpeed * 0.0005;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const dune1 = Math.sin((x + time * 20) * 0.02) * 0.3;
                const dune2 = Math.sin((x + time * 15) * 0.01) * 0.5;
                const height = (dune1 + dune2 + 1) / 2;
                const sand = (y / grid.height) > height ? 1 : 0.3;
                
                const r = sand * (200 + Math.random() * 55);
                const g = sand * (180 + Math.random() * 50);
                const b = sand * (100 + Math.random() * 30);
                
                const index = (y * grid.width + x) * 4;
                data[index] = r;
                data[index + 1] = g;
                data[index + 2] = b;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "forest") {
            // Forêt animée
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            // Ciel
            const skyGradient = ctx.createLinearGradient(grid.x, grid.y, grid.x, grid.y + grid.height/2);
            skyGradient.addColorStop(0, '#87CEEB');
            skyGradient.addColorStop(1, '#98FB98');
            ctx.fillStyle = skyGradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height/2);
            
            // Sol
            ctx.fillStyle = '#228B22';
            ctx.fillRect(grid.x, grid.y + grid.height/2, grid.width, grid.height/2);
            
            // Arbres
            for (let i = 0; i < 10; i++) {
              const x = grid.x + (i * grid.width / 10) + Math.sin(time + i) * 10;
              const treeHeight = 50 + Math.sin(i) * 20;
              
              // Tronc
              ctx.fillStyle = '#8B4513';
              ctx.fillRect(x - 3, grid.y + grid.height/2, 6, treeHeight/2);
              
              // Feuillage
              ctx.fillStyle = `hsl(${120 + Math.sin(time + i) * 20}, 70%, 30%)`;
              ctx.fillRect(x - 15, grid.y + grid.height/2 - treeHeight/2, 30, treeHeight/2);
            }
          } else if (grid.patternType === "sunset") {
            // Coucher de soleil
            const time = elapsedTime * grid.animationSpeed * 0.0005;
            
            // Dégradé du ciel
            const gradient = ctx.createLinearGradient(grid.x, grid.y, grid.x, grid.y + grid.height);
            const intensity = Math.sin(time) * 0.2 + 0.8;
            
            gradient.addColorStop(0, `rgb(${255 * intensity}, ${140 * intensity}, ${0})`);
            gradient.addColorStop(0.3, `rgb(${255 * intensity}, ${165 * intensity}, ${0})`);
            gradient.addColorStop(0.6, `rgb(${255 * intensity}, ${69 * intensity}, ${0})`);
            gradient.addColorStop(1, `rgb(${25 * intensity}, ${25 * intensity}, ${112 * intensity})`);
            
            ctx.fillStyle = gradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);



          } else if (grid.patternType === "quantum") {
            // Effet quantique
            const time = elapsedTime * grid.animationSpeed * 0.003;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Particules quantiques
            for (let i = 0; i < 50; i++) {
              const x = grid.x + (Math.sin(time + i) * 0.5 + 0.5) * grid.width;
              const y = grid.y + (Math.cos(time * 1.3 + i) * 0.5 + 0.5) * grid.height;
              const size = Math.sin(time * 2 + i) * 3 + 4;
              const alpha = Math.sin(time * 3 + i) * 0.5 + 0.5;
              
              ctx.fillStyle = `rgba(${100 + alpha * 155}, ${200 + alpha * 55}, 255, ${alpha})`;
              ctx.fillRect(x - size/2, y - size/2, size, size);
              
              // Connexions
              for (let j = i + 1; j < Math.min(i + 5, 50); j++) {
                const x2 = grid.x + (Math.sin(time + j) * 0.5 + 0.5) * grid.width;
                const y2 = grid.y + (Math.cos(time * 1.3 + j) * 0.5 + 0.5) * grid.height;
                const distance = Math.sqrt((x2-x)**2 + (y2-y)**2);
                
                if (distance < 100) {
                  ctx.strokeStyle = `rgba(100, 200, 255, ${(1 - distance/100) * alpha * 0.3})`;
                  ctx.lineWidth = 1;
                  ctx.beginPath();
                  ctx.moveTo(x, y);
                  ctx.lineTo(x2, y2);
                  ctx.stroke();
                }
              }
            }
          } else if (grid.patternType === "blackhole") {
            // Trou noir avec distorsion
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            // Espace profond
            ctx.fillStyle = '#000005';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Disque d'accrétion
            for (let i = 0; i < 100; i++) {
              const angle = (i / 100) * Math.PI * 2 + time * 2;
              const radius = 50 + Math.sin(i * 0.5 + time) * 20;
              const x = centerX + Math.cos(angle) * radius;
              const y = centerY + Math.sin(angle) * radius;
              const intensity = Math.sin(angle + time) * 0.5 + 0.5;
              
              ctx.fillStyle = `rgba(255, ${100 + intensity * 155}, 0, ${intensity})`;
              ctx.fillRect(x - 1, y - 1, 2, 2);
            }
            
            // Trou noir central
            ctx.fillStyle = '#000';
            ctx.beginPath();
            ctx.arc(centerX, centerY, 20, 0, Math.PI * 2);
            ctx.fill();
          } else if (grid.patternType === "nebula") {
            // Nébuleuse colorée
            const time = elapsedTime * grid.animationSpeed * 0.0005;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const centerX = grid.width / 2;
                const centerY = grid.height / 2;
                const distance = Math.sqrt((x - centerX)**2 + (y - centerY)**2);
                const angle = Math.atan2(y - centerY, x - centerX);
                
                const density = Math.sin(distance * 0.02 + time) * Math.sin(angle * 3 + time) * 0.5 + 0.5;
                const hue = (angle * 180 / Math.PI + time * 20) % 360;
                
                // Convert HSL to RGB for nebula colors
                const s = 0.8;
                const l = density * 0.6;
                const c = (1 - Math.abs(2 * l - 1)) * s;
                const x_val = c * (1 - Math.abs(((hue / 60) % 2) - 1));
                const m = l - c / 2;
                
                let r, g, b;
                if (hue >= 0 && hue < 60) [r, g, b] = [c, x_val, 0];
                else if (hue >= 60 && hue < 120) [r, g, b] = [x_val, c, 0];
                else if (hue >= 120 && hue < 180) [r, g, b] = [0, c, x_val];
                else if (hue >= 180 && hue < 240) [r, g, b] = [0, x_val, c];
                else if (hue >= 240 && hue < 300) [r, g, b] = [x_val, 0, c];
                else [r, g, b] = [c, 0, x_val];
                
                const index = (y * grid.width + x) * 4;
                data[index] = (r + m) * 255;
                data[index + 1] = (g + m) * 255;
                data[index + 2] = (b + m) * 255;
                data[index + 3] = density * 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "comet") {
            // Comète avec traînée
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#000011';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const cometX = grid.x + ((time * 100) % (grid.width + 100)) - 50;
            const cometY = grid.y + grid.height * 0.3 + Math.sin(time * 3) * 50;
            
            // Traînée
            for (let i = 0; i < 20; i++) {
              const trailX = cometX - i * 10;
              const trailY = cometY + i * 2;
              const alpha = (20 - i) / 20;
              
              ctx.fillStyle = `rgba(100, 150, 255, ${alpha * 0.6})`;
              ctx.fillRect(trailX - 2, trailY - 2, 4, 4);
            }
            
            // Noyau de la comète
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(cometX - 3, cometY - 3, 6, 6);
          } else if (grid.patternType === "satellite") {
            // Satellite en orbite
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000033';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Terre (centre)
            const earthX = grid.x + grid.width / 2;
            const earthY = grid.y + grid.height / 2;
            const earthGradient = ctx.createRadialGradient(earthX, earthY, 0, earthX, earthY, 40);
            earthGradient.addColorStop(0, '#4488ff');
            earthGradient.addColorStop(0.7, '#2266bb');
            earthGradient.addColorStop(1, '#001122');
            ctx.fillStyle = earthGradient;
            ctx.beginPath();
            ctx.arc(earthX, earthY, 40, 0, Math.PI * 2);
            ctx.fill();
            
            // Satellites
            for (let i = 0; i < 3; i++) {
              const angle = time + (i * Math.PI * 2 / 3);
              const radius = 80 + i * 20;
              const satX = earthX + Math.cos(angle) * radius;
              const satY = earthY + Math.sin(angle) * radius;
              
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(satX - 2, satY - 2, 4, 4);
              
              // Signal
              ctx.strokeStyle = `rgba(255, 255, 255, ${Math.sin(time * 5 + i) * 0.3 + 0.3})`;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.arc(satX, satY, 15, 0, Math.PI * 2);
              ctx.stroke();
            }
          } else if (grid.patternType === "radar") {
            // Radar amélioré et agrandi
            const time = elapsedTime * grid.animationSpeed * 0.003;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            const maxRadius = Math.min(grid.width, grid.height) / 2 * 0.9;
            
            ctx.fillStyle = '#001100';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Cercles de portée agrandis
            for (let i = 1; i <= 6; i++) {
              ctx.strokeStyle = `rgba(0, 255, 0, ${0.3 + i * 0.1})`;
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.arc(centerX, centerY, (i * maxRadius) / 6, 0, Math.PI * 2);
              ctx.stroke();
            }
            
            // Lignes de quadrant
            ctx.strokeStyle = 'rgba(0, 255, 0, 0.3)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(centerX - maxRadius, centerY);
            ctx.lineTo(centerX + maxRadius, centerY);
            ctx.moveTo(centerX, centerY - maxRadius);
            ctx.lineTo(centerX, centerY + maxRadius);
            ctx.stroke();
            
            // Faisceau rotatif agrandi
            const angle = time * 2;
            const beamLength = maxRadius;
            
            // Effet de traînée du faisceau
            for (let trail = 0; trail < 20; trail++) {
              const trailAngle = angle - trail * 0.05;
              const alpha = (20 - trail) / 20 * 0.6;
              ctx.strokeStyle = `rgba(0, 255, 0, ${alpha})`;
              ctx.lineWidth = 4;
              ctx.beginPath();
              ctx.moveTo(centerX, centerY);
              ctx.lineTo(
                centerX + Math.cos(trailAngle) * beamLength,
                centerY + Math.sin(trailAngle) * beamLength
              );
              ctx.stroke();
            }
            
            // Cibles détectées
            for (let i = 0; i < 15; i++) {
              const targetAngle = Math.sin(i + time * 0.5) * Math.PI * 2;
              const targetRadius = (50 + Math.sin(i * 2) * 100) * (maxRadius / 200);
              const targetX = centerX + Math.cos(targetAngle) * targetRadius;
              const targetY = centerY + Math.sin(targetAngle) * targetRadius;
              const alpha = Math.sin(time * 2 + i) * 0.5 + 0.5;
              const size = 2 + alpha * 3;
              
              ctx.fillStyle = `rgba(255, 255, 0, ${alpha})`;
              ctx.fillRect(targetX - size/2, targetY - size/2, size, size);
              
              // Cercle de détection
              ctx.strokeStyle = `rgba(255, 255, 0, ${alpha * 0.5})`;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.arc(targetX, targetY, size * 2, 0, Math.PI * 2);
              ctx.stroke();
            }
          } else if (grid.patternType === "sonar") {
            // Sonar sous-marin
            const time = elapsedTime * grid.animationSpeed * 0.002;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            ctx.fillStyle = '#000033';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Pings sonar
            for (let i = 0; i < 5; i++) {
              const pingTime = (time + i * 0.5) % 3;
              const radius = pingTime * 100;
              const alpha = Math.max(0, 1 - pingTime / 3);
              
              ctx.strokeStyle = `rgba(0, 255, 255, ${alpha})`;
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
              ctx.stroke();
            }
            
            // Objets détectés
            for (let i = 0; i < 8; i++) {
              const objX = centerX + Math.sin(i * 3 + time * 0.1) * (50 + i * 20);
              const objY = centerY + Math.cos(i * 2 + time * 0.15) * (30 + i * 15);
              const intensity = Math.sin(time * 4 + i) * 0.5 + 0.5;
              
              ctx.fillStyle = `rgba(0, 255, 255, ${intensity})`;
              ctx.fillRect(objX - 3, objY - 3, 6, 6);
            }
          } else if (grid.patternType === "xray") {
            // Vision rayons X
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Structure "osseuse"
            for (let y = 0; y < grid.height; y += 10) {
              for (let x = 0; x < grid.width; x += 10) {
                const density = Math.sin((x + y + time * 50) * 0.05) * 0.5 + 0.5;
                const intensity = density * (Math.random() * 0.5 + 0.5);
                
                ctx.fillStyle = `rgba(255, 255, 255, ${intensity * 0.8})`;
                ctx.fillRect(grid.x + x, grid.y + y, 8, 8);
              }
            }
            
            // Effet de scan
            const scanY = grid.y + ((time * 100) % grid.height);
            ctx.fillStyle = 'rgba(0, 255, 0, 0.3)';
            ctx.fillRect(grid.x, scanY - 2, grid.width, 4);
          } else if (grid.patternType === "thermal") {
            // Vision thermique
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const heat = Math.sin((x + time * 30) * 0.05) * Math.sin((y + time * 20) * 0.03) * 0.5 + 0.5;
                
                let r, g, b;
                if (heat < 0.25) {
                  r = 0;
                  g = 0;
                  b = 255 * (heat * 4);
                } else if (heat < 0.5) {
                  r = 0;
                  g = 255 * ((heat - 0.25) * 4);
                  b = 255;
                } else if (heat < 0.75) {
                  r = 255 * ((heat - 0.5) * 4);
                  g = 255;
                  b = 255 * (1 - (heat - 0.5) * 4);
                } else {
                  r = 255;
                  g = 255 * (1 - (heat - 0.75) * 4);
                  b = 0;
                }
                
                const index = (y * grid.width + x) * 4;
                data[index] = r;
                data[index + 1] = g;
                data[index + 2] = b;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "microscope") {
            // Vue microscopique
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#f0f0f0';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Cellules
            for (let i = 0; i < 20; i++) {
              const cellX = grid.x + (Math.sin(i * 2.1 + time * 0.5) * 0.5 + 0.5) * grid.width;
              const cellY = grid.y + (Math.cos(i * 1.7 + time * 0.3) * 0.5 + 0.5) * grid.height;
              const size = 15 + Math.sin(i + time) * 5;
              const hue = (i * 30 + time * 10) % 360;
              
              ctx.fillStyle = `hsla(${hue}, 60%, 50%, 0.7)`;
              ctx.beginPath();
              ctx.arc(cellX, cellY, size, 0, Math.PI * 2);
              ctx.fill();
              
              // Noyau
              ctx.fillStyle = `hsla(${hue + 180}, 80%, 30%, 0.8)`;
              ctx.beginPath();
              ctx.arc(cellX, cellY, size * 0.4, 0, Math.PI * 2);
              ctx.fill();
            }
          } else if (grid.patternType === "telescope") {
            // Vue télescope
            const time = elapsedTime * grid.animationSpeed * 0.0005;
            
            ctx.fillStyle = '#000008';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Galaxies lointaines
            for (let i = 0; i < 15; i++) {
              const galX = grid.x + (Math.sin(i * 7.3) * 0.5 + 0.5) * grid.width;
              const galY = grid.y + (Math.cos(i * 5.7) * 0.5 + 0.5) * grid.height;
              const rotation = time + i;
              const brightness = Math.sin(time * 2 + i) * 0.3 + 0.7;
              
              ctx.save();
              ctx.translate(galX, galY);
              ctx.rotate(rotation);
              
              // Spirale galactique
              for (let j = 0; j < 50; j++) {
                const angle = (j / 50) * Math.PI * 4;
                const radius = j * 2;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;
                const alpha = (50 - j) / 50 * brightness;
                
                ctx.fillStyle = `rgba(255, 200, 100, ${alpha * 0.6})`;
                ctx.fillRect(x - 1, y - 1, 2, 2);
              }
              
              ctx.restore();
            }
          } else if (grid.patternType === "heartbeat") {
            // Un battement cardiaque par seconde - variable
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Ligne ECG avec un battement par seconde
            ctx.strokeStyle = '#00ff00';
            ctx.lineWidth = 3;
            ctx.beginPath();
            
            const centerY = grid.y + grid.height / 2;
            const pixelsPerSecond = grid.width; // 1 seconde = largeur de la grille
            
            for (let x = 0; x < grid.width; x += 2) {
              const timePosition = (x + time * pixelsPerSecond) / pixelsPerSecond;
              const beatCycle = timePosition % 1; // Un battement par seconde
              let y = centerY;
              
              // Un seul battement par cycle avec variation
              if (beatCycle > 0.1 && beatCycle < 0.3) {
                const beat = (beatCycle - 0.1) / 0.2;
                
                // Variabilité du battement basée sur le temps
                const heartRateVariation = Math.sin(time * 0.5) * 0.3 + 0.7; // Variation entre 0.4 et 1.0
                const intensityVariation = Math.sin(time * 0.3) * 0.4 + 0.6; // Variation de l'intensité
                
                if (beat < 0.2) {
                  // Pic principal vers le haut (variable)
                  y = grid.y + grid.height * (1 - beat * 5 * intensityVariation);
                } else if (beat < 0.5) {
                  // Descente rapide vers le bas (variable)
                  y = grid.y + grid.height * ((beat - 0.2) * 3.33 * heartRateVariation);
                } else if (beat < 0.8) {
                  // Pic négatif (variable)
                  y = grid.y + grid.height * (1 - (beat - 0.5) * 3.33 * intensityVariation);
                } else {
                  // Retour à la normale
                  y = centerY + (y - centerY) * (1 - (beat - 0.8) * 5);
                }
              } else {
                // Ligne de base avec légère variation naturelle
                y = centerY + Math.sin(x * 0.1 + time * 2) * 3;
              }
              
              if (x === 0) ctx.moveTo(grid.x + x, y);
              else ctx.lineTo(grid.x + x, y);
            }
            ctx.stroke();
            
            // Grille médicale
            ctx.strokeStyle = 'rgba(0, 255, 0, 0.2)';
            ctx.lineWidth = 1;
            for (let x = 0; x < grid.width; x += 25) {
              ctx.beginPath();
              ctx.moveTo(grid.x + x, grid.y);
              ctx.lineTo(grid.x + x, grid.y + grid.height);
              ctx.stroke();
            }
            for (let y = 0; y < grid.height; y += 25) {
              ctx.beginPath();
              ctx.moveTo(grid.x, grid.y + y);
              ctx.lineTo(grid.x + grid.width, grid.y + y);
              ctx.stroke();
            }
          } else if (grid.patternType === "brainwave") {
            // Ondes cérébrales
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#001122';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Plusieurs fréquences d'ondes
            const waves = [
              { freq: 1, amp: 20, color: '#ff0066', name: 'Delta' },
              { freq: 2, amp: 15, color: '#0066ff', name: 'Theta' },
              { freq: 4, amp: 10, color: '#00ff66', name: 'Alpha' },
              { freq: 8, amp: 8, color: '#ff6600', name: 'Beta' }
            ];
            
            waves.forEach((wave, waveIndex) => {
              ctx.strokeStyle = wave.color;
              ctx.lineWidth = 2;
              ctx.beginPath();
              
              const baseY = grid.y + (waveIndex + 1) * grid.height / (waves.length + 1);
              
              for (let x = 0; x < grid.width; x += 2) {
                const waveX = (x + time * 50) * 0.02;
                const y = baseY + Math.sin(waveX * wave.freq) * wave.amp;
                
                if (x === 0) ctx.moveTo(grid.x + x, y);
                else ctx.lineTo(grid.x + x, y);
              }
              ctx.stroke();
            });
          } else if (grid.patternType === "virus") {
            // Virus microscopique avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let i = 0; i < 25; i++) {
              const virusX = grid.x + (Math.sin(i * 2.3 + time * 0.5) * 0.5 + 0.5) * grid.width;
              const virusY = grid.y + (Math.cos(i * 1.9 + time * 0.3) * 0.5 + 0.5) * grid.height;
              const size = 8 + Math.sin(i + time * 2) * 3;
              const pulse = Math.sin(time * 3 + i) * 0.5 + 0.5;
              
              // Corps du virus
              ctx.fillStyle = `rgba(255, 0, 100, ${pulse})`;
              ctx.beginPath();
              ctx.arc(virusX, virusY, size, 0, Math.PI * 2);
              ctx.fill();
              
              // Spikes
              for (let spike = 0; spike < 8; spike++) {
                const angle = (spike / 8) * Math.PI * 2 + time + i;
                const spikeX = virusX + Math.cos(angle) * (size + 5);
                const spikeY = virusY + Math.sin(angle) * (size + 5);
                
                ctx.fillStyle = `rgba(255, 100, 0, ${pulse * 0.8})`;
                ctx.fillRect(spikeX - 1, spikeY - 1, 2, 2);
              }
            }
          } else if (grid.patternType === "bacteria") {
            // Bactéries
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#002200';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let i = 0; i < 30; i++) {
              const bacteria = {
                x: grid.x + (Math.sin(i * 3.7 + time * 0.3) * 0.5 + 0.5) * grid.width,
                y: grid.y + (Math.cos(i * 2.1 + time * 0.4) * 0.5 + 0.5) * grid.height,
                length: 20 + Math.sin(i) * 10,
                width: 5 + Math.sin(i * 2) * 2,
                angle: time * 0.5 + i
              };
              
              ctx.save();
              ctx.translate(bacteria.x, bacteria.y);
              ctx.rotate(bacteria.angle);
              
              // Corps elliptique
              ctx.fillStyle = `rgba(0, 255, 100, ${Math.sin(time * 2 + i) * 0.3 + 0.7})`;
              ctx.fillRect(-bacteria.length/2, -bacteria.width/2, bacteria.length, bacteria.width);
              
              // Flagelles
              for (let f = 0; f < 4; f++) {
                const flagX = -bacteria.length/2 - 10;
                const flagY = (f - 1.5) * 3;
                const wave = Math.sin(time * 5 + i + f) * 5;
                
                ctx.strokeStyle = `rgba(0, 200, 50, 0.6)`;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(flagX, flagY);
                ctx.lineTo(flagX - 15, flagY + wave);
                ctx.stroke();
              }
              
              ctx.restore();
            }
          } else if (grid.patternType === "molecule") {
            // Molécules
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Molécules complexes
            for (let mol = 0; mol < 5; mol++) {
              const molX = grid.x + (mol + 0.5) * grid.width / 5;
              const molY = grid.y + grid.height / 2;
              
              // Atomes
              for (let atom = 0; atom < 6; atom++) {
                const angle = (atom / 6) * Math.PI * 2 + time + mol;
                const radius = 30 + Math.sin(time + atom) * 10;
                const atomX = molX + Math.cos(angle) * radius;
                const atomY = molY + Math.sin(angle) * radius;
                const atomSize = 5 + Math.sin(time * 2 + atom) * 2;
                
                // Couleur selon le type d'atome
                const atomType = atom % 4;
                const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00'];
                
                ctx.fillStyle = colors[atomType];
                ctx.beginPath();
                ctx.arc(atomX, atomY, atomSize, 0, Math.PI * 2);
                ctx.fill();
                
                // Liaisons
                if (atom < 5) {
                  const nextAngle = ((atom + 1) / 6) * Math.PI * 2 + time + mol;
                  const nextRadius = 30 + Math.sin(time + atom + 1) * 10;
                  const nextX = molX + Math.cos(nextAngle) * nextRadius;
                  const nextY = molY + Math.sin(nextAngle) * nextRadius;
                  
                  ctx.strokeStyle = '#888888';
                  ctx.lineWidth = 1;
                  ctx.beginPath();
                  ctx.moveTo(atomX, atomY);
                  ctx.lineTo(nextX, nextY);
                  ctx.stroke();
                }
              }
            }
          } else if (grid.patternType === "atom") {
            // Atome avec électrons
            const time = elapsedTime * grid.animationSpeed * 0.003;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Noyau
            const nucleusGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 15);
            nucleusGradient.addColorStop(0, '#ffff00');
            nucleusGradient.addColorStop(1, '#ff8800');
            ctx.fillStyle = nucleusGradient;
            ctx.beginPath();
            ctx.arc(centerX, centerY, 15, 0, Math.PI * 2);
            ctx.fill();
            
            // Orbites électroniques
            for (let orbit = 0; orbit < 3; orbit++) {
              const orbitRadius = 40 + orbit * 30;
              
              // Orbite
              ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.arc(centerX, centerY, orbitRadius, 0, Math.PI * 2);
              ctx.stroke();
              
              // Électrons
              const numElectrons = orbit + 1;
              for (let electron = 0; electron < numElectrons; electron++) {
                const angle = (electron / numElectrons) * Math.PI * 2 + time * (orbit + 1);
                const elecX = centerX + Math.cos(angle) * orbitRadius;
                const elecY = centerY + Math.sin(angle) * orbitRadius;
                
                ctx.fillStyle = '#00ffff';
                ctx.beginPath();
                ctx.arc(elecX, elecY, 3, 0, Math.PI * 2);
                ctx.fill();
                
                // Traînée
                for (let trail = 1; trail < 5; trail++) {
                  const trailAngle = angle - trail * 0.1;
                  const trailX = centerX + Math.cos(trailAngle) * orbitRadius;
                  const trailY = centerY + Math.sin(trailAngle) * orbitRadius;
                  
                  ctx.fillStyle = `rgba(0, 255, 255, ${(5 - trail) / 5 * 0.5})`;
                  ctx.fillRect(trailX - 1, trailY - 1, 2, 2);
                }
              }
            }
          } else if (grid.patternType === "electron" || grid.patternType === "proton" || grid.patternType === "neutron") {
            // Particules subatomiques
            const time = elapsedTime * grid.animationSpeed * 0.004;
            const particleType = grid.patternType;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const colors = {
              electron: '#00ffff',
              proton: '#ff0000', 
              neutron: '#888888'
            };
            
            for (let i = 0; i < 100; i++) {
              const x = grid.x + (Math.sin(time + i) * 0.5 + 0.5) * grid.width;
              const y = grid.y + (Math.cos(time * 1.2 + i) * 0.5 + 0.5) * grid.height;
              const size = Math.sin(time * 3 + i) * 2 + 3;
              const alpha = Math.sin(time * 2 + i) * 0.5 + 0.5;
              
              ctx.fillStyle = colors[particleType] || colors.electron;
              ctx.globalAlpha = alpha;
              ctx.fillRect(x - size/2, y - size/2, size, size);
            }
            ctx.globalAlpha = 1;

          } else if (grid.patternType === "magnetic_field") {
            // Champ magnétique visualisé
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000022';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Lignes de champ
            for (let i = 0; i < 15; i++) {
              ctx.strokeStyle = `hsl(${(time * 50 + i * 20) % 360}, 70%, 50%)`;
              ctx.lineWidth = 2;
              ctx.beginPath();
              
              for (let x = 0; x < grid.width; x += 8) {
                const y = grid.height/2 + Math.sin((x + time * 80 + i * 30) * 0.02) * (grid.height/4);
                if (x === 0) ctx.moveTo(grid.x + x, grid.y + y);
                else ctx.lineTo(grid.x + x, grid.y + y);
              }
              ctx.stroke();
            }
          } else if (grid.patternType === "gravity") {
            // Effet gravitationnel
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Distorsion gravitationnelle
            for (let i = 0; i < 50; i++) {
              const angle = (i / 50) * Math.PI * 2;
              const baseRadius = 30 + i * 3;
              const distortion = Math.sin(time * 2 + i * 0.1) * 15;
              const radius = baseRadius + distortion;
              
              const x = centerX + Math.cos(angle) * radius;
              const y = centerY + Math.sin(angle) * radius;
              const alpha = (50 - i) / 50;
              
              ctx.fillStyle = `rgba(100, 0, 255, ${alpha * 0.8})`;
              ctx.fillRect(x - 1, y - 1, 2, 2);
            }
          } else if (grid.patternType === "waves") {
            // Ondes génériques
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#000044';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            for (let wave = 0; wave < 8; wave++) {
              ctx.strokeStyle = `hsl(${(wave * 45 + time * 30) % 360}, 80%, 60%)`;
              ctx.lineWidth = 3;
              ctx.beginPath();
              
              const baseY = grid.y + (wave + 1) * grid.height / 9;
              for (let x = 0; x < grid.width; x += 4) {
                const waveY = baseY + Math.sin((x + time * 100 + wave * 20) * 0.02) * 20;
                if (x === 0) ctx.moveTo(grid.x + x, waveY);
                else ctx.lineTo(grid.x + x, waveY);
              }
              ctx.stroke();
            }
          } else if (grid.patternType === "frequency") {
            // Visualisation de fréquence
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#001122';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Barres de fréquence
            const numBars = 20;
            for (let i = 0; i < numBars; i++) {
              const frequency = Math.sin(time * 3 + i * 0.3) * 0.5 + 0.5;
              const barHeight = frequency * grid.height * 0.8;
              const x = grid.x + (i * grid.width / numBars);
              const y = grid.y + grid.height - barHeight;
              
              ctx.fillStyle = `hsl(${i * 18 + time * 20}, 80%, 60%)`;
              ctx.fillRect(x, y, grid.width / numBars - 2, barHeight);
            }
          } else if (grid.patternType === "amplitude") {
            // Visualisation d'amplitude
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Signal d'amplitude variable
            ctx.strokeStyle = '#00ff88';
            ctx.lineWidth = 3;
            ctx.beginPath();
            
            const centerY = grid.y + grid.height / 2;
            for (let x = 0; x < grid.width; x += 2) {
              const amplitude = Math.sin((x + time * 50) * 0.01) * (grid.height / 4);
              const envelope = Math.sin((x + time * 30) * 0.005) * 0.5 + 0.5;
              const y = centerY + amplitude * envelope;
              
              if (x === 0) ctx.moveTo(grid.x + x, y);
              else ctx.lineTo(grid.x + x, y);
            }
            ctx.stroke();
          } else if (grid.patternType === "interference") {
            // Interférences d'ondes
            const time = elapsedTime * grid.animationSpeed * 0.001;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const source1X = grid.width * 0.3;
                const source1Y = grid.height / 2;
                const source2X = grid.width * 0.7;
                const source2Y = grid.height / 2;
                
                const dist1 = Math.sqrt((x - source1X)**2 + (y - source1Y)**2);
                const dist2 = Math.sqrt((x - source2X)**2 + (y - source2Y)**2);
                
                const wave1 = Math.sin((dist1 * 0.1) - (time * 5));
                const wave2 = Math.sin((dist2 * 0.1) - (time * 5));
                const interference = (wave1 + wave2) / 2;
                
                const intensity = (interference + 1) / 2;
                const index = (y * grid.width + x) * 4;
                data[index] = intensity * 255;
                data[index + 1] = intensity * 100;
                data[index + 2] = intensity * 255;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "diffraction") {
            // Diffraction
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            // Motif de diffraction
            for (let i = 0; i < 100; i++) {
              const angle = (i / 100) * Math.PI * 2;
              const radius = 20 + Math.sin(angle * 6 + time * 2) * 15;
              const x = centerX + Math.cos(angle) * radius;
              const y = centerY + Math.sin(angle) * radius;
              const intensity = Math.sin(angle * 6 + time * 2) * 0.5 + 0.5;
              
              ctx.fillStyle = `rgba(255, 255, 255, ${intensity})`;
              ctx.fillRect(x - 1, y - 1, 2, 2);
            }
          } else if (grid.patternType === "refraction") {
            // Réfraction
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#001144';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Rayons qui se plient
            for (let i = 0; i < 10; i++) {
              const startX = grid.x + (i * grid.width / 10);
              const bendPoint = grid.y + grid.height / 2;
              const refractAngle = Math.sin(time + i) * 0.5;
              const endX = startX + refractAngle * 50;
              
              ctx.strokeStyle = `hsl(${i * 36 + time * 20}, 80%, 60%)`;
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(startX, grid.y);
              ctx.lineTo(startX, bendPoint);
              ctx.lineTo(endX, grid.y + grid.height);
              ctx.stroke();
            }
          } else if (grid.patternType === "reflection") {
            // Réflexion
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#222';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Ligne de réflexion
            ctx.strokeStyle = '#888';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(grid.x, grid.y + grid.height / 2);
            ctx.lineTo(grid.x + grid.width, grid.y + grid.height / 2);
            ctx.stroke();
            
            // Rayons incidents et réfléchis
            for (let i = 0; i < 8; i++) {
              const x = grid.x + (i + 1) * grid.width / 9;
              const mirrorY = grid.y + grid.height / 2;
              const angle = Math.sin(time + i) * 0.3;
              
              // Rayon incident
              ctx.strokeStyle = '#ff6666';
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(x - angle * 30, grid.y);
              ctx.lineTo(x, mirrorY);
              ctx.stroke();
              
              // Rayon réfléchi
              ctx.strokeStyle = '#66ff66';
              ctx.beginPath();
              ctx.moveTo(x, mirrorY);
              ctx.lineTo(x + angle * 30, grid.y + grid.height);
              ctx.stroke();
            }

          } else if (grid.patternType === "lens") {
            // Lentille focalisante avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const lensX = grid.x + grid.width / 2;
            
            // Lentille
            ctx.strokeStyle = '#888';
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.arc(lensX - 15, grid.y + grid.height / 2, 30, -Math.PI/2, Math.PI/2);
            ctx.arc(lensX + 15, grid.y + grid.height / 2, 30, Math.PI/2, -Math.PI/2);
            ctx.stroke();
            
            // Rayons convergeant
            for (let i = 0; i < 8; i++) {
              const rayY = grid.y + (i + 1) * grid.height / 9;
              const focusY = grid.y + grid.height / 2;
              const convergence = Math.sin(time) * 0.3 + 0.7;
              
              ctx.strokeStyle = `hsl(${i * 45}, 80%, 60%)`;
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(grid.x, rayY);
              ctx.lineTo(lensX, rayY);
              ctx.lineTo(grid.x + grid.width, focusY + (rayY - focusY) * convergence);
              ctx.stroke();
            }
          } else if (grid.patternType === "mirror") {
            // Miroir
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#111';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Surface du miroir
            const mirrorX = grid.x + grid.width / 2;
            ctx.fillStyle = '#silver';
            ctx.fillRect(mirrorX - 5, grid.y, 10, grid.height);
            
            // Réflexions multiples
            for (let i = 0; i < 6; i++) {
              const y = grid.y + (i + 1) * grid.height / 7;
              const offset = Math.sin(time + i) * 20;
              
              // Objet
              ctx.fillStyle = `hsl(${i * 60}, 80%, 60%)`;
              ctx.fillRect(grid.x + 50 + offset, y - 5, 10, 10);
              
              // Réflexion
              ctx.fillStyle = `hsla(${i * 60}, 80%, 60%, 0.6)`;
              ctx.fillRect(mirrorX + 50 - offset, y - 5, 10, 10);
            }
          } else if (grid.patternType === "holodeck") {
            // Holodeck futuriste avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.003;
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Grille holographique
            for (let x = 0; x < grid.width; x += 20) {
              for (let y = 0; y < grid.height; y += 20) {
                const intensity = Math.sin(time + x * 0.1 + y * 0.1) * 0.5 + 0.5;
                ctx.fillStyle = `rgba(0, 255, 255, ${intensity * 0.6})`;
                ctx.fillRect(grid.x + x, grid.y + y, 2, 2);
              }
            }
          } else if (grid.patternType === "warp") {
            // Distorsion warp
            const time = elapsedTime * grid.animationSpeed * 0.002;
            const imageData = ctx.createImageData(grid.width, grid.height);
            const data = imageData.data;
            
            for (let y = 0; y < grid.height; y++) {
              for (let x = 0; x < grid.width; x++) {
                const centerX = grid.width / 2;
                const centerY = grid.height / 2;
                const distance = Math.sqrt((x - centerX)**2 + (y - centerY)**2);
                const warp = Math.sin(distance * 0.05 - time * 3) * 0.5 + 0.5;
                
                const index = (y * grid.width + x) * 4;
                data[index] = warp * 100;
                data[index + 1] = warp * 50;
                data[index + 2] = warp * 255;
                data[index + 3] = 255;
              }
            }
            ctx.putImageData(imageData, grid.x, grid.y);
          } else if (grid.patternType === "teleport") {
            // Téléportation
            const time = elapsedTime * grid.animationSpeed * 0.004;
            ctx.fillStyle = '#000033';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Particules de téléportation
            for (let i = 0; i < 100; i++) {
              const phase = (time + i * 0.1) % 2;
              if (phase < 1) {
                const x = grid.x + Math.random() * grid.width;
                const y = grid.y + phase * grid.height;
                const size = (1 - phase) * 5;
                
                ctx.fillStyle = `rgba(255, 255, 255, ${1 - phase})`;
                ctx.fillRect(x - size/2, y - size/2, size, size);
              }
            }
          } else if (grid.patternType === "portal") {
            // Tunnel de portail dimensionnel GIGANTESQUE
            const time = elapsedTime * grid.animationSpeed * 0.002;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            const maxRadius = Math.max(grid.width, grid.height) * 0.7;
            
            // Tunnel d'anneaux en perspective - effet de profondeur
            for (let i = 0; i < 40; i++) {
              const depth = i / 40;
              const tunnelProgress = (time * 0.5 + depth) % 1;
              const radius = maxRadius * (1 - tunnelProgress) * (1 - tunnelProgress);
              const opacity = (1 - tunnelProgress) * (1 - depth * 0.5);
              
              if (radius > 5 && opacity > 0.1) {
                const rotation = time * (depth + 1) * 0.3;
                const intensity = Math.sin(rotation + i) * 0.5 + 0.5;
                const pulseIntensity = Math.sin(time * 4 + i * 0.3) * 0.4 + 0.8;
                
                // Couleurs du tunnel qui changent avec la profondeur
                const hue = (i * 15 + time * 120 + depth * 180) % 360;
                
                ctx.strokeStyle = `hsl(${hue}, 90%, ${50 + intensity * 30}%)`;
                ctx.lineWidth = 2 + opacity * 8;
                ctx.shadowColor = ctx.strokeStyle;
                ctx.shadowBlur = 25 * pulseIntensity * opacity;
                
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                ctx.stroke();
                
                // Particules énergétiques tourbillonnantes
                const numParticles = Math.floor(radius * 0.1) + 4;
                for (let j = 0; j < numParticles; j++) {
                  const particleAngle = (j / numParticles) * Math.PI * 2 + rotation + time * 2;
                  const particleRadius = radius + Math.sin(time * 5 + j) * 10;
                  const particleX = centerX + Math.cos(particleAngle) * particleRadius;
                  const particleY = centerY + Math.sin(particleAngle) * particleRadius;
                  const particleAlpha = Math.sin(time * 6 + i + j) * 0.5 + 0.5;
                  
                  ctx.shadowBlur = 0;
                  ctx.fillStyle = `rgba(255, 255, 255, ${particleAlpha * opacity})`;
                  const particleSize = 2 + opacity * 4;
                  ctx.fillRect(particleX - particleSize/2, particleY - particleSize/2, particleSize, particleSize);
                }
              }
            }
            
            // Centre lumineux du tunnel
            const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 30);
            const coreIntensity = Math.sin(time * 3) * 0.3 + 0.7;
            coreGradient.addColorStop(0, `rgba(255, 255, 255, ${coreIntensity})`);
            coreGradient.addColorStop(0.5, `rgba(100, 200, 255, ${coreIntensity * 0.5})`);
            coreGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
            ctx.fillStyle = coreGradient;
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            ctx.shadowBlur = 0;
          } else if (grid.patternType === "dimension") {
            // Dimension parallèle
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            // Plusieurs couches de réalité
            for (let layer = 0; layer < 3; layer++) {
              const alpha = 0.4 - layer * 0.1;
              const offset = layer * 20;
              
              ctx.fillStyle = `rgba(${100 + layer * 50}, ${50 + layer * 100}, 255, ${alpha})`;
              ctx.fillRect(
                grid.x + Math.sin(time + layer) * offset,
                grid.y + Math.cos(time + layer) * offset,
                grid.width,
                grid.height
              );
            }
          } else if (grid.patternType === "multiverse") {
            // Multivers
            const time = elapsedTime * grid.animationSpeed * 0.0005;
            ctx.fillStyle = '#000008';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Bulles d'univers
            for (let i = 0; i < 20; i++) {
              const universeX = grid.x + (Math.sin(i * 7.3 + time) * 0.5 + 0.5) * grid.width;
              const universeY = grid.y + (Math.cos(i * 5.1 + time) * 0.5 + 0.5) * grid.height;
              const size = 10 + Math.sin(time * 2 + i) * 5;
              
              const gradient = ctx.createRadialGradient(universeX, universeY, 0, universeX, universeY, size);
              gradient.addColorStop(0, `hsl(${i * 18 + time * 30}, 80%, 60%)`);
              gradient.addColorStop(1, 'rgba(0,0,0,0)');
              
              ctx.fillStyle = gradient;
              ctx.fillRect(universeX - size, universeY - size, size * 2, size * 2);
            }
          } else if (grid.patternType === "timeline") {
            // Ligne temporelle
            const time = elapsedTime * grid.animationSpeed * 0.002;
            ctx.fillStyle = '#001122';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Ligne principale
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(grid.x, grid.y + grid.height / 2);
            ctx.lineTo(grid.x + grid.width, grid.y + grid.height / 2);
            ctx.stroke();
            
            // Événements temporels
            for (let i = 0; i < 10; i++) {
              const eventX = grid.x + (i * grid.width / 10);
              const eventY = grid.y + grid.height / 2;
              const pulse = Math.sin(time * 3 + i) * 10;
              
              ctx.fillStyle = `hsl(${i * 36}, 80%, 60%)`;
              ctx.fillRect(eventX - 3, eventY - 15 + pulse, 6, 30);
            }
          } else if (grid.patternType === "paradox") {
            // Paradoxe temporel avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.003;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Boucle temporelle
            for (let loop = 0; loop < 3; loop++) {
              const phase = (time + loop * 0.66) % 2;
              const alpha = Math.sin(phase * Math.PI) * 0.6;
              
              ctx.fillStyle = `rgba(255, ${100 + loop * 50}, 255, ${alpha})`;
              ctx.fillRect(
                grid.x + phase * grid.width * 0.5,
                grid.y + loop * grid.height / 3,
                grid.width * 0.5,
                grid.height / 3
              );
            }
          } else if (grid.patternType === "infinity") {
            // Symbole infini animé
            const time = elapsedTime * grid.animationSpeed * 0.002;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Courbe infinie
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 4;
            ctx.beginPath();
            
            for (let t = 0; t < Math.PI * 2; t += 0.1) {
              const x = centerX + Math.cos(t + time) * 50 / (1 + Math.sin(t + time)**2);
              const y = centerY + Math.sin(t + time) * Math.cos(t + time) * 50 / (1 + Math.sin(t + time)**2);
              
              if (t === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.stroke();

          } else if (grid.patternType === "pi") {
            // Tunnel de cercles Pi - disparition plus tardive
            const time = elapsedTime * grid.animationSpeed * 0.002;
            const centerX = grid.x + grid.width / 2;
            const centerY = grid.y + grid.height / 2;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Tunnel de cercles concentriques qui disparaissent plus tard
            const maxRadius = Math.min(grid.width, grid.height) / 2;
            for (let i = 0; i < 30; i++) {
              const progress = (i + time * 0.7) % 30 / 30; // Plus lent
              const radius = progress * maxRadius;
              // Disparition plus tardive - les cercles restent visibles plus longtemps
              const opacity = Math.max(0, 1 - Math.pow(progress, 0.5)); // Courbe plus douce
              const tunnelDepth = Math.sin(progress * Math.PI * 3.14159) * 0.5 + 0.5;
              
              if (opacity > 0.05) { // Seuil plus bas pour garder les cercles plus longtemps
                ctx.strokeStyle = `rgba(255, ${100 + tunnelDepth * 155}, ${50 + tunnelDepth * 205}, ${opacity})`;
                ctx.lineWidth = 1 + opacity * 6;
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                ctx.stroke();
                
                // Points Pi sur chaque cercle - plus persistants
                if (radius > 5) {
                  const numPoints = Math.floor(radius * 0.15) + 3;
                  for (let j = 0; j < numPoints; j++) {
                    const angle = (j / numPoints) * Math.PI * 2 + time * 3.14159;
                    const x = centerX + Math.cos(angle) * radius;
                    const y = centerY + Math.sin(angle) * radius;
                    
                    ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.9})`;
                    const pointSize = 1 + opacity * 2;
                    ctx.fillRect(x - pointSize/2, y - pointSize/2, pointSize, pointSize);
                  }
                }
                
                // Particules supplémentaires pour plus de persistance
                for (let k = 0; k < 8; k++) {
                  const particleAngle = (k / 8) * Math.PI * 2 + time * 2;
                  const particleX = centerX + Math.cos(particleAngle) * radius;
                  const particleY = centerY + Math.sin(particleAngle) * radius;
                  const particleAlpha = Math.sin(time * 4 + k) * 0.3 + 0.4;
                  
                  ctx.fillStyle = `rgba(255, 200, 100, ${particleAlpha * opacity})`;
                  ctx.fillRect(particleX - 1, particleY - 1, 2, 2);
                }
              }
            }

          } else if (grid.patternType === "order") {
            // Ordre cristallin avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.001;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Structure ordonnée
            const spacing = 30;
            for (let x = 0; x < grid.width; x += spacing) {
              for (let y = 0; y < grid.height; y += spacing) {
                const intensity = Math.sin(time + x * 0.1 + y * 0.1) * 0.5 + 0.5;
                ctx.fillStyle = `rgba(255, 255, 255, ${intensity})`;
                ctx.fillRect(grid.x + x, grid.y + y, 4, 4);
              }
            }
          } else if (grid.patternType === "entropy") {
            // Entropie croissante
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            // Désordre progressif
            for (let i = 0; i < 100; i++) {
              const randomness = Math.min(1, time * 0.1);
              const x = grid.x + Math.random() * grid.width * randomness + (1 - randomness) * grid.width/2;
              const y = grid.y + Math.random() * grid.height * randomness + (1 - randomness) * grid.height/2;
              
              ctx.fillStyle = `hsl(${Math.random() * 360}, 80%, 60%)`;
              ctx.fillRect(x - 2, y - 2, 4, 4);
            }
          } else if (grid.patternType === "energy") {
            // Énergie pure
            const time = elapsedTime * grid.animationSpeed * 0.004;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Décharges énergétiques
            for (let i = 0; i < 20; i++) {
              const intensity = Math.sin(time * 5 + i) * 0.5 + 0.5;
              const x = grid.x + Math.random() * grid.width;
              const y = grid.y + Math.random() * grid.height;
              
              ctx.fillStyle = `rgba(255, 255, 0, ${intensity})`;
              ctx.fillRect(x - 5, y - 5, 10, 10);
            }
          } else if (grid.patternType === "power") {
            // Puissance électrique
            const time = elapsedTime * grid.animationSpeed * 0.003;
            
            ctx.fillStyle = '#001122';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Arcs électriques
            for (let i = 0; i < 5; i++) {
              if (Math.random() < 0.3) {
                ctx.strokeStyle = `rgba(255, 255, 255, ${0.8 + Math.random() * 0.2})`;
                ctx.lineWidth = 3;
                ctx.shadowColor = '#ffffff';
                ctx.shadowBlur = 10;
                
                ctx.beginPath();
                ctx.moveTo(grid.x + Math.random() * grid.width, grid.y);
                ctx.lineTo(grid.x + Math.random() * grid.width, grid.y + grid.height);
                ctx.stroke();
                ctx.shadowBlur = 0;
              }
            }
          } else if (grid.patternType === "velocity") {
            // Vélocité avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.003;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Lignes de vitesse
            for (let i = 0; i < 15; i++) {
              const y = grid.y + (i * grid.height / 15);
              const speed = Math.sin(time + i) * 0.5 + 0.5;
              const length = speed * grid.width * 0.8;
              
              ctx.strokeStyle = `hsl(${speed * 120}, 80%, 60%)`;
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(grid.x, y);
              ctx.lineTo(grid.x + length, y);
              ctx.stroke();
            }
          } else if (grid.patternType === "acceleration") {
            // Accélération
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Particules accélérées
            for (let i = 0; i < 50; i++) {
              const acceleration = time * time * 0.1;
              const x = grid.x + (i * acceleration) % grid.width;
              const y = grid.y + (i * grid.height / 50);
              
              ctx.fillStyle = `hsl(${acceleration * 10 + i * 7}, 80%, 60%)`;
              ctx.fillRect(x - 2, y - 2, 4, 4);
            }
          } else if (grid.patternType === "momentum") {
            // Momentum
            const time = elapsedTime * grid.animationSpeed * 0.003;
            
            ctx.fillStyle = '#001100';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Objets avec momentum
            for (let i = 0; i < 8; i++) {
              const mass = i + 1;
              const velocity = Math.sin(time + i) * 50;
              const momentum = mass * velocity;
              
              const x = grid.x + ((time * velocity + i * 40) % grid.width);
              const y = grid.y + (i * grid.height / 8);
              
              ctx.fillStyle = `hsl(${Math.abs(momentum) * 2}, 80%, 60%)`;
              ctx.fillRect(x - mass, y - mass, mass * 2, mass * 2);
            }
          } else if (grid.patternType === "force") {
            // Force vectorielle avec fond noir
            const time = elapsedTime * grid.animationSpeed * 0.002;
            
            ctx.fillStyle = '#000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Vecteurs de force
            for (let x = 0; x < grid.width; x += 40) {
              for (let y = 0; y < grid.height; y += 40) {
                const forceX = Math.sin((x + time * 50) * 0.02) * 20;
                const forceY = Math.cos((y + time * 50) * 0.02) * 20;
                const magnitude = Math.sqrt(forceX*forceX + forceY*forceY);
                
                ctx.strokeStyle = `hsl(${magnitude * 5}, 80%, 60%)`;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(grid.x + x, grid.y + y);
                ctx.lineTo(grid.x + x + forceX, grid.y + y + forceY);
                ctx.stroke();
                
                // Pointe de flèche
                const angle = Math.atan2(forceY, forceX);
                ctx.fillStyle = ctx.strokeStyle;
                ctx.save();
                ctx.translate(grid.x + x + forceX, grid.y + y + forceY);
                ctx.rotate(angle);
                ctx.fillRect(-5, -2, 5, 4);
                ctx.restore();
              }
            }
          } else if (grid.patternType === "snakeGame") {
            // 🐍 Snake Game Pattern - Rendu identique à la page principale
            // Initialiser le Snake Game si nécessaire
            if (!grid.snakeGameState) {
              multiSnake.initializeSnakeForGrid(grid.id);
              return; // Attendre la prochaine frame pour le rendu
            }
            
            const state = grid.snakeGameState;
            const cols = Math.floor(grid.width / grid.gridSize);
            const rows = Math.floor(grid.height / grid.gridSize);
            
            // Fond noir pour le jeu
            ctx.fillStyle = '#000000';
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
            
            // Dessiner le serpent et la nourriture s'il y a un état de jeu
            if (state) {
              // Dessiner le serpent
              state.snake.forEach((segment, index) => {
                const x = grid.x + segment.x * grid.gridSize;
                const y = grid.y + segment.y * grid.gridSize;
                
                if (index === 0) {
                  // Tête du serpent - couleur plus vive (identique à la page principale)
                  ctx.fillStyle = '#00ff88';
                  ctx.fillRect(x, y, grid.gridSize - 2, grid.gridSize - 2);
                  
                  // Ajouter des yeux qui suivent la direction
                  ctx.fillStyle = '#000';
                  const eyeSize = Math.max(3, grid.gridSize / 8);
                  const centerX = x + grid.gridSize / 2;
                  const centerY = y + grid.gridSize / 2;
                  const eyeOffset = grid.gridSize / 6;
                  
                  let eye1X, eye1Y, eye2X, eye2Y;
                  
                  switch (state.direction) {
                    case 'UP':
                      eye1X = centerX - eyeOffset; eye1Y = centerY - eyeOffset;
                      eye2X = centerX + eyeOffset; eye2Y = centerY - eyeOffset;
                      break;
                    case 'DOWN':
                      eye1X = centerX - eyeOffset; eye1Y = centerY + eyeOffset;
                      eye2X = centerX + eyeOffset; eye2Y = centerY + eyeOffset;
                      break;
                    case 'LEFT':
                      eye1X = centerX - eyeOffset; eye1Y = centerY - eyeOffset;
                      eye2X = centerX - eyeOffset; eye2Y = centerY + eyeOffset;
                      break;
                    case 'RIGHT':
                    default:
                      eye1X = centerX + eyeOffset; eye1Y = centerY - eyeOffset;
                      eye2X = centerX + eyeOffset; eye2Y = centerY + eyeOffset;
                      break;
                  }
                  
                  // Dessiner les yeux - identique à la page principale
                  ctx.fillRect(eye1X - eyeSize/2, eye1Y - eyeSize/2, eyeSize, eyeSize);
                  ctx.fillRect(eye2X - eyeSize/2, eye2Y - eyeSize/2, eyeSize, eyeSize);
                } else {
                  // Corps du serpent - dégradé vers une couleur plus sombre (identique à la page principale)
                  const intensity = 1 - (index / state.snake.length) * 0.6;
                  const green = Math.floor(255 * intensity);
                  const blue = Math.floor(136 * intensity);
                  ctx.fillStyle = `rgb(0, ${green}, ${blue})`;
                  ctx.fillRect(x + 1, y + 1, grid.gridSize - 4, grid.gridSize - 4);
                }
              });
              
              // Dessiner la nourriture avec effet de pulsation (identique à la page principale)
              if (state.food) {
                const foodX = grid.x + state.food.x * grid.gridSize;
                const foodY = grid.y + state.food.y * grid.gridSize;
                const time = performance.now();
                const pulseIntensity = Math.sin(time * 0.008) * 0.3 + 0.7;
                const foodSize = (grid.gridSize - 4) * pulseIntensity;
                const offset = (grid.gridSize - foodSize) / 2;
                
                ctx.fillStyle = `rgb(255, ${Math.floor(100 * pulseIntensity)}, 0)`;
                ctx.fillRect(foodX + offset, foodY + offset, foodSize, foodSize);
                
                // Ajouter un halo autour de la nourriture
                const gradient = ctx.createRadialGradient(
                  foodX + grid.gridSize/2, foodY + grid.gridSize/2, 0,
                  foodX + grid.gridSize/2, foodY + grid.gridSize/2, grid.gridSize * 1.5
                );
                gradient.addColorStop(0, `rgba(255, 100, 0, ${pulseIntensity * 0.4})`);
                gradient.addColorStop(1, 'rgba(255, 100, 0, 0)');
                ctx.fillStyle = gradient;
                ctx.fillRect(
                  foodX - grid.gridSize/2, 
                  foodY - grid.gridSize/2, 
                  grid.gridSize * 2, 
                  grid.gridSize * 2
                );
              }
              
              // Affichage du score (identique à la page principale)
              if (state.score > 0) {
                ctx.fillStyle = '#ffffff';
                ctx.font = `bold ${Math.max(24, grid.gridSize / 4)}px Arial`;
                ctx.textAlign = 'left';
                ctx.fillText(`Score: ${state.score}`, grid.x + 20, grid.y + 40);
              }
              
              // Afficher les contrôles pour cette grille
              const controlTexts = {
                1: "ZQSD",
                2: "Flèches",
                3: "IKJL", 
                4: "TGFH"
              };
              const controlText = controlTexts[grid.snakeControlSet || 1];
              ctx.fillStyle = "#FFFFFF";
              ctx.font = `${Math.max(10, grid.gridSize / 8)}px Arial`;
              ctx.textAlign = "right";
              ctx.strokeStyle = "#000000";
              ctx.lineWidth = 2;
              ctx.strokeText(
                `P${grid.snakeControlSet}: ${controlText}`,
                grid.x + grid.width - 8,
                grid.y + Math.max(14, grid.gridSize / 5)
              );
              ctx.fillText(
                `P${grid.snakeControlSet}: ${controlText}`,
                grid.x + grid.width - 8,
                grid.y + Math.max(14, grid.gridSize / 5)
              );
              
              // Message de fin de jeu (identique à la page principale)
              if (state.isGameOver) {
                ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
                ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
                
                ctx.fillStyle = '#ffffff';
                ctx.font = `bold ${Math.max(32, grid.gridSize / 3)}px Arial`;
                ctx.textAlign = 'center';
                ctx.fillText('GAME OVER', grid.x + grid.width / 2, grid.y + grid.height / 2);
                ctx.fillText(`Score Final: ${state.score}`, grid.x + grid.width / 2, grid.y + grid.height / 2 + 50);

              }
              
              // Écran de victoire (identique à la page principale)
              if (state.isVictory) {
                ctx.fillStyle = 'rgba(0, 255, 0, 0.8)';
                ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
                
                ctx.fillStyle = '#ffffff';
                ctx.font = `bold ${Math.max(32, grid.gridSize / 3)}px Arial`;
                ctx.textAlign = 'center';
                ctx.fillText('🎉 VICTOIRE! 🎉', grid.x + grid.width / 2, grid.y + grid.height / 2);
                ctx.fillText(`Score Final: ${state.score}`, grid.x + grid.width / 2, grid.y + grid.height / 2 + 50);
                ctx.fillText('Félicitations!', grid.x + grid.width / 2, grid.y + grid.height / 2 + 100);
              }
            }
          } else {
            ctx.fillStyle = grid.backgroundColor || "#000000";
            ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
          }
          ctx.restore();
        } else {
          // Fallback - couleur de fond par défaut
          ctx.fillStyle = grid.backgroundColor || "#000000";
          ctx.fillRect(grid.x, grid.y, grid.width, grid.height);
        }

        // Stocker l'image de la grille AVANT les lignes de grille (pour le sonar avancé)
        let gridBeforeLines: HTMLCanvasElement | null = null;
        if (grid.showSonar && grid.sonarRevealMode && grid.showGrid && (elapsedTime > 0 || isExporting)) {
          try {
            // Créer un canvas temporaire pour stocker l'image avant la grille
            gridBeforeLines = document.createElement('canvas');
            gridBeforeLines.width = grid.width;
            gridBeforeLines.height = grid.height;
            const tempCtx = gridBeforeLines.getContext('2d');
            if (tempCtx) {
              // Copier la région de la grille actuelle (sans les lignes)
              tempCtx.drawImage(canvasRef.current!, grid.x, grid.y, grid.width, grid.height, 0, 0, grid.width, grid.height);
            }
          } catch (e) {
          }
        }

        // Dessiner les lignes de grille et de dalle
        drawGridLines(ctx, grid);

        // Draw sonar - Version classique ou avancée selon le mode
        // Afficher si activé ET (animation en cours OU export vidéo avec temps)
        // N'affiche PAS la première frame de preview statique (elapsedTime=0, pas export)
        // N'affiche PAS lors d'un export PNG statique
        if (grid.showSonar && (elapsedTime > 0 || isExporting) && !isStaticPngExport) {
          const centerX = grid.x + grid.width / 2;
          const centerY = grid.y + grid.height / 2;
          const maxRadius =
            Math.sqrt(Math.pow(grid.width, 2) + Math.pow(grid.height, 2)) / 2;
          const sonarInterval = 4000; // Augmenté de 2000 à 4000 pour ralentir le sonar (2x plus lent)
          // IDENTIQUE à grid/page.tsx pour boucle parfaite
          const sonarTime = (elapsedTime * grid.sonarSpeed) % sonarInterval;
          const sonarProgress = sonarTime / sonarInterval;

          // Calculer les anneaux de sonar
          const sonarRings: Array<{ radius: number; opacity: number; thickness: number }> = [];
          for (let i = 0; i < 3; i++) {
            const ringProgress = (sonarProgress + i * 0.33) % 1;
            if (ringProgress < 0.8) {
              const radius = Math.max(0.1, ringProgress * maxRadius * 1.2);
              const opacity = Math.max(0, 1 - ringProgress * 1.25);
              const minWidth = 1;
              const maxWidth = 8;
              const lineWidth = minWidth + (maxWidth - minWidth) * ringProgress;
              
              if (opacity > 0 && radius > 0) {
                sonarRings.push({ radius, opacity, thickness: lineWidth });
              }
            }
          }

          // Mode Sonar Avancé : Révélation à travers les anneaux
          if (grid.sonarRevealMode && grid.showGrid && gridBeforeLines && sonarRings.length > 0) {
            // Créer un canvas offscreen pour la composition
            const offscreenCanvas = document.createElement('canvas');
            offscreenCanvas.width = grid.width;
            offscreenCanvas.height = grid.height;
            const offscreenCtx = offscreenCanvas.getContext('2d');
            
            if (offscreenCtx) {
              // ÉTAPE 1: Copier l'image avec grille
              offscreenCtx.drawImage(canvasRef.current!, grid.x, grid.y, grid.width, grid.height, 0, 0, grid.width, grid.height);
              
              // ÉTAPE 2: Créer des "trous" aux endroits des anneaux
              offscreenCtx.save();
              offscreenCtx.globalCompositeOperation = 'destination-out';
              sonarRings.forEach(ring => {
                offscreenCtx.beginPath();
                offscreenCtx.arc(grid.width / 2, grid.height / 2, ring.radius, 0, 2 * Math.PI);
                offscreenCtx.lineWidth = ring.thickness;
                offscreenCtx.strokeStyle = `rgba(255, 255, 255, ${ring.opacity})`;
                offscreenCtx.stroke();
              });
              
              // ÉTAPE 3: Peindre l'image du background dans les zones restantes
              offscreenCtx.globalCompositeOperation = 'destination-atop';
              offscreenCtx.drawImage(gridBeforeLines, 0, 0);
              offscreenCtx.restore();
              
              // ÉTAPE 4: Copier le résultat sur le canvas principal
              ctx.drawImage(offscreenCanvas, 0, 0, grid.width, grid.height, grid.x, grid.y, grid.width, grid.height);
            }
          }
          // Mode Sonar Classique : Anneaux blancs simples
          else if (!grid.sonarRevealMode) {
            sonarRings.forEach(ring => {
              ctx.strokeStyle = `rgba(255, 255, 255, ${ring.opacity})`;
              ctx.lineWidth = ring.thickness;
              ctx.beginPath();
              ctx.arc(centerX, centerY, ring.radius, 0, Math.PI * 2);
              ctx.stroke();
            });
          }
        }

        // Dessiner la numérotation des LEDs
        drawLedNumberingForGrid(ctx, grid, grid.enableAnamorphosis);

        // Dessiner le Pixel Map au-dessus de la numérotation des LEDs si activé (Pro only)
        if (grid.pixelMapMode?.enabled) {
          ctx.save();
          ctx.translate(grid.x, grid.y);
          renderPixelMapMode(
            ctx,
            grid.width,
            grid.height,
            grid.pixelMapMode,
            isPro,
            false,
            grid.enableAnamorphosis
          );
          ctx.restore();
        }

        // Dessiner les logos ajoutés à la grille
        if (grid.logoElements && grid.logoElements.length > 0) {
          grid.logoElements.forEach((lg) => {
            // Vérifier que l'image est valide avant de la dessiner
            if (lg.image && lg.image instanceof HTMLImageElement && lg.image.complete && lg.image.naturalWidth > 0) {
              try {
                // Calculer le centre du logo pour l'anamorphose
                const logoCenterX = lg.x + lg.width / 2;
                const logoCenterY = lg.y + lg.height / 2;

                ctx.save();
                ctx.globalAlpha = lg.opacity ?? 1;

                // Appliquer l'anamorphose si activée
                if (grid.enableAnamorphosis) {
                  ctx.translate(logoCenterX, logoCenterY)
                  ctx.scale(1, 0.5)
                  ctx.translate(-logoCenterX, -logoCenterY)
                }

                ctx.drawImage(lg.image, lg.x, lg.y, lg.width, lg.height);

                // Restaurer la transformation avant de dessiner la poignée
                if (grid.enableAnamorphosis) {
                  ctx.setTransform(1, 0, 0, 1, 0, 0);
                }
                ctx.restore();
                
                // Dessiner la poignée de redimensionnement en bas à droite si le logo est sélectionné
                // et qu'on n'est pas en plein écran ou en mode externe
         if (selectedLogoId === lg.id && !isFullscreen && !isExternalDisplay && !isExporting) {
           const handleSize = 20; // Taille de la poignée
                  const handleX = lg.x + lg.width;
                  const handleY = lg.y + lg.height;
                  
                  ctx.save();
                  // Dessiner un carré avec bordure pour la poignée
                  ctx.fillStyle = '#3b82f6'; // Bleu pour correspondre au style de sélection
                  ctx.strokeStyle = '#ffffff';
                  ctx.lineWidth = 2;
                  ctx.fillRect(handleX - handleSize, handleY - handleSize, handleSize, handleSize);
                  ctx.strokeRect(handleX - handleSize, handleY - handleSize, handleSize, handleSize);
                  
                  // Dessiner des lignes diagonales pour indiquer le redimensionnement
                  ctx.strokeStyle = '#ffffff';
                  ctx.lineWidth = 1.5;
                  ctx.beginPath();
                  // Ligne diagonale principale
                  ctx.moveTo(handleX - handleSize + 3, handleY - 3);
                  ctx.lineTo(handleX - 3, handleY - handleSize + 3);
                  ctx.stroke();
                  ctx.restore();
                }
              } catch (error) {
                // console.error('Erreur lors du dessin du logo:', error);
                ctx.restore();
              }
            }
          });
        }

        // Dessiner les textes ajoutés à la grille
        if (grid.textElements && grid.textElements.length > 0) {
          grid.textElements.forEach((t) => {
            const weight = (t.fontWeight ?? 400).toString();
            ctx.font = `${t.fontStyle || 'normal'} ${weight} ${t.fontSize || 24}px ${t.fontFamily || 'Arial, sans-serif'}`;
            ctx.textAlign = (t.textAlign as CanvasTextAlign) || 'left';
            ctx.textBaseline = 'top';
            
            // Mesurer le texte pour la zone de fond
            const textMetrics = ctx.measureText(t.text);
            const textWidth = textMetrics.width;
            const textHeight = t.fontSize || 24;
            const padding = 4;
            
            // Calculer la position du rectangle de fond selon l'alignement
            let bgX = t.x - padding;
            if (t.textAlign === 'center') {
              bgX = t.x - textWidth / 2 - padding;
            } else if (t.textAlign === 'right') {
              bgX = t.x - textWidth - padding;
            }
            const bgY = t.y - padding;
            const bgWidth = textWidth + padding * 2;
            const bgHeight = textHeight + padding * 2;
            
            // Calculer le centre du texte pour l'anamorphose
            const centerX = bgX + bgWidth / 2;
            const centerY = bgY + bgHeight / 2;

            // Appliquer l'anamorphose si activée
            if (grid.enableAnamorphosis) {
              ctx.save()
              ctx.translate(centerX, centerY)
              ctx.scale(1, 0.5)
              ctx.translate(-centerX, -centerY)
            }
            
            // Dessiner la zone de fond semi-transparente
            ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
            ctx.fillRect(bgX, bgY, bgWidth, bgHeight);
            
            // Dessiner le texte
            ctx.fillStyle = t.color || '#ffffff';
            // Ombre pour la lisibilité
            ctx.shadowColor = 'rgba(0,0,0,0.8)';
            ctx.shadowBlur = 4;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;
            const stroke = t.strokeWidth || 0;
            if (stroke > 0) {
              ctx.strokeStyle = t.color || '#ffffff';
              ctx.lineWidth = stroke;
              ctx.strokeText(t.text, t.x, t.y);
            }
            ctx.fillText(t.text, t.x, t.y);
            
            // Réinitialiser l'ombre
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            
            // Restaurer le contexte si anamorphose était activée
            if (grid.enableAnamorphosis) {
              ctx.restore()
            }
            
            // Afficher un indicateur visuel si le texte est sélectionné (masqué en plein écran / écran externe)
            if (!isFullscreen && !isExternalDisplay && !isExporting && selectedGridId === grid.id && selectedTextId === t.id && (!elementDragRef.current || (elementDragRef.current.type === 'text' && elementDragRef.current.id === t.id))) {
              ctx.strokeStyle = "#3b82f6"; // Bleu pour indiquer la sélection
              ctx.lineWidth = 2;
              ctx.setLineDash([5, 5]); // Ligne pointillée
              ctx.strokeRect(bgX, bgY, bgWidth, bgHeight);
              ctx.setLineDash([]); // Réinitialiser
            }
          });
        }

        // Dessiner le logo VisualGrid par défaut au centre seulement s'il n'y a pas de logos utilisateur
        if (grid.showLogo && grid.showDefaultLogo && (!grid.logoElements || grid.logoElements.length === 0)) {
          const baseLogoSize = Math.max(
            16,
            Math.min(grid.width, grid.height) * 0.25,
          ); // Taille minimum de 16px
          const logoSize = baseLogoSize * grid.logoScale; // Appliquer l'échelle du logo
          const logoX = grid.x + (grid.width - logoSize) / 2;
          const logoY = grid.y + (grid.height - logoSize) / 2;
          const logoCenterX = logoX + logoSize / 2;
          const logoCenterY = logoY + logoSize / 2;

          // Appliquer l'anamorphose si activée
          if (grid.enableAnamorphosis) {
            ctx.save()
            ctx.translate(logoCenterX, logoCenterY)
            ctx.scale(1, 0.5)
            ctx.translate(-logoCenterX, -logoCenterY)
          }

          // Dessiner le logo via une image si possible
          const logoImg = document.getElementById(
            "visualgrid-logo",
          ) as HTMLImageElement;
          if (logoImg && logoImg.complete) {
            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
          } else {
            // Fallback sur le logo dessiné si l'image n'est pas prête
            ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
            ctx.beginPath();
            ctx.arc(
              logoX + logoSize / 2,
              logoY + logoSize / 2,
              Math.max(1, logoSize / 2),
              0,
              Math.PI * 2,
            );
            ctx.fill();

            ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
            ctx.beginPath();
            ctx.arc(
              logoX + logoSize / 2,
              logoY + logoSize / 2,
              Math.max(1, logoSize / 2 - 2),
              0,
              Math.PI * 2,
            );
            ctx.fill();

            ctx.strokeStyle = "#3b82f6";
            ctx.lineWidth = Math.max(1, logoSize / 10);
            ctx.beginPath();
            ctx.moveTo(logoX + logoSize * 0.3, logoY + logoSize * 0.3);
            ctx.lineTo(logoX + logoSize / 2, logoY + logoSize * 0.7);
            ctx.lineTo(logoX + logoSize * 0.7, logoY + logoSize * 0.3);
            ctx.stroke();
          }

          // Restaurer le contexte si anamorphose était activée
          if (grid.enableAnamorphosis) {
            ctx.restore()
          }
          }

        // Dessiner le nom de la grille (position fixe, indépendante du logo)
          if (grid.showName) {
            // Adapter la taille de la police en fonction de la taille de la grille
            // Utiliser une échelle plus agressive pour les grandes grilles
            const maxWidth = grid.width * 0.9; // 90% de la largeur de la grille
            const baseFontSize = Math.max(8, Math.min(32, grid.width / 15)); // Échelle plus agressive
            const fontSize = baseFontSize * grid.textScale; // Appliquer l'échelle du texte

            ctx.font = `bold ${fontSize}px Arial`;
            ctx.textAlign = "center";
            ctx.fillStyle = "white";

            // Mesurer la largeur du texte
            const textWidth = ctx.measureText(grid.name).width;

            // Si le texte est trop large, le réduire
            if (textWidth > maxWidth) {
              const scaleFactor = maxWidth / textWidth;
              const adjustedFontSize = Math.max(
                8,
                Math.floor(fontSize * scaleFactor),
              );
              ctx.font = `bold ${adjustedFontSize}px Arial`;
            }

          // Position : utiliser l'offset personnalisé si défini, sinon la position par défaut
          // Les offsets sont relatifs à la position de la grille
          const nameX = grid.nameOffsetX !== undefined 
            ? grid.x + grid.nameOffsetX 
            : grid.x + grid.width / 2; // Position par défaut : centre horizontal
          const nameY = grid.nameOffsetY !== undefined 
            ? grid.y + grid.nameOffsetY 
            : grid.y + grid.height * 0.65; // Position par défaut : 65% de la hauteur depuis le haut

          // Appliquer l'anamorphose si activée
          if (grid.enableAnamorphosis) {
            ctx.save()
            ctx.translate(nameX, nameY)
            ctx.scale(1, 0.5)
            ctx.translate(-nameX, -nameY)
          }

          ctx.fillText(grid.name, nameX, nameY);

          // Restaurer le contexte si anamorphose était activée
          if (grid.enableAnamorphosis) {
            ctx.restore()
          }
          
          // Afficher un indicateur visuel si le nom est sélectionné (masqué en plein écran / écran externe)
          if (!isFullscreen && !isExternalDisplay && !isExporting && selectedGridId === grid.id && (!elementDragRef.current || (elementDragRef.current.type === 'name' && elementDragRef.current.gridId === grid.id))) {
            // Recalculer les dimensions pour l'indicateur
            let finalTextWidth = ctx.measureText(grid.name).width;
            let finalFontSize = fontSize;
            
            if (finalTextWidth > maxWidth) {
              const scaleFactor = maxWidth / finalTextWidth;
              finalFontSize = Math.max(8, Math.floor(fontSize * scaleFactor));
              ctx.font = `bold ${finalFontSize}px Arial`;
              finalTextWidth = ctx.measureText(grid.name).width;
            }
            
            const textHeight = finalFontSize * 1.2;
            const padding = 4;
            
            ctx.strokeStyle = "#3b82f6"; // Bleu pour indiquer la sélection
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]); // Ligne pointillée
            ctx.strokeRect(
              nameX - finalTextWidth / 2 - padding,
              nameY - textHeight / 2 - padding,
              finalTextWidth + padding * 2,
              textHeight + padding * 2
            );
            ctx.setLineDash([]); // Réinitialiser
          }
        }

        // Afficher les dimensions du panneau si activé (en bas à gauche)
        if (grid.showDimensions) {
          const dimensionsText = `${grid.width} × ${grid.height} px`;
          const fontSize = grid.dimensionsFontSize || Math.max(14, Math.min(20, grid.width / 25));

          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          ctx.lineWidth = Math.max(1, fontSize / 12);
          ctx.textAlign = "left";
          ctx.textBaseline = "bottom";

          const textX = grid.x + 10;
          const textY = grid.y + grid.height - 10;

          // Calculer le centre du texte pour l'anamorphose
          const textMetrics = ctx.measureText(dimensionsText);
          const textWidth = textMetrics.width;
          const textHeight = fontSize;
          const centerX = textX + textWidth / 2;
          const centerY = textY - textHeight / 2;

          // Appliquer l'anamorphose si activée
          if (grid.enableAnamorphosis) {
            ctx.save()
            ctx.translate(centerX, centerY)
            ctx.scale(1, 0.5)
            ctx.translate(-centerX, -centerY)
          }

          // Dessiner le texte avec un contour pour une meilleure visibilité
          ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
          ctx.strokeStyle = "rgba(0, 0, 0, 0.8)";
          ctx.lineWidth = 2;

          ctx.strokeText(dimensionsText, textX, textY);
          ctx.fillText(dimensionsText, textX, textY);

          // Restaurer le contexte si anamorphose était activée
          if (grid.enableAnamorphosis) {
            ctx.restore()
          }
        }

        ctx.restore();

        // Dessiner un contour si la grille est sélectionnée (masqué en plein écran / écran externe)
        if (!isFullscreen && !isExternalDisplay && !isExporting && grid.id === selectedGridId) {
          ctx.strokeStyle = "#3b82f6"; // Bleu pour la grille sélectionnée
          ctx.lineWidth = 2;
          ctx.strokeRect(
            grid.x - 2,
            grid.y - 2,
            grid.width + 4,
            grid.height + 4,
          );
        }
      });

      ctx.restore();

      // Logo VisualGrid par défaut désactivé en mode External Display
      // (l'utilisateur ne veut pas voir le logo par défaut sur l'écran externe)
    },
    [
      canvasConfig,
      grids,
      selectedGridId,
      selectedLogoId,
      selectedTextId,
      isAnimating,
      generateGradientColors,
      isDragging,
      disablePreviewWhenExternal,
      isExternalDisplayConnected,
      isExternalDisplay,
      isFullscreen,
      defaultLogoRef,
    ],
  );

  // Synchroniser la ref après la déclaration de renderCanvas
  useEffect(() => {
    renderCanvasRef.current = renderCanvas;
  }, [renderCanvas]);

  // Fonction pour synchroniser tous les états vers l'affichage externe (MultiGrid)
  const syncAllToExternal = useCallback(() => {
    if (!broadcastChannelRef.current) {
      return;
    }

    // Synchroniser la configuration du canvas
    broadcastChannelRef.current.postMessage({
      type: 'MULTIGRID_CANVAS_CONFIG_UPDATE',
      data: canvasConfig
    });

    // Synchroniser les grilles (sérialisées pour éviter les HTMLImageElement)
    broadcastChannelRef.current.postMessage({
      type: 'MULTIGRID_GRIDS_UPDATE',
      data: serializeGrids(grids)
    });

    // Synchroniser l'état d'animation
    broadcastChannelRef.current.postMessage({
      type: 'ANIMATION_STATE_UPDATE',
      data: { isAnimating }
    });

    // Synchroniser le zoom
    broadcastChannelRef.current.postMessage({
      type: 'MULTIGRID_ZOOM_UPDATE',
      data: { zoom }
    });

    // Synchroniser le paramètre de désactivation de l'aperçu
    broadcastChannelRef.current.postMessage({
      type: 'PREVIEW_DISABLE_UPDATE',
      data: { disablePreviewWhenExternal }
    });
  }, [canvasConfig, grids, isAnimating, zoom, disablePreviewWhenExternal]);

  // Initialiser BroadcastChannel et gérer la synchronisation
  useEffect(() => {
    if (!mounted) return;

    broadcastChannelRef.current = new BroadcastChannel('visualgrid-sync');
    const isExternal = isExternalDisplayRef.current;

    const handleSync = (event: MessageEvent) => {
      const { type, data } = event.data;

      switch (type) {
        case 'EXTERNAL_DISPLAY_READY':
          if (!isExternal && externalDisplayWindow) {
            setIsExternalDisplayConnected(true);
            const syncDelay = isElectronApp ? 2000 : 500;
            setTimeout(() => {
              syncAllToExternal();
              if (isElectronApp) {
                setTimeout(() => {
                  syncAllToExternal();
                }, 2000);
              }
            }, syncDelay);
          }
          break;
        case 'REQUEST_INITIAL_SYNC':
          syncAllToExternal();
          if (data.isElectron) {
            setTimeout(() => {
              syncAllToExternal();
            }, 1000);
          }
          break;
        case 'EXTERNAL_DISPLAY_STATE':
          setIsExternalDisplayFullscreen(data.isFullscreen);
          break;
        case 'MULTIGRID_CANVAS_CONFIG_UPDATE':
          if (isExternal) {
            setCanvasConfig(data);
          }
          break;
        case 'MULTIGRID_GRIDS_UPDATE':
          if (isExternal) {
            // Reconstruire les images depuis les dataUrl avant de définir les grids
            reconstructGrids(data).then(reconstructedGrids => {
              setGrids(reconstructedGrids);
            }).catch(error => {
              // console.error('Erreur lors de la reconstruction des grids:', error);
            });
          }
          break;
        case 'ANIMATION_STATE_UPDATE':
          if (isExternal) {
            setIsAnimating(data.isAnimating);
          }
          break;
        case 'MULTIGRID_ZOOM_UPDATE':
          if (isExternal) {
            setZoom(data.zoom);
          }
          break;
        case 'PREVIEW_DISABLE_UPDATE':
          // Recevoir le paramètre de désactivation de l'aperçu
          if (isExternal) {
            setDisablePreviewWhenExternal(data.disablePreviewWhenExternal);
          }
          break;
        case 'EXTERNAL_DISPLAY_CLOSING':
          setExternalDisplayWindow(null);
          setIsExternalDisplayConnected(false);
          setIsExternalDisplayFullscreen(false);
          break;
        case 'MAIN_PAGE_CLOSING':
          if (isExternal) {
            window.close();
          }
          break;
      }
    };

    broadcastChannelRef.current.addEventListener('message', handleSync);

    // Vérifier périodiquement si la fenêtre externe est fermée
    const checkExternalWindow = setInterval(async () => {
      if (externalDisplayWindow) {
        if ('closed' in externalDisplayWindow && externalDisplayWindow.closed) {
          setExternalDisplayWindow(null);
          setIsExternalDisplayConnected(false);
          setIsExternalDisplayFullscreen(false);
        } else if ('isElectron' in externalDisplayWindow && externalDisplayWindow.isElectron && (window as any).electronAPI) {
          try {
            const state = await (window as any).electronAPI.getExternalDisplayState(externalDisplayWindow.id);
            if (!state.exists) {
              setExternalDisplayWindow(null);
              setIsExternalDisplayConnected(false);
              setIsExternalDisplayFullscreen(false);
            }
          } catch (error) {
            setExternalDisplayWindow(null);
            setIsExternalDisplayConnected(false);
            setIsExternalDisplayFullscreen(false);
          }
        }
      }
    }, 1000);

    const handleBeforeUnload = () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'MAIN_PAGE_CLOSING',
          data: { timestamp: Date.now() }
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.removeEventListener('message', handleSync);
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
      clearInterval(checkExternalWindow);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [mounted, externalDisplayWindow, isElectronApp, syncAllToExternal]);

  // Auto-synchronisation des changements
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected && !isExternalDisplay) {
      syncAllToExternal();
    }
  }, [canvasConfig, grids, isAnimating, zoom, disablePreviewWhenExternal, isExternalDisplayConnected, isExternalDisplay, syncAllToExternal]);

  // Synchroniser disablePreviewWhenExternal
  useEffect(() => {
    if (broadcastChannelRef.current && isExternalDisplayConnected && !isExternalDisplay) {
      broadcastChannelRef.current.postMessage({
        type: 'PREVIEW_DISABLE_UPDATE',
        data: { disablePreviewWhenExternal }
      });
    }
  }, [disablePreviewWhenExternal, isExternalDisplayConnected, isExternalDisplay]);

  // Fonction pour ouvrir l'affichage externe
  const openExternalDisplay = useCallback(async () => {
    try {
      const selectedScreen = availableScreens[selectedScreenIndex] || availableScreens[0];

      if (!selectedScreen) {
        toast.error(t("external.noScreen"));
        return;
      }

      let windowRef = null;

      // Méthode 1: Tauri natif
      if (isTauriApp && openTauriExternalDisplay) {
        try {
          const tauriScreens = availableScreens.filter(s => s.source === 'tauri');
          const hasMultipleDisplays = tauriScreens.length > 1 || availableScreens.length > 1;
          
          if (!hasMultipleDisplays) {
            toast.error(t("external.singleDisplay"));
            return;
          }

          let displayId = selectedScreen.id;
          if (!displayId || !displayId.startsWith('display_')) {
            const tauriScreen = availableScreens.find(s => s.source === 'tauri');
            if (tauriScreen) {
              displayId = tauriScreen.id;
            } else if (availableScreens.length > 0) {
              displayId = availableScreens[0].id;
            } else {
              displayId = 'display_0';
            }
          }

          const result = await openTauriExternalDisplay(
            displayId,
            '/multigrid/?forceExternal=true',
            true
          );

          if (result.success && result.windowId) {
            windowRef = {
              id: result.windowId,
              isTauri: true,
              close: async () => {
                if (closeTauriExternalDisplay) {
                  await closeTauriExternalDisplay(result.windowId);
                }
              }
            };

            toast.success(t("external.electronSuccess")
              .replace("{screenName}", selectedScreen.name)
              .replace("{width}", selectedScreen.width.toString())
              .replace("{height}", selectedScreen.height.toString()));
          } else if (result.error) {
            if (result.error.includes("Only one display detected") || result.error.includes("multiple displays")) {
              toast.error(t("external.singleDisplay"));
            } else if (!isElectronApp) {
              toast.error(result.error || t("external.popupBlocked"));
            }
          }
        } catch (tauriError) {
          // console.error('Erreur lors de l\'ouverture Tauri:', tauriError);
          if (!isElectronApp) {
            const errorMessage = tauriError instanceof Error ? tauriError.message : String(tauriError);
            toast.error(errorMessage || t("external.popupBlocked"));
          }
        }
      }

      // Méthode 2: Electron natif
      if (!windowRef && isElectronApp && selectedScreen.source === 'electron' && (window as any).electronAPI) {
        try {
          const electronWindowId = await (window as any).electronAPI.openExternalDisplay({
            displayId: selectedScreen.id,
            url: '/multigrid/?forceExternal=true',
            options: {
              width: selectedScreen.width,
              height: selectedScreen.height,
              x: selectedScreen.left,
              y: selectedScreen.top,
              fullscreen: true,
              frame: false,
              show: true,
              webPreferences: {
                nodeIntegration: true,
                contextIsolation: false
              }
            }
          });

          if (electronWindowId) {
            windowRef = {
              id: electronWindowId,
              isElectron: true,
              close: () => (window as any).electronAPI.closeExternalDisplay(electronWindowId)
            };

            toast.success(t("external.electronSuccess")
              .replace("{screenName}", selectedScreen.name)
              .replace("{width}", selectedScreen.width.toString())
              .replace("{height}", selectedScreen.height.toString()));
          }
        } catch (electronError) {
        }
      }

      // Méthode 3: Fallback Web
      if (!windowRef) {
        const windowFeatures = [
          `width=${selectedScreen.width}`,
          `height=${selectedScreen.height}`,
          `left=${selectedScreen.left}`,
          `top=${selectedScreen.top}`,
          'menubar=no',
          'toolbar=no',
          'location=no',
          'status=no',
          'scrollbars=no',
          'resizable=yes'
        ].join(',');

        const webWindow = window.open('/multigrid/?forceExternal=true', 'visualGridExternalDisplay', windowFeatures);

        if (webWindow) {
          windowRef = webWindow;
          toast.success(t("toast.externalDisplayWeb", { name: selectedScreen.name, width: selectedScreen.width, height: selectedScreen.height }));
        }
      }

      if (windowRef) {
        setExternalDisplayWindow(windowRef);
        setTimeout(() => {
          syncAllToExternal();
        }, (windowRef && 'isElectron' in windowRef && windowRef.isElectron) ? 1500 : 2000);
      } else {
        toast.error(t("external.popupBlocked"));
      }

    } catch (error) {
      toast.error(t("external.generalError"));
    }
  }, [syncAllToExternal, availableScreens, selectedScreenIndex, isElectronApp, isTauriApp, openTauriExternalDisplay, closeTauriExternalDisplay, t]);

  // Fonction pour fermer l'affichage externe
  const closeExternalDisplay = useCallback(async () => {
    if (externalDisplayWindow) {
      if ('isTauri' in externalDisplayWindow && externalDisplayWindow.isTauri && closeTauriExternalDisplay) {
        try {
          await closeTauriExternalDisplay(externalDisplayWindow.id);
        } catch (error) {
          // console.error('Erreur lors de la fermeture Tauri:', error);
        }
      } else if ('isElectron' in externalDisplayWindow && externalDisplayWindow.isElectron && externalDisplayWindow.close) {
        externalDisplayWindow.close();
      } else if (typeof externalDisplayWindow.close === 'function') {
        externalDisplayWindow.close();
      } else if (externalDisplayWindow.close) {
        externalDisplayWindow.close();
      }
      
      setExternalDisplayWindow(null);
      setIsExternalDisplayConnected(false);
      setIsExternalDisplayFullscreen(false);
    }
  }, [externalDisplayWindow, closeTauriExternalDisplay]);

  // Hook pour l'export vidéo WebCodecs GPU-native
  // Utilise renderCanvas pour capturer exactement ce que l'utilisateur voit
  const {
    isExporting: isWebCodecsExporting,
    progress: webCodecsProgress,
    currentFrame: webCodecsCurrentFrame,
    totalFrames: webCodecsTotalFrames,
    error: webCodecsError,
    exportSpeed: webCodecsExportSpeed,
    estimatedTimeRemaining: webCodecsTimeRemaining,
    isSupported: isWebCodecsSupported,
    statusMessage: webCodecsStatusMessage,
    phase: webCodecsPhase,
    selectedFormat: webCodecsSelectedFormat,
    startExport: startWebCodecsExport,
    cancelExport: cancelWebCodecsExport,
    closeExport: closeWebCodecsExport,
  } = useWebCodecsVideoExport(
    canvasRef,
    (time, showSelection) => {
      // Pour l'export vidéo, on utilise renderCanvas avec le temps d'export spécifié
      // Cela permettra de capturer les animations et les sonars correctement
      renderCanvasRef.current({ forExport: true, exportTime: time });
    }
  );

  // Fonction pour déplacer la grille sélectionnée avec les touches de direction
  const moveSelectedGrid = useCallback(
    (direction: "up" | "down" | "left" | "right", amount: number) => {
      if (!selectedGridId) return;

      setGrids((prev) => {
        return prev.map((grid) => {
          if (grid.id !== selectedGridId) return grid;

          let newX = grid.x;
          let newY = grid.y;

          switch (direction) {
            case "up":
              newY -= amount;
              break;
            case "down":
              newY += amount;
              break;
            case "left":
              newX -= amount;
              break;
            case "right":
              newX += amount;
              break;
          }

          // Limiter aux bords du canvas
          newX = Math.max(0, Math.min(canvasConfig.width - grid.width, newX));
          newY = Math.max(0, Math.min(canvasConfig.height - grid.height, newY));

          return { ...grid, x: newX, y: newY };
        });
      });

      // Redessiner le canvas après le déplacement
      requestAnimationFrame(() => renderCanvas());
    },
    [selectedGridId, canvasConfig.width, canvasConfig.height, renderCanvas],
  );

  // Gestionnaire d'événements clavier pour déplacer la grille avec les flèches
  useEffect(() => {
    if (isFullscreen) return; // Désactiver les raccourcis clavier en mode plein écran

    // Désactiver le mouvement de grille si des jeux Snake sont actifs (conflit avec les contrôles Snake)
    const hasActiveSnakeGames = grids.some(grid => 
      grid.patternType === 'snakeGame' && grid.snakeGameState?.isPlaying
    );
    if (hasActiveSnakeGames) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedGridId) return;

      // Déterminer la distance de déplacement
      let moveAmount = 1; // Déplacement par défaut
      if (e.shiftKey) moveAmount = 10; // Shift = déplacement de 10px
      if (e.ctrlKey) moveAmount = 5; // Ctrl = déplacement de 5px

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          moveSelectedGrid("up", moveAmount);
          break;
        case "ArrowDown":
          e.preventDefault();
          moveSelectedGrid("down", moveAmount);
          break;
        case "ArrowLeft":
          e.preventDefault();
          moveSelectedGrid("left", moveAmount);
          break;
        case "ArrowRight":
          e.preventDefault();
          moveSelectedGrid("right", moveAmount);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedGridId, moveSelectedGrid, isFullscreen, grids]);

  // Gestionnaire d'événements clavier pour copier/coller/supprimer (CTRL+C / CTRL+V / SUPPR)
  useEffect(() => {
    if (isFullscreen) return; // Désactiver en mode plein écran

    const handleCopyPasteKeyDown = (e: KeyboardEvent) => {
      // Ignorer si on est dans un champ de texte
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      // CTRL+C : Copier la grille sélectionnée
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedGridId) {
        e.preventDefault();
        copyGrid(selectedGridId);
      }

      // CTRL+V : Coller la grille copiée
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && copiedGrid) {
        e.preventDefault();
        pasteGrid();
      }

      // DELETE ou SUPPR : Supprimer la grille sélectionnée
      if ((e.key === 'Delete' || e.key === 'Suppr') && selectedGridId) {
        e.preventDefault();
        deleteGrid(selectedGridId);
      }
    };

    window.addEventListener("keydown", handleCopyPasteKeyDown);
    return () => window.removeEventListener("keydown", handleCopyPasteKeyDown);
  }, [selectedGridId, copiedGrid, isFullscreen, copyGrid, pasteGrid, deleteGrid]);

  // Gestionnaire d'événements clavier pour Snake Game multijoueur
  useEffect(() => {
    const handleSnakeKeyDown = (e: KeyboardEvent) => {
      const snakeGrids = grids.filter(grid => grid.patternType === 'snakeGame' && grid.snakeGameState);
      
      if (snakeGrids.length === 0) return;

      // Éviter les conflits quand l'utilisateur tape dans un champ
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      // Empêcher l'activation des contrôles Snake en mode plein écran
      if (isFullscreen) return;
      
      snakeGrids.forEach(grid => {
        const controlSet = grid.snakeControlSet;
        const state = grid.snakeGameState;
        
        if (!state) return;


        
        if (!state.isPlaying) return;

        // Contrôles pendant le jeu
        switch (controlSet) {
          case 1: // Grille 1: ZQSD
            switch (e.key.toLowerCase()) {
              case 'z':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'UP');
                break;
              case 'q':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'LEFT');
                break;
              case 's':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'DOWN');
                break;
              case 'd':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'RIGHT');
                break;
            }
            break;
            
          case 2: // Grille 2: Flèches directionnelles
            switch (e.key) {
              case 'ArrowUp':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'UP');
                break;
              case 'ArrowLeft':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'LEFT');
                break;
              case 'ArrowDown':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'DOWN');
                break;
              case 'ArrowRight':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'RIGHT');
                break;
            }
            break;
            
          case 3: // Grille 3: IKJL
            switch (e.key.toLowerCase()) {
              case 'i':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'UP');
                break;
              case 'j':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'LEFT');
                break;
              case 'k':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'DOWN');
                break;
              case 'l':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'RIGHT');
                break;
            }
            break;
            
          case 4: // Grille 4: TGFH
            switch (e.key.toLowerCase()) {
              case 't':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'UP');
                break;
              case 'f':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'LEFT');
                break;
              case 'g':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'DOWN');
                break;
              case 'h':
                e.preventDefault();
                multiSnake.changeSnakeDirection(grid.id, 'RIGHT');
                break;
            }
            break;
        }
      });
    };

    window.addEventListener("keydown", handleSnakeKeyDown);
    return () => window.removeEventListener("keydown", handleSnakeKeyDown);
  }, [grids, multiSnake, isFullscreen]);

  // Gestionnaire d'événements pour la touche Espace (contrôle global des jeux Snake)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isFullscreen) {
        // Vérifier qu'on n'est pas dans un input/textarea
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        
        e.preventDefault();
        resetAndStartAllSnakeGames();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [resetAndStartAllSnakeGames, isFullscreen]);

  // Raccourcis clavier pour les outils (V = flèche, H = main, Espace maintenu = main temporaire)
  useEffect(() => {
    if (isFullscreen || isExternalDisplay) return;

    const handleToolKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'v' || e.key === 'V') {
        setActiveTool('arrow');
      } else if (e.key === 'h' || e.key === 'H') {
        setActiveTool('hand');
      } else if (e.key === ' ' && activeTool === 'arrow') {
        e.preventDefault();
        setActiveTool('hand');
      }
    };

    const handleToolKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        setActiveTool('arrow');
      }
    };

    window.addEventListener('keydown', handleToolKeyDown);
    window.addEventListener('keyup', handleToolKeyUp);
    return () => {
      window.removeEventListener('keydown', handleToolKeyDown);
      window.removeEventListener('keyup', handleToolKeyUp);
    };
  }, [isFullscreen, isExternalDisplay, activeTool]);

  // Auto-démarrage de l'animation quand des grilles Snake sont présentes
  useEffect(() => {
    const hasSnakeGrids = grids.some(grid => grid.patternType === 'snakeGame');
    if (hasSnakeGrids && !isAnimating) {
      setIsAnimating(true);
    }
  }, [grids, isAnimating]);

  // Effet pour l'animation
  const animateLoop = useCallback(() => {
    renderCanvasRef.current();
    animationFrameRef.current = requestAnimationFrame(animateLoop);
  }, []);

  useEffect(() => {
    if (isAnimating) {
      // Ne pas réinitialiser startTimeRef sur changement de config; seulement au démarrage
      if (!animationFrameRef.current) {
        startTimeRef.current = performance.now();
      }
      animationFrameRef.current = requestAnimationFrame(animateLoop);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      renderCanvas(); // Redessine une dernière fois à l'arrêt
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = null;
    };
  }, [isAnimating, animateLoop]);

  // Effet pour le rendu initial et lors des changements
  // Ce useEffect se déclenche quand renderCanvas change (ce qui arrive quand grids, canvasConfig, etc. changent)
  // ou quand le canvas est monté
  useEffect(() => {
    // Vérifier que le canvas est monté avant de le rendre
    if (canvasMounted && canvasRef.current) {
    renderCanvas();
    }
  }, [renderCanvas, canvasMounted]);
  
  // Effet spécifique pour forcer le rendu après le chargement de la config par défaut
  // Se déclenche quand configLoadTrigger est incrémenté ET que le canvas est monté
  useEffect(() => {
    if (configLoadTrigger > 0 && canvasMounted && canvasRef.current && grids.length > 0) {
      // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
      requestAnimationFrame(() => {
        if (canvasRef.current) {
          renderCanvas();
        }
      });
    }
  }, [configLoadTrigger, canvasMounted, grids.length, renderCanvas]);

  // Ajouter après cet effet:
  // Effet pour redessiner le canvas après un changement de zoom
  useEffect(() => {
    renderCanvas();
  }, [zoom, renderCanvas]);

  // Fonction pour ouvrir le dialogue de choix d'export PNG
  const handleExportCanvas = () => {
    if (!canvasRef.current) return;
    setShowExportPngDialog(true);
  };

  // Fonction pour exporter le canvas entier uniquement
  const handleExportFullCanvas = async () => {
    if (!canvasRef.current) return;
    
    setShowExportPngDialog(false);

    try {
      // Désélectionner tous les éléments pour un export clean
      setSelectedGridId(null);
      setSelectedTextId(null);
      setSelectedLogoId(null);

      toast.info(t("toast.exportingCanvas") || "Exportation du canvas entier en cours...");

      // Capturer le temps actuel de l'animation pour avoir les mêmes couleurs que la preview
      const exportTime = isAnimating ? performance.now() - startTimeRef.current : 0;

      // Redessiner sans indicateurs de sélection pour l'export
      renderCanvas({ forExport: true });
      
      const canvasDataUrl = canvasRef.current.toDataURL("image/png");
      
      // Nouvelle nomenclature: {Résolution}_VisualGrid_{JJ-MM-YYYY}_{HH-MM-SS}.png
      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
      const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      const canvasFilename = `${canvasConfig.width}x${canvasConfig.height}_VisualGrid_${dateStr}_${timeStr}.png`;
      
      if (isDesktopApp) {
        // Utiliser l'API Electron/Tauri pour sauvegarder le canvas entier
        const canvasResult = await exportCanvas(canvasDataUrl, canvasFilename);
        
        if (!canvasResult.success) {
          toast.error(t("multigrid.exportError") || "Erreur lors de l'exportation du canvas: " + (canvasResult.error || "Erreur inconnue"));
          return;
        }
        toast.success(t("toast.canvasExported") || "Canvas exporté avec succès");
        
        // ✅ PostHog: Track PNG export (full canvas)
        captureEvent('multigrid:export_png_full', {
          resolution: `${canvasConfig.width}x${canvasConfig.height}`,
          grids_count: grids.length,
          is_animating: isAnimating,
        });
      } else {
        // Fallback pour navigateur web - canvas entier
      const link = document.createElement("a");
        link.download = canvasFilename;
        link.href = canvasDataUrl;
      link.click();
        toast.success(t("toast.canvasExported") || "Canvas exporté avec succès");
      }
      
      // Incrémenter le compteur lors de l'exportation
      try {
        await fetch(getApiUrl('/api/grid-counters/increment'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode: 'multiGridMode' }),
        });
      } catch (error) {
      }
    } catch (error) {
      // console.error("Erreur lors de l'exportation du canvas:", error);
      toast.error(t("multigrid.exportError") || "Erreur lors de l'exportation");
    }
  };

  // Fonction pour exporter toutes les grilles individuellement
  const handleExportAllGrids = async () => {
    if (!canvasRef.current) return;
    
    setShowExportPngDialog(false);
    
    if (grids.length === 0) {
      toast.error(t("multigrid.noGrids") || "Aucune grille à exporter");
      return;
    }

    try {
      // Désélectionner tous les éléments pour un export clean
      setSelectedGridId(null);
      setSelectedTextId(null);
      setSelectedLogoId(null);
      
      toast.info(t("toast.exportingMultiple", { count: grids.length }) || `Exportation en cours : ${grids.length} grilles individuelles...`);

      // Capturer le temps actuel de l'animation pour avoir les mêmes couleurs que la preview
      const exportTime = isAnimating ? performance.now() - startTimeRef.current : 0;

      // Redessiner le canvas principal une seule fois pour capturer l'état actuel
      renderCanvas({ forExport: true });

      // Exporter toutes les grilles individuellement
      let successfulGridExports = 0;
      let failedGridExports = 0;
      
      if (grids.length > 0) {
        for (let i = 0; i < grids.length; i++) {
          const grid = grids[i];
          
          try {
            // Créer un canvas temporaire pour cette grille
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = grid.width;
            tempCanvas.height = grid.height;
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) {
              failedGridExports++;
              continue;
            }

            // Copier directement la région de la grille depuis le canvas principal
            // Cela garantit que tous les motifs, couleurs et animations sont respectés
            tempCtx.save();
            tempCtx.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
            
            // Copier la région de cette grille depuis le canvas principal
            // Cela inclut déjà tout : motif, grille, numéros LED, logos, textes, etc.
            if (canvasRef.current) {
              tempCtx.drawImage(
                canvasRef.current,
                grid.x, grid.y, grid.width, grid.height, // Source (position dans le canvas principal)
                0, 0, grid.width, grid.height // Destination (canvas temporaire)
              );
            }

            // Plus besoin de redessiner quoi que ce soit car tout est déjà dans l'image copiée
            tempCtx.restore();

            // Exporter la grille
            const gridDataUrl = tempCanvas.toDataURL("image/png");
            
            // Nouvelle nomenclature: {Nom}_{Résolution}_{Res panneau}_VisualGrid_{JJ-MM-YYYY}_{HH-MM-SS}.png
            const now = new Date();
            const dateStr = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
            const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
            const gridName = grid.name.replace(/[^a-z0-9]/gi, '_');
            const panelW = grid.dalleConfigMode === 'custom' ? grid.dalleWidth : grid.gridSize;
            const panelH = grid.dalleConfigMode === 'custom' ? grid.dalleHeight : grid.gridSize;
            const gridFilename = `${gridName}_${grid.width}x${grid.height}_${panelW}x${panelH}_VisualGrid_${dateStr}_${timeStr}.png`;
            
            if (isDesktopApp) {
              // Utiliser l'API Electron/Tauri pour sauvegarder le fichier
              const result = await exportCanvas(gridDataUrl, gridFilename);
              if (result.success) {
                successfulGridExports++;
              } else {
                failedGridExports++;
              }
            } else {
              // Fallback pour navigateur web
              const gridLink = document.createElement("a");
              gridLink.download = gridFilename;
              gridLink.href = gridDataUrl;
              gridLink.click();
              // Petite pause entre les téléchargements pour éviter de surcharger le navigateur
              await new Promise(resolve => setTimeout(resolve, 100));
              successfulGridExports++;
            }
          } catch (error) {
            failedGridExports++;
          }
        }
        
      }
      
      // Redessiner avec indicateurs de sélection
      renderCanvas();
      
      // Message de succès
      if (failedGridExports === 0) {
        toast.success(t("toast.exportSuccess", { count: successfulGridExports, grids: successfulGridExports }).replace("{count}", successfulGridExports.toString()).replace("{grids}", successfulGridExports.toString()) || `${successfulGridExports} grille(s) exportée(s) avec succès`);

        // ✅ PostHog: Track PNG export (individual grids)
        captureEvent('multigrid:export_png_individual', {
          resolution: `${canvasConfig.width}x${canvasConfig.height}`,
          grids_count: grids.length,
          successful_exports: successfulGridExports,
          failed_exports: failedGridExports,
          is_animating: isAnimating,
        });
      } else {
        toast.warning(t("toast.exportPartial", { success: successfulGridExports, failed: failedGridExports }).replace("{success}", successfulGridExports.toString()).replace("{failed}", failedGridExports.toString()) || `Export partiel : ${successfulGridExports} fichiers exportés avec succès, ${failedGridExports} échecs.`);
        
        // ✅ PostHog: Track PNG export (individual grids - partial)
        captureEvent('multigrid:export_png_individual', {
          resolution: `${canvasConfig.width}x${canvasConfig.height}`,
          grids_count: grids.length,
          successful_exports: successfulGridExports,
          failed_exports: failedGridExports,
          is_animating: isAnimating,
          partial_success: true,
        });
      }
      
      // Incrémenter le compteur lors de l'exportation
      try {
        await fetch(getApiUrl('/api/grid-counters/increment'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode: 'multiGridMode' }),
        });
      } catch (error) {
      }
    } catch (error) {
      // console.error("Erreur lors de l'exportation:", error);
      toast.error(t("multigrid.exportError") || "Erreur lors de l'exportation");
    }
  };

  // Fonction pour ouvrir le dialogue d'export vidéo
  const handleExportLoopVideo = useCallback(() => {
    setShowExportDialog(true);
  }, []);

  // Handler pour l'export WebCodecs avec settings personnalisés (depuis le dialogue)
  const handleWebCodecsExportWithSettings = useCallback(async (options: {
    fps: number;
    duration: number;
    outputFormat: 'MP4' | 'MOV' | 'ProRes' | 'WebM';
    exportMode?: 'full-canvas' | 'individual-grids';
  }) => {
    // Désélectionner tous les éléments pour un export clean
    setSelectedGridId(null);
    setSelectedTextId(null);
    setSelectedLogoId(null);

    if (!isWebCodecsSupported) {
      toast.error(t("export.webCodecsNotSupported") || "WebCodecs non supporté", {
        description: t("export.webCodecsNotSupportedDesc") || "Votre navigateur ne supporte pas WebCodecs."
      });
      return;
    }

    const exportMode = options.exportMode || 'full-canvas';

    // Mode Canvas Complet : Export du canvas entier
    if (exportMode === 'full-canvas') {
      // Pour les grilles animées (gradient, etc.), ajuster la vitesse pour un loop parfait
      const hasAnimatedGrids = grids.some(grid =>
        grid.patternType === 'gradient' || grid.patternType === 'gradient4'
      );

      if (hasAnimatedGrids) {
        // Appliquer les vitesses optimales à TOUTES les grilles pour un loop parfait
        const durationInSeconds = options.duration / 1000;
        const durationInMs = options.duration;

        setGrids(prev => prev.map(grid => {
          const patternTypeLower = grid.patternType?.toLowerCase();
          const isGradientPattern =
            patternTypeLower === 'gradient' ||
            patternTypeLower === 'gradient4';

          if (isGradientPattern) {
            const loopDuration = grid.gradientLoopDuration ?? 10;
            const optimalAnimationSpeed = loopDuration / durationInSeconds;

            const sonarInterval = 4000; // Augmenté de 2000 à 4000 pour ralentir le sonar
            const numSonarCycles = Math.max(1, Math.floor(durationInMs / sonarInterval));
            const optimalSonarSpeed = (numSonarCycles * sonarInterval) / durationInMs;

            return {
              ...grid,
              animationSpeed: optimalAnimationSpeed,
              sonarSpeed: optimalSonarSpeed
            };
          }
          return grid;
        }));

        await new Promise(resolve => setTimeout(resolve, 150));
      }

      const result = await startWebCodecsExport({
        duration: options.duration,
        fps: options.fps,
        outputFormat: options.outputFormat,
        loopDuration: options.loopDuration,
        fpsForFilename: options.fpsForFilename,
      });

      if (result.success) {
        // ✅ PostHog: Track video export
        captureEvent('multigrid:export_video', {
          resolution: `${canvasConfig.width}x${canvasConfig.height}`,
          grids_count: grids.length,
          is_animating: isAnimating,
          fps: options.fps,
          duration: options.duration,
          output_format: options.outputFormat,
          export_mode: exportMode,
        });
      } else {
        toast.error(t("export.error") || "Erreur d'export", {
          description: result.error
        });
      }
    }
    // Mode Grilles Individuelles : Export de chaque grille séparément
    else if (exportMode === 'individual-grids') {
      try {
        // Initialiser l'export des grilles
        setTotalGridsToExport(grids.length);
        setIsExportingGrids(true);
        setGridsExportProgress(0);
        setCurrentGridExporting(0);

        // Import JSZip pour créer l'archive
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();

        // Exporter chaque grille séparément
        for (let i = 0; i < grids.length; i++) {
          const grid = grids[i];
          setCurrentGridExporting(i + 1);
          setGridsExportProgress(Math.round((i / grids.length) * 100));


          // Calculer les vitesses optimales pour une boucle parfaite
          const durationInSeconds = options.duration / 1000;
          const durationInMs = options.duration;

          // Vitesse d'animation optimale pour avoir exactement X cycles (basée sur gradientLoopDuration)
          // Pour 10s: 10 / durationInSeconds
          // Pour 5s: 5 / durationInSeconds
          // Pour 3s: 3 / durationInSeconds
          const loopDuration = grid.gradientLoopDuration ?? 10;
          const optimalAnimationSpeed = loopDuration / durationInSeconds;

          // Vitesse du sonar optimale pour avoir des cycles entiers (intervalle 4000ms pour ralentir)
          const sonarInterval = 4000; // milliseconds - Augmenté de 2000 à 4000 pour ralentir le sonar
          const numSonarCycles = Math.max(1, Math.floor(durationInMs / sonarInterval));
          const optimalSonarSpeed = (numSonarCycles * sonarInterval) / durationInMs;

          // Appliquer les vitesses optimales à cette grille (case-insensitive)
          const patternTypeLower = grid.patternType?.toLowerCase();
          const isGradientPattern =
            patternTypeLower === 'gradient' ||
            patternTypeLower === 'gradient4' ||
            (grid.activePatternMode === 'custom' && (patternTypeLower === 'gradient' || patternTypeLower === 'gradient4'));

          if (isGradientPattern) {
            // Mettre à jour la grille dans l'état React
            setGrids(prev => prev.map(g =>
              g.id === grid.id
                ? { ...g, animationSpeed: optimalAnimationSpeed, sonarSpeed: optimalSonarSpeed }
                : g
            ));

            // Attendre que les changements soient appliqués et le re-render soit terminé (identique à /grid)
            await new Promise(resolve => setTimeout(resolve, 150));
          }

          // Créer un canvas temporaire pour cette grille
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = grid.width;
          tempCanvas.height = grid.height;

          // Créer une fonction de rendu pour cette grille spécifique
          const drawGridFunction = (time: number, showSelection: boolean) => {
            // CLEF: Rendre le CANVAS PRINCIPAL COMPLET avec tout (patterns, animations, sonars, logos, textes)
            // Cela garantit que j'obtiens exactement les mêmes rendus que ce qui s'affiche à l'écran
            renderCanvasRef.current({ forExport: true, exportTime: time });

            // Puis extraire juste la zone de la grille depuis le canvas principal
            const mainCanvas = canvasRef.current;
            if (!mainCanvas) {
              return;
            }

            const mainCtx = mainCanvas.getContext('2d');
            if (!mainCtx) {
              return;
            }

            // Extraire les données image pour cette grille depuis le canvas principal
            const imageData = mainCtx.getImageData(
              Math.max(0, Math.floor(grid.x)),
              Math.max(0, Math.floor(grid.y)),
              Math.min(grid.width, mainCanvas.width - grid.x),
              Math.min(grid.height, mainCanvas.height - grid.y)
            );

            // Coller l'image data sur le canvas temporaire
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) {
              return;
            }

            tempCtx.putImageData(imageData, 0, 0);
          };

          // Exporter cette grille avec le hook existant
          // On va utiliser un approche alternative : capturer directement les frames

          // Créer un promesse pour l'export de cette grille
          const gridExportPromise = new Promise<Blob>((resolve, reject) => {
            try {
              // Utiliser le worker directement pour cette grille
              const worker = new Worker(
                new URL('../../hooks/workers/video-encoder.worker.ts', import.meta.url),
                { type: 'module' }
              );

              let videoBuffer: ArrayBuffer;

              worker.onmessage = async (event: MessageEvent) => {
                const msg = event.data;

                if (msg.type === 'ready') {
                  // Encoder est prêt, commencer à envoyer les frames
                  const totalFrames = Math.floor((options.duration / 1000) * options.fps);
                  const frameDuration = 1000 / options.fps;

                  try {
                    for (let frameIndex = 0; frameIndex < totalFrames; frameIndex++) {
                      const time = frameIndex * frameDuration;

                      // Rendre le frame sur le canvas temporaire
                      drawGridFunction(time, false);

                      // Créer un ImageBitmap à partir du canvas
                      const imageBitmap = await createImageBitmap(tempCanvas);

                      // Envoyer le frame au worker
                      worker.postMessage(
                        {
                          type: 'frame',
                          frameIndex,
                          imageBitmap,
                          timestamp: time,
                        },
                        [imageBitmap]
                      );

                      // Yield to allow UI updates (every 10 frames)
                      if (frameIndex % 10 === 0) {
                        await new Promise(res => setTimeout(res, 0));
                      }
                    }

                    // Finalize encoding
                    worker.postMessage({ type: 'finalize' });
                  } catch (err) {
                    worker.terminate();
                    reject(err);
                  }
                } else if (msg.type === 'complete') {
                  videoBuffer = msg.videoBuffer;
                  const mimeType = msg.containerFormat === 'webm' ? 'video/webm' : 'video/mp4';
                  const blob = new Blob([videoBuffer], { type: mimeType });

                  worker.terminate();
                  resolve(blob);
                } else if (msg.type === 'error') {
                  worker.terminate();
                  reject(new Error(msg.error));
                }
              };

              worker.onerror = (error) => {
                worker.terminate();
                reject(error);
              };

              // Initialize encoder
              const config = {
                fps: options.fps,
                duration_ms: options.duration,
                width: grid.width,
                height: grid.height,
                bitrate: 50_000_000,
                keyframeInterval: 60,
              };

              worker.postMessage({ type: 'init', config });
            } catch (err) {
              reject(err);
            }
          });

          try {
            let gridBlob = await gridExportPromise;
            const loopSuffix = options.loopDuration ? `_${options.loopDuration}s` : '';
            const fpsSuffix = options.fpsForFilename ? `@${options.fpsForFilename}Hz` : '';
            let filename = `Grid_${String(i + 1).padStart(2, '0')}_${grid.width}x${grid.height}${loopSuffix}${fpsSuffix}`;
            const extension = 'webm'; // WebCodecs always outputs WebM

            // Pour Tauri avec un format non-WebM, convertir avec FFMPEG
            if (isTauriApp && options.outputFormat !== 'WebM') {
              setCurrentGridExporting(i + 1); // Mise à jour pour montrer la conversion

              // Convertir le blob WebM en base64 pour Tauri
              const reader = new FileReader();
              const base64Promise = new Promise<string>((resolve, reject) => {
                reader.onload = () => {
                  const result = reader.result as string;
                  resolve(result.split(',')[1]); // Extraire juste la partie base64
                };
                reader.onerror = reject;
                reader.readAsDataURL(gridBlob);
              });

              try {
                const base64Data = await base64Promise;
                const { invoke } = await import('@tauri-apps/api/core');

                // Créer un fichier temporaire pour la conversion
                const tempWebmPath = `${Date.now()}_grid_${i + 1}_temp.webm`;
                const outputPath = `${Date.now()}_grid_${i + 1}_output`;

                // Sauvegarder le WebM temporaire et convertir
                await invoke('convert_webm_to_format', {
                  buffer: base64Data,
                  format: options.outputFormat,
                  outputPath: outputPath,
                });

                // Lire le fichier converti
                const { readBinaryFile } = await import('@tauri-apps/plugin-fs');
                const convertedData = await readBinaryFile(outputPath);
                gridBlob = new Blob([convertedData], {
                  type: options.outputFormat === 'MP4' ? 'video/mp4' :
                        options.outputFormat === 'MOV' ? 'video/quicktime' :
                        'video/quicktime' // ProRes is MOV container
                });

                // Mettre à jour l'extension
                const newExtension = options.outputFormat === 'MP4' ? 'mp4' : 'mov';
                filename += `.${newExtension}`;

              } catch (convertErr) {
                filename += `.${extension}`;
              }
            } else {
              filename += `.${extension}`;
            }

            zip.file(filename, gridBlob);
            setGridsExportProgress(Math.round(((i + 1) / grids.length) * 90)); // Garder 90% pour le ZIP
          } catch (err) {
          }
        }

        // Créer l'archive ZIP
        setGridsExportProgress(95);
        const zipBlob = await zip.generateAsync({ type: 'blob' });

        // Télécharger l'archive
        setGridsExportProgress(98);
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `VisualGrid_Grids_Export_${new Date().getTime()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Terminé !
        setGridsExportProgress(100);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Laisser le UI montrer 100%
        setIsExportingGrids(false);

      } catch (error) {
        // console.error("[Grid Export] Error during individual grids export:", error);
        setIsExportingGrids(false);
        setGridsExportProgress(0);
      }
    }
  }, [isWebCodecsSupported, startWebCodecsExport, grids, isAnimating, canvasConfig]);

  // Fonction helper pour les confirmations (compatible Web et Tauri)
  const showConfirmDialog = async (message: string): Promise<boolean> => {
    if (isTauriApp) {
      try {
        const { confirm } = await import('@tauri-apps/plugin-dialog');
        return await confirm(message, {
          title: t("confirm.title") || "Confirmation",
          kind: 'warning'
        }) || false;
      } catch (error) {
        // console.error("Erreur lors de l'affichage de la confirmation Tauri:", error);
        // Fallback sur window.confirm si l'API Tauri échoue
        return window.confirm(message);
      }
    } else {
      // Web: utiliser window.confirm
      return window.confirm(message);
    }
  };

  // Fonction pour réinitialiser le MultiGrid
  const handleResetMultiGrid = () => {
    // Réinitialiser le canvas à la configuration par défaut
    setCanvasConfig({
      width: 1920,
      height: 1080,
      allowFullscreenScroll: false,
      stretchFullscreen: false,
      canvasPattern: 'none',
      canvasAnimationSpeed: 1,
      canvasPatternSeed: 0,
      canvasGradientLoopDuration: 10,
    });
    
    // Vider toutes les grilles
    setGrids([]);
    setSelectedGridId(null);
    
    // Réinitialiser les autres états
    setZoom(1);
    setIsAnimating(false);
    
    // Réinitialiser la configuration actuellement chargée
    setCurrentConfigName(null);
    setCurrentConfigDisplayName(null);
    
    // Forcer le rendu du canvas
    setTimeout(() => {
      if (canvasRef.current) {
        renderCanvas();
      }
    }, 100);
    
    toast.success(t("toast.configReset") || "Configuration réinitialisée avec succès");
  };

  // Fonction pour sauvegarder la configuration (Save - sauvegarde directe si config existante)
  const handleSaveConfig = async () => {
    // Si une configuration est déjà chargée, sauvegarder directement dessus
    if (currentConfigName) {
      try {
        // Préparer l'objet de configuration complète
        const fullConfig = {
          version: "1.0.0",
          timestamp: new Date().toISOString(),
          canvas: canvasConfig,
          grids: grids,
          multiGrid: true,
        };

        // Sauvegarder directement sur la configuration existante
        const savedConfig = savedConfigs.find(c => c.name === currentConfigName);
        const result = await saveConfig(fullConfig, {
          name: currentConfigName,
          displayName: currentConfigDisplayName || savedConfig?.displayName || currentConfigName,
          makeDefault: savedConfig?.isDefault || false
        });

        if (result && result.success) {
          const displayName = currentConfigDisplayName || savedConfig?.displayName || currentConfigName || "Configuration";
          toast.success((t("toast.configSaved") || "Configuration \"{name}\" sauvegardée avec succès").replace("{name}", displayName));
          await loadConfigList();
        } else {
          toast.error(result?.error || (t("config.saveError") || "Erreur lors de la sauvegarde"));
        }
      } catch (error) {
        // console.error("Erreur lors de la sauvegarde:", error);
        toast.error(t("config.saveError") || "Erreur lors de la sauvegarde");
      }
    } else {
      // Si aucune config n'est chargée, ouvrir le dialogue Save As
      handleSaveAsConfig();
    }
  }

  // Fonction pour sauvegarder sous un nouveau nom (Save As)
  const handleSaveAsConfig = () => {
    if (showSaveDialog) {
      // Si le dialogue est déjà ouvert, ne rien faire
      return;
    }

    // Pré-remplir le nom si une config est chargée (pour créer une copie)
    const initialName = currentConfigDisplayName 
      ? `${currentConfigDisplayName} (copie)`
      : "";
    
    // Ouvrir le dialogue de sauvegarde en mode Save As
    setConfigDisplayName(initialName);
    setMakeDefaultConfig(false);
    setIsSaveAs(true);
    setShowSaveDialog(true);
  }

  // Fonction helper pour reconstruire les grids avec les HTMLImageElement à partir des dataUrl
  const reconstructGrids = async (gridsToReconstruct: any[]): Promise<GridConfig[]> => {
    // Vérifier qu'on est côté client
    if (typeof window === 'undefined') {
      return gridsToReconstruct as GridConfig[];
    }
    
    return Promise.all(gridsToReconstruct.map(async (grid: any) => {
      const reconstructedGrid = { ...grid };
      
      // Reconstruire les logoElements si présents
      if (grid.logoElements && Array.isArray(grid.logoElements)) {
        reconstructedGrid.logoElements = await Promise.all(grid.logoElements.map(async (logoData: any) => {
          // Utiliser window.Image pour éviter les conflits avec Next.js Image component
          const img = new window.Image();
          
          // Utiliser dataUrl si disponible, sinon essayer image.src ou image
          const imageSrc = logoData.dataUrl || logoData.image?.src || (typeof logoData.image === 'string' ? logoData.image : '');
          
          // Créer une promesse pour attendre le chargement de l'image
          return new Promise<LogoElement>((resolve) => {
            img.onload = () => {
              resolve({
                id: logoData.id,
                x: logoData.x,
                y: logoData.y,
                width: logoData.width || img.width || 100,
                height: logoData.height || img.height || 100,
                originalWidth: logoData.originalWidth || logoData.width || img.width || 100,
                originalHeight: logoData.originalHeight || logoData.height || img.height || 100,
                opacity: logoData.opacity ?? 1,
                image: img
              });
            };
            img.onerror = () => {
              // En cas d'erreur, créer une image vide
              const emptyImg = new window.Image();
              emptyImg.width = logoData.width || 100;
              emptyImg.height = logoData.height || 100;
              resolve({
                id: logoData.id,
                x: logoData.x,
                y: logoData.y,
                width: logoData.width || 100,
                height: logoData.height || 100,
                originalWidth: logoData.originalWidth || logoData.width || 100,
                originalHeight: logoData.originalHeight || logoData.height || 100,
                opacity: logoData.opacity ?? 1,
                image: emptyImg
              });
            };
            
            if (imageSrc) {
              img.src = imageSrc;
            } else {
              // Si pas de source, déclencher onerror
              img.onerror(new Event('error'));
            }
          });
        }));
      }
      
      return reconstructedGrid;
    }));
  };

  // Fonction helper pour convertir les grids en format sérialisable (convertir les HTMLImageElement en dataUrl)
  const serializeGrids = (gridsToSerialize: GridConfig[]): any[] => {
    // Vérifier qu'on est côté client
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      // Côté serveur, retourner les grids sans conversion (les logos ne seront pas convertis)
      return gridsToSerialize.map(grid => {
        const serializableGrid = { ...grid };
        // Si les logoElements existent déjà avec des dataUrl, les conserver
        if (grid.logoElements && Array.isArray(grid.logoElements)) {
          serializableGrid.logoElements = grid.logoElements.map(logo => ({
            id: logo.id,
            x: logo.x,
            y: logo.y,
            width: logo.width,
            height: logo.height,
            originalWidth: logo.originalWidth,
            originalHeight: logo.originalHeight,
            opacity: logo.opacity,
            dataUrl: (logo as any).dataUrl || ''
          }));
        }
        return serializableGrid;
      });
    }
    
    return gridsToSerialize.map(grid => {
      const serializableGrid = { ...grid };
      
      // Convertir les logoElements si présents
      if (grid.logoElements && grid.logoElements.length > 0) {
        serializableGrid.logoElements = grid.logoElements.map(logo => {
          // Si l'image est déjà un dataUrl (cas d'un logo déjà sérialisé), le conserver
          if (logo.image && typeof logo.image === 'string' && logo.image.startsWith('data:')) {
            return {
              id: logo.id,
              x: logo.x,
              y: logo.y,
              width: logo.width,
              height: logo.height,
              originalWidth: logo.originalWidth,
              originalHeight: logo.originalHeight,
              opacity: logo.opacity,
              dataUrl: logo.image
            };
          }
          
          // Si l'image src est déjà un dataUrl, l'utiliser directement
          if (logo.image && logo.image.src && logo.image.src.startsWith('data:')) {
            return {
              id: logo.id,
              x: logo.x,
              y: logo.y,
              width: logo.width,
              height: logo.height,
              originalWidth: logo.originalWidth,
              originalHeight: logo.originalHeight,
              opacity: logo.opacity,
              dataUrl: logo.image.src
            };
          }
          
          // Sinon, créer un canvas temporaire pour convertir l'image en dataUrl
          if (logo.image && logo.image instanceof HTMLImageElement) {
            const canvas = document.createElement('canvas');
            canvas.width = logo.image.width || logo.width;
            canvas.height = logo.image.height || logo.height;
            const ctx = canvas.getContext('2d');
            if (ctx && logo.image) {
              try {
                ctx.drawImage(logo.image, 0, 0);
                const dataUrl = canvas.toDataURL('image/png');
                return {
                  id: logo.id,
                  x: logo.x,
                  y: logo.y,
                  width: logo.width,
                  height: logo.height,
                  originalWidth: logo.originalWidth,
                  originalHeight: logo.originalHeight,
                  opacity: logo.opacity,
                  dataUrl: dataUrl
                };
              } catch (error) {
                // console.error('Erreur lors de la conversion de l\'image en dataUrl:', error);
                // Si l'image ne peut pas être convertie, essayer d'utiliser src si disponible
                return {
                  id: logo.id,
                  x: logo.x,
                  y: logo.y,
                  width: logo.width,
                  height: logo.height,
                  originalWidth: logo.originalWidth,
                  originalHeight: logo.originalHeight,
                  opacity: logo.opacity,
                  dataUrl: logo.image.src || ''
                };
              }
            }
          }
          
          // Fallback : retourner avec un dataUrl vide
          return {
            id: logo.id,
            x: logo.x,
            y: logo.y,
            width: logo.width,
            height: logo.height,
            originalWidth: logo.originalWidth,
            originalHeight: logo.originalHeight,
            opacity: logo.opacity,
            dataUrl: ''
          };
        });
      }
      
      return serializableGrid;
    });
  };

  // Fonction pour effectuer la sauvegarde
  const handleSaveConfigConfirm = async () => {
    try {
      // Valider le nom d'affichage et générer le nom technique automatiquement
      const displayName = configDisplayName.trim() || `Configuration MultiGrid du ${new Date().toLocaleDateString()}`;
      // Générer le nom technique à partir du nom d'affichage (sans espaces, caractères spéciaux, etc.)
      const name = displayName
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Supprimer les accents
        .replace(/[^a-z0-9]/g, "_") // Remplacer les caractères non alphanumériques par des underscores
        .replace(/_+/g, "_") // Remplacer les underscores multiples par un seul
        .replace(/^_|_$/g, "") // Supprimer les underscores en début/fin
        || `multigrid_${new Date().getTime()}`;

      // Convertir les grids en format sérialisable (convertir les HTMLImageElement en dataUrl)
      const serializableGrids = serializeGrids(grids);

      // Préparer l'objet de configuration complète
      const fullConfig = {
        version: "1.0.0",
        timestamp: new Date().toISOString(),
        canvas: canvasConfig,
        grids: serializableGrids,
        multiGrid: true, // Marqueur pour identifier une config MultiGrid
      };

      // Sauvegarder la configuration avec saveConfig (supporte makeDefault)
      const result = await saveConfig(fullConfig, {
        name,
        displayName,
        makeDefault: makeDefaultConfig
      });

      if (result && result.success) {
        const configDisplayName = displayName || name || "Configuration";
        
        // Définir la configuration actuellement chargée après sauvegarde
        setCurrentConfigName(name);
        setCurrentConfigDisplayName(displayName);
        
        toast.success((t("toast.configSaved") || "Configuration \"{name}\" sauvegardée avec succès").replace("{name}", configDisplayName));
        setShowSaveDialog(false);
        setIsSaveAs(false);
        
        // ✅ PostHog: Track config save
        captureEvent('multigrid:save_config', {
          resolution: `${canvasConfig.width}x${canvasConfig.height}`,
          grids_count: grids.length,
          is_animating: isAnimating,
          is_save_as: isSaveAs,
        });
        
        // Recharger la liste des configs pour qu'elle apparaisse dans le dialogue d'import
        await loadConfigList();

        // Incrémenter le compteur lors de la sauvegarde
        try {
          await fetch(getApiUrl('/api/grid-counters/increment'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ mode: 'multiGridMode' }),
          });
        } catch (error) {
        }
      } else {
        toast.error(result?.error || (t("config.error") || "Erreur lors de la sauvegarde"));
      }
    } catch (error) {
      // console.error("Erreur lors de la sauvegarde:", error);
      toast.error(t("config.error") || "Erreur lors de la sauvegarde");
    }
  };

  // Fonction pour exporter la configuration en fichier .mgrd depuis le menu Import/Export
  const handleExportConfigFileFromMenu = async () => {
    try {
      // Générer un nom de fichier par défaut basé sur la date
      const defaultName = `multigrid_${new Date().toISOString().slice(0, 10).replace(/-/g, '_')}`;
      const filename = `${defaultName}.mgrd`;
      
      // Convertir les grids en format sérialisable
      const serializableGrids = serializeGrids(grids);
      
      const fullConfig = {
        version: "1.0.0",
        timestamp: new Date().toISOString(),
        canvas: canvasConfig,
        grids: serializableGrids,
        multiGrid: true,
      };

      // Utiliser exportConfigFile si disponible (Electron/Tauri), sinon fallback web
      if (isDesktopApp && exportConfigFile) {
        const result = await exportConfigFile(fullConfig, filename);
        if (result && result.success) {
          toast.success(t("config.exportedToFile") || "Configuration exportée en fichier .mgrd");
        } else {
          toast.error(result?.error || t("config.exportFileError") || "Erreur lors de l'export du fichier");
        }
      } else {
        // Fallback pour navigateur web
        const configBlob = new Blob([JSON.stringify(fullConfig, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(configBlob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        
        setTimeout(() => URL.revokeObjectURL(url), 100);
        
        toast.success(t("config.exportedToFile") || "Configuration exportée en fichier .mgrd");
      }
    } catch (error) {
      // console.error("Erreur lors de l'export:", error);
      toast.error(t("config.exportFileError") || "Erreur lors de l'export du fichier");
    }
  };

  // Fonction pour exporter la configuration en fichier .mgrd depuis le dialogue de sauvegarde
  const handleExportConfigFile = async () => {
    try {
      // Utiliser les valeurs du dialogue ou des valeurs par défaut
      const displayName = configDisplayName.trim() || `Configuration MultiGrid du ${new Date().toLocaleDateString()}`;
      // Générer le nom technique à partir du nom d'affichage
      const name = displayName
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Supprimer les accents
        .replace(/[^a-z0-9]/g, "_") // Remplacer les caractères non alphanumériques par des underscores
        .replace(/_+/g, "_") // Remplacer les underscores multiples par un seul
        .replace(/^_|_$/g, "") // Supprimer les underscores en début/fin
        || `multigrid_${new Date().getTime()}`;
      const filename = `${name}.mgrd`;

      // Convertir les grids en format sérialisable
      const serializableGrids = serializeGrids(grids);

      const fullConfig = {
        version: "1.0.0",
        timestamp: new Date().toISOString(),
        canvas: canvasConfig,
        grids: serializableGrids,
        multiGrid: true,
      };

      // Utiliser exportConfigFile si disponible (Electron/Tauri), sinon fallback web
      if (isDesktopApp && exportConfigFile) {
        const result = await exportConfigFile(fullConfig, filename);
        if (result && result.success) {
          toast.success(t("config.exportedToFile") || "Configuration exportée en fichier .mgrd");
        } else {
          toast.error(result?.error || t("config.exportFileError") || "Erreur lors de l'export du fichier");
        }
      } else {
        // Fallback pour navigateur web
        const configBlob = new Blob([JSON.stringify(fullConfig, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(configBlob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        
        setTimeout(() => URL.revokeObjectURL(url), 100);
        
        toast.success(t("config.exportedToFile") || "Configuration exportée en fichier .mgrd");
      }
    } catch (error) {
      // console.error("Erreur lors de l'export:", error);
      toast.error(t("config.exportFileError") || "Erreur lors de l'export du fichier");
    }
  };

  // Fonction pour charger une config depuis les configs sauvegardées
  const handleLoadSavedConfig = async (configName: string) => {
    try {
      const result = await loadConfig(configName);
      if (result && result.success && result.config) {
        const loadedConfig = result.config;
        
        // Vérifier que c'est bien une config MultiGrid
        if (loadedConfig.multiGrid && loadedConfig.canvas && Array.isArray(loadedConfig.grids)) {
          setCanvasConfig(loadedConfig.canvas);
          
          // Reconstruire les grids avec les HTMLImageElement à partir des dataUrl
          const reconstructedGrids = await reconstructGrids(loadedConfig.grids);
          
          setGrids(reconstructedGrids);
          
          if (reconstructedGrids.length > 0) {
            setSelectedGridId(reconstructedGrids[0].id);
          }
          
          setShowImportDialog(false);
          // Récupérer le displayName depuis savedConfigs
          const savedConfig = savedConfigs.find(c => c.name === configName);
          const configDisplayName = savedConfig?.displayName || configName || "Configuration";
          
          // Définir la configuration actuellement chargée
          setCurrentConfigName(configName);
          setCurrentConfigDisplayName(configDisplayName);
          
          toast.success((t("toast.configLoaded") || "Configuration \"{name}\" chargée").replace("{name}", configDisplayName));
          // Forcer le rendu du canvas après le chargement (avec un délai pour laisser les images se charger)
          setTimeout(() => {
            if (canvasRef.current) {
              renderCanvas();
            }
          }, 200);
        } else {
          toast.error(t("config.invalidMultiGrid") || "Cette configuration n'est pas une configuration MultiGrid valide");
        }
      } else {
        toast.error(result?.error || (t("config.loadError") || "Erreur lors du chargement de la configuration"));
      }
    } catch (error) {
      // console.error("Erreur lors du chargement de la config:", error);
      toast.error(t("config.loadError") || "Erreur lors du chargement de la configuration");
    }
  };

  // Fonction pour renommer une config sauvegardée
  const handleRenameSavedConfig = async (configName: string, newDisplayName: string) => {
    if (!newDisplayName.trim()) {
      toast.error(t("config.nameRequired") || "Le nom ne peut pas être vide");
      return;
    }

    try {
      // Charger la configuration existante
      const result = await loadConfig(configName);
      if (result && result.success && result.config) {
        // Sauvegarder avec le nouveau nom d'affichage
        const saveResult = await saveConfig(result.config, {
          name: configName, // Garder le même nom technique
          displayName: newDisplayName.trim(),
          makeDefault: savedConfigs.find(c => c.name === configName)?.isDefault || false
        });

        if (saveResult && saveResult.success) {
          toast.success(t("config.renamed") || "Configuration renommée avec succès");
          await loadConfigList();
          setRenamingConfigName(null);
          setNewConfigDisplayName("");
        } else {
          toast.error(saveResult?.error || (t("config.renameError") || "Erreur lors du renommage"));
        }
      } else {
        toast.error(t("config.loadError") || "Erreur lors du chargement de la configuration");
      }
    } catch (error) {
      // console.error("Erreur lors du renommage:", error);
      toast.error(t("config.renameError") || "Erreur lors du renommage");
    }
  };

  // Fonction pour définir une config comme défaut
  const handleSetDefaultConfig = async (configName: string) => {
    try {
      // Charger la configuration existante
      const result = await loadConfig(configName);
      if (result && result.success && result.config) {
        const savedConfig = savedConfigs.find(c => c.name === configName);
        // Sauvegarder avec makeDefault = true
        const saveResult = await saveConfig(result.config, {
          name: configName,
          displayName: savedConfig?.displayName || configName,
          makeDefault: true
        });

        if (saveResult && saveResult.success) {
          toast.success(t("config.setAsDefault") || "Configuration définie comme défaut");
          await loadConfigList();
        } else {
          toast.error(saveResult?.error || (t("config.setDefaultError") || "Erreur lors de la définition comme défaut"));
        }
      } else {
        toast.error(t("config.loadError") || "Erreur lors du chargement de la configuration");
      }
    } catch (error) {
      // console.error("Erreur lors de la définition comme défaut:", error);
      toast.error(t("config.setDefaultError") || "Erreur lors de la définition comme défaut");
    }
  };

  // Fonction pour supprimer une config sauvegardée
  const handleDeleteSavedConfig = async (configName: string, displayName?: string) => {
    const configDisplayName = displayName || configName;
    const confirmMessage = (t("config.deleteConfirm") || "Êtes-vous sûr de vouloir supprimer la configuration \"{name}\" ?").replace("{name}", configDisplayName);
    
    const confirmed = await showConfirmDialog(confirmMessage);
    if (!confirmed) {
      return;
    }
    
    try {
      const result = await deleteConfig(configName);
      if (result && result.success) {
        toast.success(t("config.deleted") || "Configuration supprimée avec succès");
        // Recharger la liste des configs
        await loadConfigList();
      } else {
        toast.error(result?.error || (t("config.deleteError") || "Erreur lors de la suppression"));
      }
    } catch (error) {
      // console.error("Erreur lors de la suppression:", error);
      toast.error(t("config.deleteError") || "Erreur lors de la suppression");
    }
  };

  // Fonction pour importer une configuration depuis un fichier (uniquement .mgrd)
  const handleImportConfig = async () => {
    try {
      const result = await importConfigFile('mgrd');
      
      if (result && result.success && result.config) {
        const loadedConfig = result.config;

        // Vérifier que la configuration est valide (MultiGrid format: canvas + grids)
        if (loadedConfig.canvas && Array.isArray(loadedConfig.grids)) {
          // Charger la configuration dans l'interface
          setCanvasConfig(loadedConfig.canvas);
          
          // Reconstruire les grids avec les HTMLImageElement à partir des dataUrl
          const reconstructedGrids = await reconstructGrids(loadedConfig.grids);
          
          setGrids(reconstructedGrids);

          // Sélectionner la première grille si elle existe
          if (reconstructedGrids.length > 0) {
            setSelectedGridId(reconstructedGrids[0].id);
          }

          // Extraire le nom du fichier (avec ou sans extension selon ce qui est retourné)
          const fileNameWithExt = result.fileName || `imported_${new Date().getTime()}.mgrd`;
          const fileName = fileNameWithExt.replace(/\.[^/.]+$/, ""); // Retirer l'extension
          const importConfigName = fileName
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // Supprimer les accents
            .replace(/[^a-z0-9]/g, "_") // Remplacer les caractères non alphanumériques par des underscores
            .replace(/_+/g, "_") // Remplacer les underscores multiples par un seul
            .replace(/^_|_$/g, "") // Supprimer les underscores en début/fin
            || `imported_${new Date().getTime()}`;

          // Préparer la configuration complète avec les métadonnées
          const fullConfig = {
            ...loadedConfig,
            version: loadedConfig.version || "1.0.0",
            timestamp: new Date().toISOString(),
            multiGrid: true,
          };

          // Sauvegarder la configuration importée
          const saveResult = await saveConfig(fullConfig, {
            name: importConfigName,
            displayName: fileName,
            makeDefault: false
          });

          if (saveResult && saveResult.success) {
            // Recharger la liste des configs
            await loadConfigList();
            
            // Définir la configuration actuellement chargée (config importée et sauvegardée)
            setCurrentConfigName(importConfigName);
            setCurrentConfigDisplayName(fileName);
            
            toast.success((t("config.importedAndSaved") || "Configuration \"{name}\" importée et sauvegardée").replace("{name}", fileName));
          } else {
            // Si la sauvegarde a échoué, c'est une nouvelle config non sauvegardée
            setCurrentConfigName(null);
            setCurrentConfigDisplayName(null);
            toast.success((t("toast.configLoaded") || "Configuration \"{name}\" chargée").replace("{name}", fileName));
          }

          renderCanvas();
        } else {
          toast.error(t("config.invalidFormat") || "Format de configuration invalide (doit contenir canvas et grids)");
        }
      } else if (result && !result.success) {
        toast.error(result.error || t("config.loadError") || "Erreur lors du chargement");
      }
    } catch (error) {
      // console.error("Erreur lors du chargement:", error);
      toast.error(t("config.loadError") || "Erreur lors du chargement");
    }
  };

  // Gestionnaire de changement pour les dimensions du canvas
  const handleCanvasDimensionChange = (
    key: "width" | "height",
    value: string,
  ) => {
    // Supprimer les zéros non significatifs au début
    let cleanValue = value.replace(/^0+(?=\d)/, "");

    // Si la valeur est vide ou juste "0", on la garde telle quelle
    if (cleanValue === "" || cleanValue === "0") {
      cleanValue = cleanValue || "0";
    }

    // Convertir en nombre et mettre à jour la configuration
    const numericValue = parseInt(cleanValue, 10);
    setCanvasConfig((c) => ({
      ...c,
      [key]: isNaN(numericValue) ? 0 : numericValue,
    }));
    
    // Signaler une modification du canvas
    gridCounter.onActivity(false); // false = modification
  };

  // Fonction pour basculer le mode plein écran
  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      // Désélectionner les éléments avant de passer en plein écran
      setSelectedGridId(null);
      setSelectedTextId(null);
      setSelectedLogoId(null);
      
      if (fullscreenContainerRef.current) {
        fullscreenContainerRef.current.requestFullscreen();
      }
    }
    
    // Incrémenter le compteur lors du passage en plein écran
    try {
      fetch(getApiUrl('/api/grid-counters/increment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode: 'multiGridMode' }),
      });
    } catch (error) {
    }
  };

  // Écouteur d'événement pour le changement de plein écran
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      
      // Désélectionner les éléments quand on passe en mode plein écran
      if (document.fullscreenElement) {
        setSelectedGridId(null);
        setSelectedTextId(null);
        setSelectedLogoId(null);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Effet pour gérer l'échelle du canvas en mode plein écran
  useEffect(() => {
    // Cette fonction n'est plus nécessaire car le zoom est maintenant géré directement
    // via les styles du conteneur fullscreen
    const adjustCanvasScale = () => {
      // Ajuster le canvas en cas de redimensionnement de la fenêtre
      if (canvasRef.current) {
        // Redessiner le canvas pour s'assurer que tout est correctement affiché
        renderCanvas();
      }
    };

    // Ajuster si la fenêtre est redimensionnée
    window.addEventListener("resize", adjustCanvasScale);
    return () => window.removeEventListener("resize", adjustCanvasScale);
  }, [isFullscreen, zoom, renderCanvas]);

  // Désactiver le défilement en mode plein écran si configuré ainsi
  useEffect(() => {
    if (isFullscreen && !canvasConfig.allowFullscreenScroll) {
      const handleWheel = (e: WheelEvent) => {
        e.preventDefault();
      };

      document.addEventListener("wheel", handleWheel, { passive: false });
      return () => document.removeEventListener("wheel", handleWheel);
    }
  }, [isFullscreen, canvasConfig.allowFullscreenScroll]);

  // Gestion automatique de l'affichage UI pour les mires standards de chaque grille
  useEffect(() => {
    grids.forEach(grid => {
      const isStandardPatternActive = grid.activePatternMode === 'standard' && grid.standardPatternType !== 'none';
      const hasStavedStates = previousGridUIStates[grid.id];
      
      if (isStandardPatternActive) {
        // Passer en mode mires standards : sauvegarder et désactiver
        if (!hasStavedStates) {
          // Sauvegarder les états actuels de cette grille
          setPreviousGridUIStates(prev => ({
            ...prev,
            [grid.id]: {
              showTechnicalDetails: grid.showTechnicalDetails,
              showGrid: grid.showGrid,
              showLogo: grid.showLogo,
              showLEDNumbers: grid.showLEDNumbers,
              showDimensions: grid.showDimensions,
              showSonar: grid.showSonar
            }
          }));
          
          // Désactiver détails techniques sauf dimensions pour cette grille (mires standards)
          updateGrid(grid.id, {
            showTechnicalDetails: false,
            showGrid: false,
            showLogo: false,
            showLEDNumbers: false,
            showDimensions: true, // Garder dimensions pour mires
            showSonar: false
          });
        }
      } else {
        // Sortir du mode mires standards : restaurer les valeurs précédentes
        if (hasStavedStates) {
          updateGrid(grid.id, {
            showTechnicalDetails: hasStavedStates.showTechnicalDetails,
            showGrid: hasStavedStates.showGrid,
            showLogo: hasStavedStates.showLogo,
            showLEDNumbers: hasStavedStates.showLEDNumbers,
            showDimensions: hasStavedStates.showDimensions,
            showSonar: hasStavedStates.showSonar
          });
          
          // Effacer la sauvegarde pour cette grille
          setPreviousGridUIStates(prev => {
            const newState = { ...prev };
            delete newState[grid.id];
            return newState;
          });
        }
      }
    });
  }, [grids, previousGridUIStates, updateGrid]);

  // Trouver la grille sélectionnée
  const selectedGrid = selectedGridId
    ? grids.find((g) => g.id === selectedGridId)
    : null;

  // Calculs pour le récapitulatif
  const totalPixels = grids.reduce(
    (acc, grid) => acc + grid.width * grid.height,
    0,
  );

  // Modifier la fonction snapThreshold pour tenir compte du zoom
  const snapThreshold = 10 / zoom;

  // Show paywall if not premium (only when Supabase is configured)
  // Attendre le montage pour éviter les problèmes d'hydration
  if (!mounted) {
    return (
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </ThemeProvider>
    );
  }
  
  if (supabaseConfigured) {
    if (authLoading) {
      // Show loading state while checking auth
      return (
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        </ThemeProvider>
      );
    }

    if (!isPro && !isExternalDisplay) {
      return (
        <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
          <Paywall message="Le MultiGrid est une fonctionnalité premium. Passez en Pro pour y accéder." />
        </ThemeProvider>
      );
    }
  }

  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
      <main className="flex min-h-screen flex-col">
        {/* Header avec retour et titre - masqué en mode externe */}
        {!isExternalDisplay && (
        <div className="border-b">
          <div className="flex h-16 items-center px-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                // Priorité: navigation relative fiable en file:// avec SmartLink
                if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
                  // Aller vers la racine exportée index.html
                  const pathname = window.location.pathname || '';
                  const outIdx = pathname.lastIndexOf('/out/');
                  const base = outIdx >= 0 ? pathname.substring(0, outIdx + '/out/'.length) : pathname;
                  window.location.href = base + 'index.html';
                } else {
                  // Web normal: history back ou racine
                  if (window.history.length > 1) window.history.back();
                  else window.location.href = '/';
                }
              }}
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <div className="ml-4 flex items-center gap-2">
              <AppLogo />
              <h1 className="text-2xl font-bold tracking-tight">
                {t("multigrid.title") || "MultiGrid"}
              </h1>
            </div>
            <div className="ml-auto flex items-center space-x-2">
              {/* Desktop Navigation */}
              {!isMobile && (
                <>
                  <LanguageSwitcher />
                </>
              )}
              
              {/* Bouton Reset */}
              <Button 
                onClick={async () => {
                  const confirmed = await showConfirmDialog(t("confirm.resetMultiGrid") || "Attention: Cela va réinitialiser TOUTE la configuration MultiGrid, toutes les grilles et les paramètres du canvas. Êtes-vous sûr de vouloir continuer?");
                  if (confirmed) {
                    handleResetMultiGrid();
                  }
                }}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {t("preview.reset") || "Reset"}
              </Button>
              
              {/* Bouton Save */}
              {/* Bouton Save - Toujours présent, sauvegarde directe si config existante */}
              <Button 
                onClick={handleSaveConfig} 
                variant="default" 
                className="flex items-center gap-2"
                title={currentConfigName 
                  ? (t("config.saveTooltip") || "Sauvegarder la configuration actuelle") 
                  : (t("config.saveAsTooltip") || "Sauvegarder sous un nouveau nom")}
              >
                <Save className="h-4 w-4" />
                {t("config.save") || "Sauvegarder"}
              </Button>
              
              {/* Action menu - Import/Export */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="default" className="flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    {t("action.importExport") || "Import/Export"}
                    <ChevronDown className="h-4 w-4 opacity-70" />
              </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuItem onClick={() => setShowImportDialog(true)} className="cursor-pointer">
                    <Upload className="mr-3 h-4 w-4 text-purple-500" />
                    <div className="flex flex-col">
                      <span className="font-medium">{t("config.importFromSaved") || "Charger depuis les configs sauvegardées"}</span>
                      <span className="text-xs text-muted-foreground">{t("config.importFromSavedDesc") || "Sélectionner une config sauvegardée"}</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleImportConfig} className="cursor-pointer">
                    <Upload className="mr-3 h-4 w-4 text-blue-500" />
                    <div className="flex flex-col">
                      <span className="font-medium">{t("config.importFromFile") || "Charger depuis un fichier"}</span>
                      <span className="text-xs text-muted-foreground">{t("config.importFromFileDesc") || "Importer un fichier .mgrd"}</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSaveAsConfig} className="cursor-pointer">
                    <Save className="mr-3 h-4 w-4 text-green-500" />
                    <div className="flex flex-col">
                      <span className="font-medium">{t("config.saveAs") || "Sauvegarder sous..."}</span>
                      <span className="text-xs text-muted-foreground">{t("config.saveAsTooltip") || "Sauvegarder sous un nouveau nom"}</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportConfigFileFromMenu} className="cursor-pointer">
                    <Download className="mr-3 h-4 w-4 text-indigo-500" />
                    <div className="flex flex-col">
                      <span className="font-medium">{t("config.exportToFile") || "Exporter en .mgrd"}</span>
                      <span className="text-xs text-muted-foreground">{t("config.exportToFileDesc") || "Exporter la configuration en fichier .mgrd"}</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportCanvas} className="cursor-pointer">
                    <Download className="mr-3 h-4 w-4 text-orange-500" />
                    <div className="flex flex-col">
                      <span className="font-medium">{t("config.export") || "Exporter en PNG"}</span>
                      <span className="text-xs text-muted-foreground">{t("config.exportPNGDesc") || "Télécharger l'image du canvas"}</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                onClick={handleExportLoopVideo}
                disabled={isWebCodecsExporting}
                    className={`cursor-pointer ${isWebCodecsExporting ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                    <Video className="mr-3 h-4 w-4 text-green-500" />
                    <div className="flex flex-col">
                      <span className={`font-medium ${isWebCodecsExporting ? "" : "text-green-500"}`}>
                        {t('action.exportVideo') || "Exporter en vidéo"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {isWebCodecsExporting ? (t("export.inProgress") || "Export en cours...") : (t("export.createVideo") || "Créer une vidéo animée")}
                      </span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Mobile Burger Menu */}
              {isMobile && (
                <BurgerMenu showDownloads={false} />
              )}
            </div>
          </div>
        </div>
        )}

        {/* Image cachée du logo pour le canvas - toujours visible même en mode externe */}
        <div style={{ display: "none" }}>
          <Image
            id="visualgrid-logo"
            src={logoImageUrl}
            alt="VisualGrid Logo"
            width={50}
            height={50}
          />
        </div>

        {/* Contenu principal */}
        <div className="flex flex-1 overflow-hidden">
          {/* Panneau de contrôle */}
          {/* Sidebar - masquée en mode externe */}
          {!isExternalDisplay && (
          <div className="w-96 border-r p-4 overflow-y-auto">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t("multigrid.recapTitle") || "Récapitulatif"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>
                    {t("multigrid.recapCanvasSize") || "Taille Canvas"}:
                  </span>
                  <strong>
                    {canvasConfig.width} x {canvasConfig.height} px
                  </strong>
                </div>
                <div className="flex justify-between">
                  <span>
                    {t("multigrid.recapGridCount") || "Nombre de grilles"}:
                  </span>
                  <strong>{grids.length}</strong>
                </div>
                <div className="flex justify-between">
                  <span>
                    {t("multigrid.recapTotalPixels") || "Total Pixels"}:
                  </span>
                  <strong>{totalPixels.toLocaleString()} px</strong>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  {t("multigrid.canvasConfig") || "Configuration du Canvas"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="canvasWidth">
                      {t("config.width") || "Largeur"}
                    </Label>
                    <MathInput
                      id="canvasWidth"
                      type="text"
                      inputMode="numeric"
                      value={canvasConfig.width}
                      onValueChange={(value) =>
                        handleCanvasDimensionChange("width", value.toString())
                      }
                      aria-label={t("multigrid.canvasWidth") || "Largeur du canvas"}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="canvasHeight">
                      {t("config.height") || "Hauteur"}
                    </Label>
                    <MathInput
                      id="canvasHeight"
                      type="text"
                      inputMode="numeric"
                      value={canvasConfig.height}
                      onValueChange={(value) =>
                        handleCanvasDimensionChange("height", value.toString())
                      }
                      aria-label={t("multigrid.canvasHeight") || "Hauteur du canvas"}
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="allowFullscreenScroll"
                    checked={canvasConfig.allowFullscreenScroll}
                    onCheckedChange={(checked) => {
                      setCanvasConfig({
                        ...canvasConfig,
                        allowFullscreenScroll: checked,
                      });
                      gridCounter.onActivity(false); // false = modification
                    }}
                  />
                  <Label htmlFor="allowFullscreenScroll">
                    {t("config.fullscreenScroll") || "Défilement plein écran"}
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="stretchFullscreen"
                    checked={canvasConfig.stretchFullscreen}
                    onCheckedChange={(checked) =>
                      setCanvasConfig({
                        ...canvasConfig,
                        stretchFullscreen: checked,
                      })
                    }
                  />
                  <Label htmlFor="stretchFullscreen">
                    {t("config.stretchFullscreen") || "Étirer en plein écran"}
                  </Label>
                </div>

                {/* Motif de canvas */}
                <div className="space-y-2 pt-2 border-t">
                  <Label className="text-sm font-medium">{t("multigrid.canvasPattern") || "Motif du canvas"}</Label>
                  <p className="text-xs text-muted-foreground">{t("multigrid.canvasPatternDesc") || "Le motif couvre tout le canvas. Les mires deviennent des fenêtres transparentes."}</p>
                  <Select
                    value={canvasConfig.canvasPattern || 'none'}
                    onValueChange={(value) => setCanvasConfig({ ...canvasConfig, canvasPattern: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("multigrid.canvasPatternNone") || "Aucun (motifs par mire)"}</SelectItem>
                      {patternTypes.filter(p => p.value !== 'snakeGame').map((pattern) => (
                        <SelectItem key={pattern.value} value={pattern.value}>{pattern.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {canvasConfig.canvasPattern && canvasConfig.canvasPattern !== 'none' && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs w-28">{t("multigrid.animSpeed") || "Vitesse"}</Label>
                        <Slider
                          min={0.1} max={5} step={0.1}
                          value={[canvasConfig.canvasAnimationSpeed ?? 1]}
                          onValueChange={([v]) => setCanvasConfig({ ...canvasConfig, canvasAnimationSpeed: v })}
                          className="flex-1"
                        />
                        <span className="text-xs w-8 text-right">{(canvasConfig.canvasAnimationSpeed ?? 1).toFixed(1)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t("multigrid.grids") || "Grilles"}</CardTitle>
                <Button size="sm" onClick={addNewGrid}>
                  <Plus className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-2">
                {grids.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {t("multigrid.noGrids") ||
                      "Aucune grille créée. Cliquez sur + pour ajouter une grille."}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {grids.map((grid) => (
                      <div
                        key={grid.id}
                        className={`flex items-center justify-between p-2 rounded-md cursor-pointer ${
                          selectedGridId === grid.id
                            ? "bg-primary/10"
                            : "hover:bg-secondary/50"
                        }`}
                        onClick={() => {
                          // Si la grille est déjà sélectionnée, la dé-sélectionner
                          if (selectedGridId === grid.id) {
                            setSelectedGridId(null);
                          } else {
                            setSelectedGridId(grid.id);
                          }
                        }}
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          <Grid className="h-4 w-4 mr-2 flex-shrink-0" />
                          {editingGridId === grid.id ? (
                            <Input
                              className="text-sm font-medium h-7 flex-1"
                              value={editingGridName}
                              onChange={(e) => setEditingGridName(e.target.value)}
                              onBlur={() => {
                                if (editingGridName.trim()) {
                                  updateGrid(grid.id, { name: editingGridName.trim() });
                                  renderCanvas();
                                }
                                setEditingGridId(null);
                                setEditingGridName("");
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  if (editingGridName.trim()) {
                                    updateGrid(grid.id, { name: editingGridName.trim() });
                                    renderCanvas();
                                  }
                                  setEditingGridId(null);
                                  setEditingGridName("");
                                } else if (e.key === "Escape") {
                                  setEditingGridId(null);
                                  setEditingGridName("");
                                }
                              }}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span
                              className="text-sm font-medium flex-1 truncate"
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                                setEditingGridId(grid.id);
                                setEditingGridName(grid.name);
                              }}
                              title={t("multigrid.doubleClickToEdit") || "Double-clic pour éditer"}
                            >
                              {grid.name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground mr-2">
                            {`${grid.width}x${grid.height}`}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateGrid(grid.id);
                            }}
                            title={t("multigrid.duplicateGrid") || "Dupliquer"}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteGrid(grid.id);
                            }}
                            title={t("multigrid.deleteGrid") || "Supprimer"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {selectedGrid && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    {t("multigrid.gridParams") || "Paramètres de la grille"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="gridName">
                      {t("multigrid.gridName") || "Nom"}
                    </Label>
                    <Input
                      id="gridName"
                      type="text"
                      value={selectedGrid.name}
                      onChange={(e) => {
                        updateGrid(selectedGrid.id, { name: e.target.value });
                        renderCanvas(); // Mettre à jour immédiatement le canvas
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label htmlFor="gridWidth">
                        {t("config.width") || "Largeur"}
                      </Label>
                      <MathInput
                        id="gridWidth"
                        type="number"
                        value={selectedGrid.width}
                        onValueChange={(value) => {
                          updateGrid(selectedGrid.id, { width: value });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                        aria-label={t("multigrid.gridWidth") || "Largeur de la grille"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gridHeight">
                        {t("config.height") || "Hauteur"}
                      </Label>
                      <MathInput
                        id="gridHeight"
                        type="number"
                        value={selectedGrid.height}
                        onValueChange={(value) => {
                          updateGrid(selectedGrid.id, { height: value });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                        aria-label={t("multigrid.gridHeight") || "Hauteur de la grille"}
                      />
                    </div>
                  </div>

                  {/* Position de la grille - déplacée ici comme demandé */}
                  <div className="space-y-2">
                    <Label htmlFor="gridPosition">
                      {t("multigrid.position") || "Position"}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="gridPositionX"
                        type="number"
                        value={selectedGrid.x}
                        onChange={(e) => {
                          updateGrid(selectedGrid.id, {
                            x: parseInt(e.target.value) || 0,
                          });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                        placeholder={t("multigrid.positionX") || "X"}
                      />
                      <Input
                        id="gridPositionY"
                        type="number"
                        value={selectedGrid.y}
                        onChange={(e) => {
                          updateGrid(selectedGrid.id, {
                            y: parseInt(e.target.value) || 0,
                          });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                        placeholder={t("multigrid.positionY") || "Y"}
                      />
                    </div>
                  </div>

                  {/* Sélecteur de mode de motif - masqué si un motif de canvas est actif */}
                  {(canvasConfig.canvasPattern && canvasConfig.canvasPattern !== 'none') ? (
                    <div className="rounded-md bg-muted/40 border border-dashed p-3 text-xs text-muted-foreground">
                      {t("multigrid.canvasPatternActive") || "Un motif est défini au niveau du canvas. Les mires sont des fenêtres transparentes vers ce motif."}
                    </div>
                  ) : (<>
                  <div className="space-y-2">
                    <Label>
                      {t("multigrid.patternMode") || "Mode de motif"}
                    </Label>
                    <ToggleGroup
                      type="single"
                      value={selectedGrid.activePatternMode}
                      onValueChange={(value: "custom" | "solid" | "standard") => {
                        if (value) {
                          updateGrid(selectedGrid.id, {
                            activePatternMode: value,
                          });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }
                      }}
                      className="grid grid-cols-3"
                    >
                      <ToggleGroupItem value="custom">
                        {t("multigrid.customPattern") || "Motif"}
                      </ToggleGroupItem>
                      <ToggleGroupItem value="solid">
                        {t("multigrid.solidColor") || "Couleur unie"}
                      </ToggleGroupItem>
                      <ToggleGroupItem value="standard">
                        {t("multigrid.standardPattern") || "Mire standard"}
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>

                  {/* Options spécifiques au mode de motif sélectionné */}
                  {selectedGrid.activePatternMode === "custom" && (
                    <div className="space-y-2">
                      <Label>{t("config.pattern") || "Motif"}</Label>
                      <Select
                        value={selectedGrid.patternType}
                        onValueChange={(value) => {
                          updateGrid(selectedGrid.id, { patternType: value });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {patternTypes.map((pattern) => (
                            <SelectItem
                              key={pattern.value}
                              value={pattern.value}
                            >
                              {pattern.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Contrôles Snake Game */}
                  {selectedGrid.activePatternMode === "custom" && selectedGrid.patternType === "snakeGame" && (
                    <div className="space-y-4 border rounded-lg p-4 bg-muted/20">
                      <div className="space-y-2">
                        <Label className="text-lg font-semibold">🐍 Contrôles Snake Game</Label>
                        <div className="text-sm text-muted-foreground">
                          Joueur {selectedGrid.snakeControlSet}: {
                            selectedGrid.snakeControlSet === 1 ? "ZQSD" :
                            selectedGrid.snakeControlSet === 2 ? "Flèches directionnelles" :
                            selectedGrid.snakeControlSet === 3 ? "IKJL" :
                            "TGFH"
                          }
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            // Bouton individuel : TOUJOURS reset puis démarrer
                            multiSnake.resetSnakeGame(selectedGrid.id);
                            multiSnake.startSnakeGame(selectedGrid.id);
                            toast.success(t("snake.gameResetAndStarted") || "Snake Game réinitialisé et démarré!");
                          }}
                          disabled={selectedGrid.snakeGameState?.isPlaying}
                          size="sm"
                          variant="default"
                        >
                          <Play className="h-4 w-4 mr-1" />
                          {t("snake.start") || "Démarrer"}
                        </Button>
                        
                        <Button
                          onClick={() => multiSnake.stopSnakeGame(selectedGrid.id)}
                          disabled={!selectedGrid.snakeGameState?.isPlaying}
                          size="sm"
                          variant="secondary"
                        >
                          <Pause className="h-4 w-4 mr-1" />
                          {t("snake.stop") || "Arrêter"}
                        </Button>
                        
                        <Button
                          onClick={() => {
                            multiSnake.resetSnakeGame(selectedGrid.id);
                            toast.success(t("snake.gameReset") || "Snake Game réinitialisé!");
                          }}
                          size="sm"
                          variant="outline"
                        >
                          {t("snake.reset") || "Réinitialiser"}
                        </Button>
                      </div>
                      
                      {selectedGrid.snakeGameState && (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium">{t("snake.score") || "Score"}:</span> {selectedGrid.snakeGameState.score}
                          </div>
                          <div>
                            <span className="font-medium">{t("snake.state") || "État"}:</span> {
                              selectedGrid.snakeGameState.isPlaying ? t("snake.playing") || "En cours" :
                              selectedGrid.snakeGameState.isGameOver ? t("snake.gameOver") || "Game Over" :
                              selectedGrid.snakeGameState.isVictory ? t("snake.victory") || "Victoire!" :
                              t("snake.stopped") || "Arrêté"
                            }
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-muted-foreground">
                        <div><strong>{t("multigrid.multiplayerMode") || "Mode Multijoueur:"}</strong> {t("multigrid.multiplayerDesc") || "Créez plusieurs grilles avec le motif Snake Game pour jouer jusqu'à 4 joueurs simultanément!"}</div>
                      </div>
                    </div>
                  )}

                  {selectedGrid.activePatternMode === "solid" && (
                    <div className="space-y-2">
                      <Label htmlFor="gridColor">
                        {t("multigrid.bgColor") || "Couleur de fond"}
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="gridColor"
                          type="color"
                          value={selectedGrid.backgroundColor}
                          onChange={(e) => {
                            updateGrid(selectedGrid.id, {
                              backgroundColor: e.target.value,
                            });
                            renderCanvas(); // Mettre à jour immédiatement le canvas
                          }}
                          className="w-12 p-1 h-10"
                        />
                        <Input
                          type="text"
                          value={selectedGrid.backgroundColor}
                          onChange={(e) => {
                            updateGrid(selectedGrid.id, {
                              backgroundColor: e.target.value,
                            });
                            renderCanvas(); // Mettre à jour immédiatement le canvas
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {selectedGrid.activePatternMode === "standard" && (
                    <div className="space-y-2">
                      <Label htmlFor="standardPattern">
                        {t("config.standardPattern") || "Mire standard"}
                      </Label>
                      <Select
                        value={selectedGrid.standardPatternType}
                        onValueChange={(value) => {
                          updateGrid(selectedGrid.id, {
                            standardPatternType: value,
                          });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("config.choose") || "Choisir..."}
                          />
                        </SelectTrigger>
                        <SelectContent>

                          {/* Mires générales */}
                          <SelectItem value="liveVHScroll">
                            {t("pattern.liveVHScroll") || "Line V H Scroll"}
                          </SelectItem>
                          <SelectItem value="liveVScroll">
                            {t("pattern.liveVScroll") || "Line V Scroll"}
                          </SelectItem>
                          <SelectItem value="lineHScroll">
                            {t("pattern.lineHScroll") || "Line H Scroll"}
                          </SelectItem>
                          <SelectItem value="grayRamp">
                            {t("pattern.grayRamp") || "Dégradé de gris"}
                          </SelectItem>
                          <SelectItem value="redRamp">
                            {t("pattern.redRamp") || "Dégradé rouge"}
                          </SelectItem>
                          <SelectItem value="greenRamp">
                            {t("pattern.greenRamp") || "Dégradé vert"}
                          </SelectItem>
                          <SelectItem value="blueRamp">
                            {t("pattern.blueRamp") || "Dégradé bleu"}
                          </SelectItem>
                          <SelectItem value="whiteRamp">
                            {t("pattern.whiteRamp") || "Dégradé blanc"}
                          </SelectItem>
                          <SelectItem value="movingBall">
                            {t("pattern.movingBall") || "Balle en mouvement"}
                          </SelectItem>
                          <SelectItem value="colorBar">
                            {t("pattern.colorBar") || "Barres de couleur"}
                          </SelectItem>
                          <SelectItem value="grayBar8">
                            {t("pattern.grayBar8") || "Barres grises 8"}
                          </SelectItem>
                          <SelectItem value="grayBar16">
                            {t("pattern.grayBar16") || "Barres grises 16"}
                          </SelectItem>
                          <SelectItem value="smpteBar">
                            {t("pattern.smpteBar") || "Barres SMPTE"}
                          </SelectItem>
                          
                          {/* Écrans de couleur */}
                          <SelectItem value="redScreen">
                            {t("pattern.redScreen") || "Écran rouge"}
                          </SelectItem>
                          <SelectItem value="blueScreen">
                            {t("pattern.blueScreen") || "Écran bleu"}
                          </SelectItem>
                          <SelectItem value="greenScreen">
                            {t("pattern.greenScreen") || "Écran vert"}
                          </SelectItem>
                          <SelectItem value="cyanScreen">
                            {t("pattern.cyanScreen") || "Écran cyan"}
                          </SelectItem>
                          <SelectItem value="yellowScreen">
                            {t("pattern.yellowScreen") || "Écran jaune"}
                          </SelectItem>
                          <SelectItem value="blackScreen">
                            {t("pattern.blackScreen") || "Écran noir"}
                          </SelectItem>
                          <SelectItem value="whiteScreen">
                            {t("pattern.whiteScreen") || "Écran blanc"}
                          </SelectItem>
                          
                          {/* Motifs géométriques */}
                          <SelectItem value="verticalSplit">
                            {t("pattern.verticalSplit") || "Séparation verticale"}
                          </SelectItem>
                          <SelectItem value="horizontalSplit">
                            {t("pattern.horizontalSplit") || "Séparation horizontale"}
                          </SelectItem>
                          <SelectItem value="quarterBlock">
                            {t("pattern.quarterBlock") || "Bloc en quart"}
                          </SelectItem>
                          <SelectItem value="alternateCheckboard">
                            {t("pattern.alternateCheckboard") || "Damier alterné"}
                          </SelectItem>
                          
                          {/* MIRES SPÉCIFIQUES VIDÉOPROJECTEURS */}
                          <SelectItem value="pluge">
                            {t("pattern.pluge") || "PLUGE (Niveau noir)"}
                          </SelectItem>
                          <SelectItem value="zonePlate">
                            {t("pattern.zonePlate") || "Zone Plate (Convergence)"}
                          </SelectItem>
                          <SelectItem value="crosshatch">
                            {t("pattern.crosshatch") || "Crosshatch (Grille convergence)"}
                          </SelectItem>
                          <SelectItem value="focusPattern">
                            {t("pattern.focusPattern") || "Focus Pattern (Netteté)"}
                          </SelectItem>
                          <SelectItem value="geometricDistortion">
                            {t("pattern.geometricDistortion") || "Distorsion géométrique"}
                          </SelectItem>
                          <SelectItem value="windowPattern">
                            {t("pattern.windowPattern") || "Window Pattern (Positionnement)"}
                          </SelectItem>
                          <SelectItem value="multiburst">
                            {t("pattern.multiburst") || "Multiburst (Résolution H)"}
                          </SelectItem>
                          <SelectItem value="gamma22">
                            {t("pattern.gamma22") || "Gamma 2.2 Ramp"}
                          </SelectItem>
                          <SelectItem value="whiteUniformity">
                            {t("pattern.whiteUniformity") || "Uniformité blanc"}
                          </SelectItem>
                          <SelectItem value="blackUniformity">
                            {t("pattern.blackUniformity") || "Uniformité noir"}
                          </SelectItem>
                          <SelectItem value="ansiCheckerboard">
                            {t("pattern.ansiCheckerboard") || "ANSI Checkerboard"}
                          </SelectItem>
                          <SelectItem value="colorTemperature">
                            {t("pattern.colorTemperature") || "Température couleur"}
                          </SelectItem>
                          <SelectItem value="horizontalResolution">
                            {t("pattern.horizontalResolution") || "Résolution horizontale"}
                          </SelectItem>
                          <SelectItem value="verticalResolution">
                            {t("pattern.verticalResolution") || "Résolution verticale"}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  </>)}

                  <div className="space-y-2">
                    <Label>
                      {t("config.animationSpeed") || "Vitesse d'animation"}
                    </Label>
                    <Slider
                      value={[selectedGrid.animationSpeed]}
                      onValueChange={(value) => {
                        updateGrid(selectedGrid.id, {
                          animationSpeed: value[0],
                        });
                        renderCanvas(); // Mettre à jour immédiatement le canvas
                      }}
                      max={5}
                      min={0.1}
                      step={0.1}
                    />
                    <div className="text-sm text-muted-foreground text-center">
                      {selectedGrid.animationSpeed.toFixed(1)}x
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      {t("config.sonarSpeed") || "Vitesse du sonar"}
                    </Label>
                    <Slider
                      value={[selectedGrid.sonarSpeed]}
                      onValueChange={(value) => {
                        updateGrid(selectedGrid.id, { sonarSpeed: value[0] });
                        renderCanvas(); // Mettre à jour immédiatement le canvas
                      }}
                      max={5}
                      min={0.1}
                      step={0.1}
                    />
                    <div className="text-sm text-muted-foreground text-center">
                      {selectedGrid.sonarSpeed.toFixed(1)}x
                    </div>
                  </div>

                  {/* Paramètres d'affichage */}
                  <div className="space-y-4 pt-2 border-t">
                    <Label className="text-sm font-medium">
                      {t("config.display") || "Affichage"}
                    </Label>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="showGrid"
                        checked={selectedGrid.showGrid}
                        onCheckedChange={(checked) => {
                          updateGrid(selectedGrid.id, { showGrid: checked });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                      />
                      <Label htmlFor="showGrid">
                        {t("config.showGrid") || "Afficher la grille"}
                      </Label>
                    </div>

                    {/* Logo VisualGrid */}
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="showDefaultLogo"
                        checked={selectedGrid.showDefaultLogo}
                        onCheckedChange={(checked) => {
                          updateGrid(selectedGrid.id, { showDefaultLogo: checked });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                      />
                      <Label htmlFor="showDefaultLogo">
                        {t("config.showDefaultLogo") || "Afficher le logo VisualGrid"}
                      </Label>
                    </div>

                    {/* Slider pour l'échelle du logo */}
                    {selectedGrid.showDefaultLogo && (
                      <div className="space-y-2 pl-8">
                        <Label>
                          {t("multigrid.logoScale") || "Taille du logo"}
                        </Label>
                        <Slider
                          value={[selectedGrid.logoScale]}
                          onValueChange={(value) => {
                            updateGrid(selectedGrid.id, { logoScale: value[0] });
                            renderCanvas(); // Mettre à jour immédiatement le canvas
                          }}
                          max={3}
                          min={0.5}
                          step={0.1}
                        />
                        <div className="text-sm text-muted-foreground text-center">
                          {(selectedGrid.logoScale * 100).toFixed(0)}%
                        </div>
                      </div>
                    )}

                    {/* Pixel Map Mode (Pro only) */}
                    {isPro && (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="pixelMapMode"
                            checked={selectedGrid.pixelMapMode?.enabled || false}
                            onCheckedChange={(checked) => {
                              updateGrid(selectedGrid.id, {
                                pixelMapMode: {
                                  ...(selectedGrid.pixelMapMode || {
                                    enabled: false,
                                    mainScreenText: selectedGrid.name,
                                    posX: selectedGrid.x,
                                    posY: selectedGrid.y,
                                    resW: selectedGrid.width,
                                    resH: selectedGrid.height,
                                    panelW: selectedGrid.dalleConfigMode === "custom" && selectedGrid.dalleWidth > 0 ? selectedGrid.dalleWidth : selectedGrid.gridSize,
                                    panelH: selectedGrid.dalleConfigMode === "custom" && selectedGrid.dalleHeight > 0 ? selectedGrid.dalleHeight : selectedGrid.gridSize,
                                    size: 1.0
                                  }),
                                  enabled: checked,
                                  mainScreenText: selectedGrid.name,
                                  posX: selectedGrid.x,
                                  posY: selectedGrid.y,
                                  resW: selectedGrid.width,
                                  resH: selectedGrid.height,
                                  panelW: selectedGrid.dalleConfigMode === "custom" && selectedGrid.dalleWidth > 0 ? selectedGrid.dalleWidth : selectedGrid.gridSize,
                                  panelH: selectedGrid.dalleConfigMode === "custom" && selectedGrid.dalleHeight > 0 ? selectedGrid.dalleHeight : selectedGrid.gridSize,
                                },
                                showDefaultLogo: checked ? false : selectedGrid.showDefaultLogo,
                                showName: checked ? false : selectedGrid.showName,
                              });
                              renderCanvas(); // Mettre à jour immédiatement le canvas
                            }}
                          />
                          <Label htmlFor="pixelMapMode" className="text-white flex items-center gap-1">
                            {t("config.pixelMapMode") || "Pixel Map Mode"}
                          </Label>
                        </div>

                        {selectedGrid.pixelMapMode?.enabled && (
                          <div className="pl-4 space-y-2 border-l border-border">
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs text-muted-foreground">{t("config.pixelMapMode.size") || "Taille"}</Label>
                                <span className="text-xs text-muted-foreground">
                                  {Math.round((selectedGrid.pixelMapMode.size ?? 1.0) * 100)}%
                                </span>
                              </div>
                              <Slider
                                value={[selectedGrid.pixelMapMode.size ?? 1.0]}
                                onValueChange={(value) => {
                                  updateGrid(selectedGrid.id, {
                                    pixelMapMode: {
                                      ...selectedGrid.pixelMapMode!,
                                      size: value[0],
                                    },
                                  });
                                  renderCanvas();
                                }}
                                min={0.2}
                                max={5.0}
                                step={0.1}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sonar */}
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="showSonar"
                        checked={selectedGrid.showSonar}
                        onCheckedChange={(checked) => {
                          updateGrid(selectedGrid.id, { showSonar: checked });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                      />
                      <Label htmlFor="showSonar">
                        {t("config.showSonar") || "Afficher le sonar"}
                      </Label>
                    </div>

                    {/* Sonar Avancé (Mode Révélation) */}
                    {selectedGrid.showSonar && (
                      <div className="flex items-center space-x-2 pl-8">
                        <Switch
                          id="sonarRevealMode"
                          checked={selectedGrid.sonarRevealMode}
                          onCheckedChange={(checked) => {
                            updateGrid(selectedGrid.id, { sonarRevealMode: checked });
                            renderCanvas(); // Mettre à jour immédiatement le canvas
                          }}
                        />
                        <Label htmlFor="sonarRevealMode">
                          {t("config.sonarMode") || "Mode Sonar Avancé"}
                        </Label>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="showName"
                        checked={selectedGrid.showName}
                        onCheckedChange={(checked) => {
                          updateGrid(selectedGrid.id, { showName: checked });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                      />
                      <Label htmlFor="showName">
                        {t("multigrid.showName") || "Afficher le nom"}
                      </Label>
                    </div>

                    {/* Slider pour l'échelle du texte */}
                    {selectedGrid.showName && (
                      <div className="space-y-2 pl-8">
                        <Label>
                          {t("multigrid.textScale") || "Taille du texte"}
                        </Label>
                        <Slider
                          value={[selectedGrid.textScale]}
                          onValueChange={(value) => {
                            updateGrid(selectedGrid.id, { textScale: value[0] });
                            renderCanvas(); // Mettre à jour immédiatement le canvas
                          }}
                          max={3}
                          min={0.5}
                          step={0.1}
                        />
                        <div className="text-sm text-muted-foreground text-center">
                          {(selectedGrid.textScale * 100).toFixed(0)}%
                        </div>
                        {/* Bouton pour réinitialiser la position du nom */}
                        {(selectedGrid.nameOffsetX !== undefined || selectedGrid.nameOffsetY !== undefined) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => {
                              updateGrid(selectedGrid.id, { nameOffsetX: undefined, nameOffsetY: undefined });
                              renderCanvas();
                            }}
                          >
                            {t("multigrid.resetNamePosition") || "Réinitialiser la position"}
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Show Dimensions Toggle */}
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="showDimensions"
                        checked={selectedGrid.showDimensions}
                        onCheckedChange={(checked) => {
                          updateGrid(selectedGrid.id, { showDimensions: checked });
                          renderCanvas();
                        }}
                      />
                      <Label htmlFor="showDimensions">
                        {t("config.showResolution") || "Show Resolution"}
                      </Label>
                    </div>

                    {/* Dimensions Font Size Slider */}
                    {selectedGrid.showDimensions && (
                      <div className="space-y-2 pl-8">
                        <Label>
                          {t("config.resolutionFontSize") || "Resolution Size"}
                        </Label>
                        <Slider
                          value={[selectedGrid.dimensionsFontSize || Math.max(14, Math.min(20, selectedGrid.width / 25))]}
                          onValueChange={(value) => {
                            updateGrid(selectedGrid.id, { dimensionsFontSize: value[0] });
                            renderCanvas();
                          }}
                          min={12}
                          max={72}
                          step={1}
                        />
                        <div className="text-sm text-muted-foreground text-center">
                          {selectedGrid.dimensionsFontSize || Math.max(14, Math.min(20, selectedGrid.width / 25))}px
                        </div>
                      </div>
                    )}

                  </div>

                  {/* Configuration de la grille (Simple/Custom) */}
                  <div className="space-y-4 pt-2 border-t">
                    <Label className="text-sm font-medium">
                      {t("config.gridConfigTitle") ||
                        "Configuration de la Grille"}
                    </Label>
                    <ToggleGroup
                      type="single"
                      value={selectedGrid.dalleConfigMode}
                      onValueChange={(value: "simple" | "custom") => {
                        if (value) {
                          updateGrid(selectedGrid.id, {
                            dalleConfigMode: value,
                          });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }
                      }}
                      className="grid grid-cols-2"
                    >
                      <ToggleGroupItem value="simple">
                        {t("config.simple") || "Simple"}
                      </ToggleGroupItem>
                      <ToggleGroupItem value="custom">
                        {t("config.custom") || "Custom"}
                      </ToggleGroupItem>
                    </ToggleGroup>

                    {selectedGrid.dalleConfigMode === "simple" && (
                      <div className="space-y-2">
                        <Label htmlFor="gridSize">
                          {t("config.gridSize") || "Taille de grille (px)"}
                        </Label>
                        <MathInput
                          id="gridSize"
                          type="number"
                          value={selectedGrid.gridSize}
                          onValueChange={(value) => {
                            updateGrid(selectedGrid.id, { gridSize: value });
                            renderCanvas(); // Mettre à jour immédiatement le canvas
                          }}
                          aria-label={t("config.gridSize") || "Taille de grille"}
                        />
                      </div>
                    )}

                    {selectedGrid.dalleConfigMode === "custom" && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label htmlFor="dalleWidth">
                            {t("config.dalleWidth") || "Largeur Dalle"}
                          </Label>
                          <MathInput
                            id="dalleWidth"
                            type="number"
                            value={selectedGrid.dalleWidth}
                            onValueChange={(value) => {
                              updateGrid(selectedGrid.id, { dalleWidth: value });
                              renderCanvas(); // Mettre à jour immédiatement le canvas
                            }}
                            placeholder="px"
                            aria-label={t("config.dalleWidth") || "Largeur de la dalle"}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dalleHeight">
                            {t("config.dalleHeight") || "Hauteur Dalle"}
                          </Label>
                          <MathInput
                            id="dalleHeight"
                            type="number"
                            value={selectedGrid.dalleHeight}
                            onValueChange={(value) => {
                              updateGrid(selectedGrid.id, {
                                dalleHeight: value,
                              });
                              renderCanvas(); // Mettre à jour immédiatement le canvas
                            }}
                            placeholder="px"
                            aria-label={t("config.dalleHeight") || "Hauteur de la dalle"}
                          />
                        </div>
                      </div>
                    )}

                    {/* Épaisseur des bordures */}
                    <div className="space-y-2">
                      <Label htmlFor="borderWidth">
                        {t("config.borderWidth") ||
                          "Épaisseur des bordures (px)"}
                      </Label>
                      <MathInput
                        id="borderWidth"
                        type="number"
                        min="0"
                        max="10"
                        value={selectedGrid.borderWidth}
                        onValueChange={(value) => {
                          updateGrid(selectedGrid.id, {
                            borderWidth: Math.max(0, Math.min(10, value)),
                          });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                        aria-label={t("config.borderWidth") || "Épaisseur des bordures"}
                      />
                    </div>

                    {/* Anamorphose */}
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="enableAnamorphosis"
                        checked={selectedGrid.enableAnamorphosis}
                        onCheckedChange={(checked) => {
                          updateGrid(selectedGrid.id, { enableAnamorphosis: checked });
                          renderCanvas(); // Mettre à jour immédiatement le canvas
                        }}
                      />
                      <Label htmlFor="enableAnamorphosis" className="text-white">
                        {t("config.anamorphose") || "Anamorphose"}
                      </Label>
                    </div>
                  </div>

                  {/* Panneau Gestion des Logos */}
                  <div className="space-y-3 pt-4 border-t">
                    <Label className="text-sm font-medium">{t("logo.title") || "Logos"}</Label>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const img = new window.Image();
                        img.onload = () => {
                          const id = Date.now().toString();
                          // Calculer les dimensions en préservant le ratio d'aspect
                          const maxW = selectedGrid.width / 4;
                          const maxH = selectedGrid.height / 4;
                          const aspect = img.width / img.height;
                          
                          let width = Math.min(img.width, maxW);
                          let height = width / aspect;
                          
                          // Si la hauteur dépasse la limite, ajuster à partir de la hauteur
                          if (height > maxH) {
                            height = maxH;
                            width = height * aspect;
                          }
                          
                          width = Math.max(1, Math.round(width));
                          height = Math.max(1, Math.round(height));
                          
                          const logo: LogoElement = { id, image: img, x: selectedGrid.x + (selectedGrid.width - width)/2, y: selectedGrid.y + (selectedGrid.height - height)/2, width, height, originalWidth: img.width, originalHeight: img.height, opacity: 1 };
                          setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, logoElements: [...(g.logoElements || []), logo] }));
                          setSelectedLogoId(id);
                          renderCanvas();
                          // Réinitialiser la valeur de l'input pour permettre de réimporter le même fichier
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        };
                        img.src = ev.target?.result as string;
                      };
                      reader.onloadend = () => {
                        // Sécurité: s'assurer que l'input est vidé même si onload n'est pas appelé
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      };
                      reader.readAsDataURL(file);
                    }} />
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">{t("logo.import") || "Importer un logo"}</Button>
                    </div>
                    {(selectedGrid.logoElements?.length || 0) > 0 && (
                      <div className="space-y-2">
                        {selectedGrid.logoElements!.map(lg => (
                          <div key={lg.id} className="flex items-center gap-2">
                            <Button size="sm" variant={selectedLogoId === lg.id ? 'default' : 'outline'} className="flex-1 justify-start" onClick={() => {
                              // Si le logo est déjà sélectionné, le dé-sélectionner
                              if (selectedLogoId === lg.id) {
                                setSelectedLogoId(null);
                              } else {
                                setSelectedLogoId(lg.id);
                              }
                            }}>Logo {lg.id.slice(-4)}</Button>
                            <Button size="sm" variant="outline" onClick={() => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, logoElements: (g.logoElements || []).filter(x => x.id !== lg.id) })); if (selectedLogoId === lg.id) setSelectedLogoId(null); renderCanvas(); }}><Trash2 className="w-3 h-3"/></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedLogoId && selectedGrid.logoElements?.find(l => l.id === selectedLogoId) && (
                      <div className="space-y-2 border-t pt-3">
                        {(() => { const lg = selectedGrid.logoElements!.find(l => l.id === selectedLogoId)!; return (
                          <>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label>{t("logo.width") || "Largeur"}</Label>
                                <Input
                                  type="number"
                                  value={tempLogoSizeInputs.width !== undefined ? tempLogoSizeInputs.width : Math.round(lg.width)}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setTempLogoSizeInputs(prev => ({ ...prev, width: value }));

                                    const numValue = parseInt(value);

                                    if (value === "" || isNaN(numValue) || numValue < 1) {
                                      setLogoSizeErrors(prev => ({
                                        ...prev,
                                        width: (t("logo.widthMin") || "Minimum 1 requis")
                                      }));
                                      return;
                                    }

                                    // Valeur valide - appliquer et nettoyer
                                    setLogoSizeErrors(prev => ({ ...prev, width: undefined }));
                                    setTempLogoSizeInputs(prev => ({ ...prev, width: undefined }));
                                    const w = numValue;
                                    const aspectRatio = lg.originalHeight > 0 ? lg.originalWidth / lg.originalHeight : 1;
                                    const h = Math.round(w / aspectRatio);
                                    setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, logoElements: g.logoElements!.map(x => x.id === lg.id ? { ...x, width: w, height: h } : x) }));
                                    renderCanvas();
                                  }}
                                />
                                {logoSizeErrors.width && <p className="text-xs text-red-500 mt-1">{logoSizeErrors.width}</p>}
                              </div>
                              <div>
                                <Label>{t("logo.height") || "Hauteur"}</Label>
                                <Input
                                  type="number"
                                  value={tempLogoSizeInputs.height !== undefined ? tempLogoSizeInputs.height : Math.round(lg.height)}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setTempLogoSizeInputs(prev => ({ ...prev, height: value }));

                                    const numValue = parseInt(value);

                                    if (value === "" || isNaN(numValue) || numValue < 1) {
                                      setLogoSizeErrors(prev => ({
                                        ...prev,
                                        height: (t("logo.heightMin") || "Minimum 1 requis")
                                      }));
                                      return;
                                    }

                                    // Valeur valide - appliquer et nettoyer
                                    setLogoSizeErrors(prev => ({ ...prev, height: undefined }));
                                    setTempLogoSizeInputs(prev => ({ ...prev, height: undefined }));
                                    const h = numValue;
                                    const aspectRatio = lg.originalHeight > 0 ? lg.originalWidth / lg.originalHeight : 1;
                                    const w = Math.round(h * aspectRatio);
                                    setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, logoElements: g.logoElements!.map(x => x.id === lg.id ? { ...x, height: h, width: w } : x) }));
                                    renderCanvas();
                                  }}
                                />
                                {logoSizeErrors.height && <p className="text-xs text-red-500 mt-1">{logoSizeErrors.height}</p>}
                              </div>
                            </div>
                            <div>
                              <Label>{t("logo.opacity") || "Opacité"}</Label>
                              <Slider value={[lg.opacity]} min={0} max={1} step={0.1} onValueChange={(v) => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, logoElements: g.logoElements!.map(x => x.id === lg.id ? { ...x, opacity: v[0] } : x) })); renderCanvas(); }} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label>{t("logo.positionX") || t("multigrid.positionX") || "Position X (px)"}</Label>
                                <Input type="number" value={Math.round(lg.x)} onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  const clamped = Math.max(selectedGrid.x, Math.min(selectedGrid.x + selectedGrid.width - lg.width, val));
                                  setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, logoElements: g.logoElements!.map(x => x.id === lg.id ? { ...x, x: clamped } : x) }));
                                  renderCanvas();
                                }} />
                              </div>
                              <div>
                                <Label>{t("logo.positionY") || t("multigrid.positionY") || "Position Y (px)"}</Label>
                                <Input type="number" value={Math.round(lg.y)} onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  const clamped = Math.max(selectedGrid.y, Math.min(selectedGrid.y + selectedGrid.height - lg.height, val));
                                  setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, logoElements: g.logoElements!.map(x => x.id === lg.id ? { ...x, y: clamped } : x) }));
                                  renderCanvas();
                                }} />
                              </div>
                            </div>
                          </>
                        )})()}
                      </div>
                    )}
                  </div>

                  {/* Panneau Gestion des Textes */}
                  <div className="space-y-3 pt-4 border-t">
                    <Label className="text-sm font-medium">{t("text.elements") || "Textes"}</Label>
                    <Button variant="outline" className="w-full" onClick={() => {
                      const id = Date.now().toString();
                      const newText: TextElement = { id, text: t("text.new") || 'Nouveau texte', x: selectedGrid.x + 150, y: selectedGrid.y + 50, fontSize: 48, fontFamily: 'Arial, sans-serif', fontWeight: 400, fontStyle: 'normal', textAlign: 'center', color: '#ffffff', strokeWidth: 0 };
                      setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: [...(g.textElements || []), newText] }));
                      setSelectedTextId(id);
                      renderCanvas();
                    }}>{t("text.add") || "Ajouter un texte"}</Button>
                    {(selectedGrid.textElements?.length || 0) > 0 && (
                      <div className="space-y-2">
                        {selectedGrid.textElements!.map(tx => (
                          <div key={tx.id} className="flex items-center gap-2">
                            <Button size="sm" variant={selectedTextId === tx.id ? 'default' : 'outline'} className="flex-1 justify-start" onClick={() => {
                              // Si le texte est déjà sélectionné, le dé-sélectionner
                              if (selectedTextId === tx.id) {
                                setSelectedTextId(null);
                              } else {
                                setSelectedTextId(tx.id);
                              }
                            }}>{tx.text.substring(0,15)}...</Button>
                            <Button size="sm" variant="outline" onClick={() => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: (g.textElements || []).filter(x => x.id !== tx.id) })); if (selectedTextId === tx.id) setSelectedTextId(null); renderCanvas(); }}><Trash2 className="w-3 h-3"/></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {selectedTextId && selectedGrid.textElements?.find(t => t.id === selectedTextId) && (
                      <div className="space-y-2 border-t pt-3">
                        {(() => { const textElem = selectedGrid.textElements!.find(tt => tt.id === selectedTextId)!; return (
                          <>
                            <div className="space-y-2">
                              <Label>{t("text.text") || "Texte"}</Label>
                              <Input type="text" value={textElem.text} onChange={(e) => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, text: e.target.value } : x) })); renderCanvas(); }} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label>{t("text.size") || "Taille"}</Label>
                                <Input type="number" value={textElem.fontSize} onChange={(e) => { const val = Math.max(1, parseInt(e.target.value) || 1); setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, fontSize: val } : x) })); renderCanvas(); }} />
                              </div>
                              <div>
                                <Label>{t("text.color") || "Couleur"}</Label>
                                <Input type="color" value={textElem.color} onChange={(e) => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, color: e.target.value } : x) })); renderCanvas(); }} />
                              </div>
                            </div>
                            <div>
                              <Label>{t("text.font") || "Police"}</Label>
                              <Select value={textElem.fontFamily} onValueChange={(v) => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, fontFamily: v } : x) })); renderCanvas(); }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {fontFamilies.map(f => <SelectItem key={f.name} value={f.value}>{f.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>{t("text.weight") || "Graisse"}</Label>
                              <Slider value={[textElem.fontWeight]} min={100} max={1000} step={50} onValueChange={(v) => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, fontWeight: v[0] } : x) })); renderCanvas(); }} />
                            </div>
                            <div>
                              <Label>{t("text.style") || "Style"}</Label>
                              <Select value={textElem.fontStyle} onValueChange={(v) => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, fontStyle: v } : x) })); renderCanvas(); }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {fontStyles.map(fs => <SelectItem key={fs.value} value={fs.value}>{fs.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>{t("text.alignment") || "Alignement"}</Label>
                              <Select value={textElem.textAlign} onValueChange={(v) => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, textAlign: v } : x) })); renderCanvas(); }}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {textAlignments.map(a => <SelectItem key={a.value} value={a.value}>{a.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>{t("text.strokeWidth") || "Contour"}</Label>
                              <Slider value={[textElem.strokeWidth || 0]} min={0} max={10} step={1} onValueChange={(v) => { setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, strokeWidth: v[0] } : x) })); renderCanvas(); }} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label>{t("multigrid.positionX") || "X"}</Label>
                                <Input type="number" value={Math.round(textElem.x)} onChange={(e) => { const val = parseInt(e.target.value) || 0; const clamped = Math.max(selectedGrid.x, Math.min(selectedGrid.x + selectedGrid.width, val)); setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, x: clamped } : x) })); renderCanvas(); }} />
                              </div>
                              <div>
                                <Label>{t("multigrid.positionY") || "Y"}</Label>
                                <Input type="number" value={Math.round(textElem.y)} onChange={(e) => { const val = parseInt(e.target.value) || 0; const clamped = Math.max(selectedGrid.y, Math.min(selectedGrid.y + selectedGrid.height - (textElem.fontSize || 24), val)); setGrids(prev => prev.map(g => g.id !== selectedGrid.id ? g : { ...g, textElements: g.textElements!.map(x => x.id === textElem.id ? { ...x, y: clamped } : x) })); renderCanvas(); }} />
                              </div>
                            </div>
                          </>
                        )})()}
                      </div>
                    )}
                  </div>

                  <div className="pt-2">
                    <p className="text-sm text-muted-foreground">
                      {t("multigrid.dragTip") ||
                        "Conseil: Vous pouvez déplacer la grille en la faisant glisser sur le canvas."}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          )}

          {/* Espace vide pour éviter que le contenu ne soit coupé lors du défilement */}
          {!isExternalDisplay && (
          <div className="h-24 mt-4"></div>
          )}

          {/* Zone de rendu du canvas - fixée à droite */}
          <div 
            className={`flex-1 flex flex-col ${isExternalDisplay ? 'fixed inset-0 z-50' : ''}`}
            style={{ 
              ...(isExternalDisplay ? {} : {
                position: "fixed", 
                top: "4rem", 
                bottom: 0, 
                right: 0, 
                width: "calc(100% - 24rem)", 
                overflow: "hidden"
              })
            }}
          >
            {/* Barre de contrôles sticky - masquée en mode externe */}
            {!isExternalDisplay && (
            <div className="w-full sticky top-0 z-10 flex justify-between items-center gap-2 p-2 border-b border-gray-700 bg-background/80 backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <Switch
                  id="snap-switch"
                  checked={snapEnabled}
                  onCheckedChange={setSnapEnabled}
                />
                <Label htmlFor="snap-switch">
                  {t("multigrid.snap") || "Alignement"}
                </Label>
              </div>
              {selectedGridId && (
                <div>
                  <span className="text-xs text-muted-foreground">
                    {t("multigrid.arrowTip") ||
                      "Utilisez les flèches pour déplacer la grille (Shift=10px, Ctrl=5px)"}
                  </span>
                </div>
              )}
            </div>
            )}

            {/* Zone de canvas */}
            <div
              className={`flex-1 flex items-center justify-center ${isExternalDisplay ? 'p-0' : 'p-4'} overflow-hidden`}
              style={{ 
                height: isExternalDisplay ? "100vh" : "calc(100vh - 8rem)",
                borderRadius: (isFullscreen || isExternalDisplay) ? "0" : "auto" // Force la suppression des coins arrondis du conteneur parent
              }}
            >
              <div
                ref={scrollContainerRef}
                className={`overflow-hidden bg-black relative w-full h-full ${isFullscreen || isExternalDisplay ? "fixed inset-0 z-50" : ""}`}
                style={{
                  ...(isFullscreen || isExternalDisplay
                    ? {
                        border: "none",
                        borderRadius: "0",
                      }
                    : {
                        border: "1px solid #444",
                        cursor: activeTool === 'hand' ? (panDragRef.current ? 'grabbing' : 'grab') : 'default',
                      }),
                }}
                onMouseDown={(e) => {
                  if (isFullscreen || isExternalDisplay) return;
                  
                  // Clic molette OU outil main avec clic gauche pour pan
                  if (e.button === 1 || (e.button === 0 && activeTool === 'hand')) {
                    e.preventDefault();
                    
                    panDragRef.current = {
                      startX: e.clientX,
                      startY: e.clientY,
                      startOffsetX: panOffset.x,
                      startOffsetY: panOffset.y
                    };
                    
                    const container = scrollContainerRef.current;
                    if (container) container.style.cursor = 'grabbing';
                  }
                }}
                onMouseMove={(e) => {
                  if (!isFullscreen && panDragRef.current) {
                    const dx = e.clientX - panDragRef.current.startX;
                    const dy = e.clientY - panDragRef.current.startY;
                    
                    setPanOffset({
                      x: panDragRef.current.startOffsetX + dx,
                      y: panDragRef.current.startOffsetY + dy
                    });
                  }
                }}
                onMouseUp={(e) => {
                  if (e.button === 1 || (e.button === 0 && activeTool === 'hand')) {
                    panDragRef.current = null;
                    const container = scrollContainerRef.current;
                    if (container) container.style.cursor = activeTool === 'hand' ? 'grab' : 'default';
                  }
                }}
                onMouseLeave={() => {
                  panDragRef.current = null;
                  const container = scrollContainerRef.current;
                  if (container) container.style.cursor = activeTool === 'hand' ? 'grab' : 'default';
                }}
                onWheel={(e) => {
                  if (!isFullscreen && e.ctrlKey) {
                    e.preventDefault();
                    const delta = -e.deltaY * 0.001;
                    setZoom((z) => Math.max(0.1, Math.min(5, z + delta)));
                  }
                }}
              >
                <div
                  ref={fullscreenContainerRef}
                  style={{
                    ...(isFullscreen || isExternalDisplay
                      ? {
                          transform: isExternalDisplay ? "scale(1)" : `scale(${zoom})`,
                          transformOrigin: "top left",
                        }
                      : {
                          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
                          transformOrigin: "center",
                        }),
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                <canvas
                  ref={(node) => {
                    // Callback ref pour détecter quand le canvas est monté
                    (canvasRef as React.MutableRefObject<HTMLCanvasElement | null>).current = node;
                    if (node && !canvasMounted) {
                      setCanvasMounted(true);
                    }
                  }}
                  className={`${isFullscreen || isExternalDisplay ? "" : "max-w-full h-auto"} ${isDragging ? "cursor-grabbing" : (isFullscreen || isExternalDisplay) ? "cursor-none" : activeTool === 'hand' ? "cursor-grab" : "cursor-pointer"}`}
                  style={{
                    maxHeight: (isFullscreen || isExternalDisplay) ? "none" : "calc(100vh - 12rem)",
                    objectFit: (isFullscreen || isExternalDisplay) && canvasConfig.stretchFullscreen ? "fill" : "contain",
                    width: (isFullscreen || isExternalDisplay) && canvasConfig.stretchFullscreen ? "100vw" : "auto",
                    height: (isFullscreen || isExternalDisplay) && canvasConfig.stretchFullscreen ? "100vh" : "auto",
                    transformOrigin: (isFullscreen || isExternalDisplay) ? "top left" : "center center",
                    display: "block",
                    margin: (isFullscreen || isExternalDisplay) ? "0" : "0 auto", // Pas de centrage en plein écran
                    position: (isFullscreen || isExternalDisplay) ? "absolute" : "static",
                    top: (isFullscreen || isExternalDisplay) ? "0" : "auto",
                    left: (isFullscreen || isExternalDisplay) ? "0" : "auto",
                    borderRadius: (isFullscreen || isExternalDisplay) ? "0" : "auto" // Force la suppression des coins arrondis du canvas en plein écran
                  }}
                  onClick={(e) => {
                    if (activeTool === 'hand') return; // Ne pas sélectionner avec l'outil main
                    
                    const canvas = canvasRef.current;
                    if (!canvas) return;

                    const rect = canvas.getBoundingClientRect();
                    // getBoundingClientRect tient déjà compte des transformations CSS (zoom + pan)
                    const canvasX = (e.clientX - rect.left) * (canvas.width / rect.width);
                    const canvasY = (e.clientY - rect.top) * (canvas.height / rect.height);

                    // Vérifier si on a cliqué sur une grille
                    const clickedGrid = [...grids]
                      .reverse()
                      .find(
                        (grid) =>
                          canvasX >= grid.x &&
                          canvasX <= grid.x + grid.width &&
                          canvasY >= grid.y &&
                          canvasY <= grid.y + grid.height,
                      );

                    if (clickedGrid) {
                      setSelectedGridId(clickedGrid.id);
                      // Redessiner immédiatement pour montrer la sélection
                      renderCanvas();
                    } else {
                      setSelectedGridId(null);
                      renderCanvas();
                    }
                  }}
                  onMouseDown={(e) => {
                    if (isFullscreen || isExternalDisplay) return; // Désactiver le drag en mode plein écran ou externe
                    if (activeTool === 'hand') return; // Ne pas interagir avec l'outil main
                    if (e.button !== 0) return; // Seul le clic gauche interagit avec les éléments

                    const canvas = canvasRef.current;
                    if (!canvas) return;

                    const rect = canvas.getBoundingClientRect();
                    // getBoundingClientRect tient déjà compte des transformations CSS (zoom + pan)
                    const canvasX = (e.clientX - rect.left) * (canvas.width / rect.width);
                    const canvasY = (e.clientY - rect.top) * (canvas.height / rect.height);

                    // Utiliser reverse() pour trouver la grille la plus en avant
                    const clickedGridOnMouseDown = [...grids]
                      .reverse()
                      .find(
                        (grid) =>
                          canvasX >= grid.x &&
                          canvasX <= grid.x + grid.width &&
                          canvasY >= grid.y &&
                          canvasY <= grid.y + grid.height,
                      );

                    if (clickedGridOnMouseDown) {
                      // Tentative de drag d'un texte en priorité
                      const texts = (clickedGridOnMouseDown.textElements || []).slice().reverse();
                      const ctx = canvas.getContext('2d');
                      if (ctx) {
                        for (const t of texts) {
                          ctx.font = `${t.fontStyle || 'normal'} ${(t.fontWeight || 400)} ${t.fontSize || 24}px ${t.fontFamily || 'Arial, sans-serif'}`;
                          const w = ctx.measureText(t.text).width; const h = t.fontSize || 24;
                          let x0 = t.x; if (t.textAlign === 'center') x0 = t.x - w/2; else if (t.textAlign === 'right') x0 = t.x - w;
                          if (canvasX >= x0 && canvasX <= x0 + w && canvasY >= t.y && canvasY <= t.y + h) {
                            setSelectedGridId(clickedGridOnMouseDown.id);
                            setSelectedTextId(t.id);
                            setSelectedLogoId(null);
                            elementDragRef.current = { type: 'text', gridId: clickedGridOnMouseDown.id, id: t.id, startMouseX: canvasX, startMouseY: canvasY, startX: t.x, startY: t.y };
                            setIsDragging(true);
                            return;
                          }
                        }
                      }
                      // Tentative de drag d'un logo si pas de texte
                      const logos = (clickedGridOnMouseDown.logoElements || []).slice().reverse();
                      for (const lg of logos) {
                        if (canvasX >= lg.x && canvasX <= lg.x + lg.width && canvasY >= lg.y && canvasY <= lg.y + lg.height) {
                          // Vérifier si on a cliqué sur la poignée de redimensionnement (en bas à droite)
                          const handleSize = 20;
                          const handleX = lg.x + lg.width;
                          const handleY = lg.y + lg.height;
                          const isOnHandle = canvasX >= handleX - handleSize && canvasX <= handleX && 
                                            canvasY >= handleY - handleSize && canvasY <= handleY;
                          
            if (isOnHandle && !isFullscreen && !isExternalDisplay) {
              // Commencer le redimensionnement
              setSelectedGridId(clickedGridOnMouseDown.id);
              setSelectedLogoId(lg.id);
              setSelectedTextId(null);
              const aspectRatio = lg.originalHeight > 0 ? lg.originalWidth / lg.originalHeight : 1;
              elementDragRef.current = {
                type: 'logo-resize',
                gridId: clickedGridOnMouseDown.id,
                id: lg.id,
                startMouseX: canvasX,
                startMouseY: canvasY,
                startX: lg.x,
                startY: lg.y,
                startWidth: lg.width,
                startHeight: lg.height,
                originalWidth: lg.originalWidth,
                originalHeight: lg.originalHeight
              };
              setIsDragging(true);
              return;
            } else {
                            // Drag normal du logo
                            setSelectedGridId(clickedGridOnMouseDown.id);
                            setSelectedLogoId(lg.id);
                            setSelectedTextId(null);
                            elementDragRef.current = { type: 'logo', gridId: clickedGridOnMouseDown.id, id: lg.id, startMouseX: canvasX, startMouseY: canvasY, startX: lg.x, startY: lg.y };
                            setIsDragging(true);
                            return;
                          }
                        }
                      }
                      
                      // Vérifier si on a cliqué sur le nom de la grille (si showName est activé)
                      if (clickedGridOnMouseDown.showName) {
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                          // Calculer exactement comme dans le rendu
                          const maxWidth = clickedGridOnMouseDown.width * 0.9;
                          const baseFontSize = Math.max(8, Math.min(32, clickedGridOnMouseDown.width / 15));
                          const fontSize = baseFontSize * clickedGridOnMouseDown.textScale;
                          
                          ctx.font = `bold ${fontSize}px Arial`;
                          ctx.textAlign = "center";
                          let textWidth = ctx.measureText(clickedGridOnMouseDown.name).width;
                          let finalFontSize = fontSize;
                          
                          // Si le texte est trop large, ajuster comme dans le rendu
                          if (textWidth > maxWidth) {
                            const scaleFactor = maxWidth / textWidth;
                            finalFontSize = Math.max(8, Math.floor(fontSize * scaleFactor));
                            ctx.font = `bold ${finalFontSize}px Arial`;
                            textWidth = ctx.measureText(clickedGridOnMouseDown.name).width;
                          }
                          
                          // Position du texte (identique au rendu) - utiliser les offsets relatifs
                          const nameX = clickedGridOnMouseDown.nameOffsetX !== undefined 
                            ? clickedGridOnMouseDown.x + clickedGridOnMouseDown.nameOffsetX 
                            : clickedGridOnMouseDown.x + clickedGridOnMouseDown.width / 2;
                          const nameY = clickedGridOnMouseDown.nameOffsetY !== undefined 
                            ? clickedGridOnMouseDown.y + clickedGridOnMouseDown.nameOffsetY 
                            : clickedGridOnMouseDown.y + clickedGridOnMouseDown.height * 0.65;
                          
                          // Calculer la zone de clic (avec une marge pour faciliter le clic)
                          const textHeight = finalFontSize * 1.5; // Hauteur avec marge pour faciliter le clic
                          const textLeft = nameX - textWidth / 2 - 5; // Marge de 5px de chaque côté
                          const textRight = nameX + textWidth / 2 + 5;
                          const textTop = nameY - textHeight / 2;
                          const textBottom = nameY + textHeight / 2;
                          
                          // Vérifier si le clic est dans la zone du texte
                          if (canvasX >= textLeft && canvasX <= textRight && 
                              canvasY >= textTop && canvasY <= textBottom) {
                            // Double-clic pour éditer, simple clic pour drag
                            if (e.detail === 2) {
                              // Double-clic : ouvrir le dialogue d'édition
                              e.preventDefault();
                              e.stopPropagation();
                              setEditingGridIdFromCanvas(clickedGridOnMouseDown.id);
                              setEditingGridName(clickedGridOnMouseDown.name);
                              setShowEditNameDialog(true);
                              return;
                            } else {
                              // Simple clic : commencer le drag du nom
                              setSelectedGridId(clickedGridOnMouseDown.id);
                              setSelectedTextId(null);
                              setSelectedLogoId(null);
                              elementDragRef.current = { 
                                type: 'name', 
                                gridId: clickedGridOnMouseDown.id, 
                                startMouseX: canvasX, 
                                startMouseY: canvasY, 
                                startX: nameX, 
                                startY: nameY 
                              };
                              setIsDragging(true);
                              return;
                            }
                          }
                        }
                      }
                      
                      // Si on clique sur une grille qui n'est pas sélectionnée, on la sélectionne d'abord
                      if (clickedGridOnMouseDown.id !== selectedGridId) {
                        setSelectedGridId(clickedGridOnMouseDown.id);
                      }

                      // Commencer le drag
                      draggedGridRef.current = clickedGridOnMouseDown.id;
                      dragOffsetRef.current = {
                        x: canvasX - clickedGridOnMouseDown.x,
                        y: canvasY - clickedGridOnMouseDown.y,
                      };
                      setIsDragging(true);
                    }
                  }}
                  onMouseMove={(e) => {
                    if (!isFullscreen && elementDragRef.current) {
                      const canvas = canvasRef.current; if (!canvas) return;
                      const rect = canvas.getBoundingClientRect();
                      const canvasX = (e.clientX - rect.left) * (canvas.width / rect.width);
                      const canvasY = (e.clientY - rect.top) * (canvas.height / rect.height);
                      const drag = elementDragRef.current;
                      setGrids(prev => {
                        const grid = prev.find(g => g.id === drag.gridId);
                        if (!grid) return prev;
                        const dx = canvasX - drag.startMouseX;
                        const dy = canvasY - drag.startMouseY;
                        if (drag.type === 'logo-resize') {
                          // Redimensionnement du logo en gardant le ratio d'aspect
                          const idx = grid.logoElements?.findIndex(l => l.id === drag.id) ?? -1;
                          if (idx < 0 || !drag.startWidth || !drag.startHeight || !drag.originalWidth || !drag.originalHeight) return prev;
                          const lg = grid.logoElements![idx];
                          
                          // Calculer le ratio d'aspect à partir des dimensions originales
                          const aspectRatio = drag.originalHeight > 0 ? drag.originalWidth / drag.originalHeight : 1;
                          
                          // Calculer la distance diagonale depuis le coin initial (poignée en bas à droite)
                          // Utiliser la moyenne de dx et dy pour un redimensionnement plus fluide
                          // ou utiliser la dimension qui a le plus changé pour plus de précision
                          const deltaX = dx;
                          const deltaY = dy;
                          
                          // Calculer la nouvelle taille en préservant le ratio d'aspect
                          // Utiliser la dimension qui a le plus changé pour déterminer la nouvelle taille
                          let newWidth: number;
                          let newHeight: number;
                          
                          if (Math.abs(deltaX) > Math.abs(deltaY)) {
                            // La largeur a plus changé, utiliser dx
                            newWidth = drag.startWidth + deltaX;
                            newHeight = newWidth / aspectRatio;
                          } else {
                            // La hauteur a plus changé, utiliser dy
                            newHeight = drag.startHeight + deltaY;
                            newWidth = newHeight * aspectRatio;
                          }
                          
                          // S'assurer que la taille minimale est respectée
                          newWidth = Math.max(10, newWidth);
                          newHeight = Math.max(10, newHeight);
                          
                          // Vérifier que le logo reste dans les limites de la grille
                          const maxWidth = grid.x + grid.width - lg.x;
                          const maxHeight = grid.y + grid.height - lg.y;
                          if (newWidth > maxWidth) {
                            newWidth = maxWidth;
                            newHeight = newWidth / aspectRatio;
                          }
                          if (newHeight > maxHeight) {
                            newHeight = maxHeight;
                            newWidth = newHeight * aspectRatio;
                          }
                          
                          return prev.map(g => g.id !== grid.id ? g : { 
                            ...g, 
                            logoElements: g.logoElements!.map(l => l.id === lg.id ? { 
                              ...l, 
                              width: Math.round(newWidth), 
                              height: Math.round(newHeight) 
                            } : l) 
                          });
                        } else if (drag.type === 'logo') {
                          const idx = grid.logoElements?.findIndex(l => l.id === drag.id) ?? -1;
                          if (idx < 0) return prev;
                          const lg = grid.logoElements![idx];
                          let newX = drag.startX + dx;
                          let newY = drag.startY + dy;
                          newX = Math.max(grid.x, Math.min(grid.x + grid.width - lg.width, newX));
                          newY = Math.max(grid.y, Math.min(grid.y + grid.height - lg.height, newY));
                          return prev.map(g => g.id !== grid.id ? g : { ...g, logoElements: g.logoElements!.map(l => l.id === lg.id ? { ...l, x: newX, y: newY } : l) });
                        } else if (drag.type === 'text') {
                          const idx = grid.textElements?.findIndex(t => t.id === drag.id) ?? -1;
                          if (idx < 0) return prev;
                          const t = grid.textElements![idx];
                          let newX = drag.startX + dx;
                          let newY = drag.startY + dy;
                          newX = Math.max(grid.x, Math.min(grid.x + grid.width, newX));
                          newY = Math.max(grid.y, Math.min(grid.y + grid.height - (t.fontSize || 24), newY));
                          return prev.map(g => g.id !== grid.id ? g : { ...g, textElements: g.textElements!.map(tt => tt.id === t.id ? { ...tt, x: newX, y: newY } : tt) });
                        } else if (drag.type === 'name') {
                          // Drag du nom de la grille - calculer la position absolue puis convertir en offset relatif
                          let newX = drag.startX + dx;
                          let newY = drag.startY + dy;
                          // Limiter la position à l'intérieur de la grille (avec une marge pour permettre le centrage)
                          // Calculer la largeur du texte pour les limites
                          const canvasEl = canvasRef.current;
                          if (canvasEl) {
                            const ctx = canvasEl.getContext('2d');
                            if (ctx) {
                              const maxWidth = grid.width * 0.9;
                              const baseFontSize = Math.max(8, Math.min(32, grid.width / 15));
                              const fontSize = baseFontSize * grid.textScale;
                              ctx.font = `bold ${fontSize}px Arial`;
                              const textWidth = ctx.measureText(grid.name).width;
                              const finalTextWidth = textWidth > maxWidth ? maxWidth : textWidth;
                              
                              // Limiter la position pour que le texte reste visible dans la grille
                              newX = Math.max(grid.x + finalTextWidth / 2, Math.min(grid.x + grid.width - finalTextWidth / 2, newX));
                              newY = Math.max(grid.y, Math.min(grid.y + grid.height, newY));
                            }
                          }
                          // Convertir en offsets relatifs à la grille
                          const nameOffsetX = newX - grid.x;
                          const nameOffsetY = newY - grid.y;
                          return prev.map(g => g.id !== grid.id ? g : { ...g, nameOffsetX, nameOffsetY });
                        }
                        return prev;
                      });
                      // Appeler renderCanvas pour mettre à jour immédiatement le canvas pendant le drag
                      requestAnimationFrame(() => renderCanvas());
                      return;
                    }
                    if (
                      !isFullscreen &&
                      isDragging &&
                      draggedGridRef.current &&
                      dragOffsetRef.current
                    ) {
                      const canvas = canvasRef.current;
                      if (!canvas) return;

                      const rect = canvas.getBoundingClientRect();
                      // getBoundingClientRect tient déjà compte des transformations CSS (zoom + pan)
                      const canvasX = (e.clientX - rect.left) * (canvas.width / rect.width);
                      const canvasY = (e.clientY - rect.top) * (canvas.height / rect.height);

                      setGrids((prev) => {
                        const currentGrid = prev.find(
                          (g) => g.id === draggedGridRef.current,
                        );
                        if (!currentGrid) return prev;

                        let newX = canvasX - dragOffsetRef.current!.x;
                        let newY = canvasY - dragOffsetRef.current!.y;

                        if (snapEnabled) {
                          const threshold = 8;
                          // Snap aux bords du canvas
                          if (Math.abs(newX) < threshold) newX = 0;
                          if (Math.abs(newY) < threshold) newY = 0;
                          if (
                            Math.abs(
                              newX + currentGrid.width - canvasConfig.width,
                            ) < threshold
                          )
                            newX = canvasConfig.width - currentGrid.width;
                          if (
                            Math.abs(
                              newY + currentGrid.height - canvasConfig.height,
                            ) < threshold
                          )
                            newY = canvasConfig.height - currentGrid.height;

                          const otherGrids = prev.filter(
                            (g) => g.id !== draggedGridRef.current,
                          );

                          for (const other of otherGrids) {
                            // Snap vertical edges
                            if (
                              Math.abs(newX - (other.x + other.width)) <
                              threshold
                            )
                              newX = other.x + other.width;
                            if (
                              Math.abs(newX + currentGrid.width - other.x) <
                              threshold
                            )
                              newX = other.x - currentGrid.width;
                            if (Math.abs(newX - other.x) < threshold)
                              newX = other.x;
                            if (
                              Math.abs(
                                newX +
                                  currentGrid.width -
                                  (other.x + other.width),
                              ) < threshold
                            )
                              newX = other.x + other.width - currentGrid.width;

                            // Snap horizontal edges
                            if (
                              Math.abs(newY - (other.y + other.height)) <
                              threshold
                            )
                              newY = other.y + other.height;
                            if (
                              Math.abs(newY + currentGrid.height - other.y) <
                              threshold
                            )
                              newY = other.y - currentGrid.height;
                            if (Math.abs(newY - other.y) < threshold)
                              newY = other.y;
                            if (
                              Math.abs(
                                newY +
                                  currentGrid.height -
                                  (other.y + other.height),
                              ) < threshold
                            )
                              newY =
                                other.y + other.height - currentGrid.height;
                          }
                        }

                        // Limiter aux bords du canvas
                        newX = Math.max(
                          0,
                          Math.min(
                            canvasConfig.width - currentGrid.width,
                            newX,
                          ),
                        );
                        newY = Math.max(
                          0,
                          Math.min(
                            canvasConfig.height - currentGrid.height,
                            newY,
                          ),
                        );

                        const roundedX = Math.round(newX);
                        const roundedY = Math.round(newY);

                        return prev.map((grid) => {
                          if (grid.id !== draggedGridRef.current) return grid;
                          const dx = roundedX - grid.x;
                          const dy = roundedY - grid.y;
                          return {
                            ...grid,
                            x: roundedX,
                            y: roundedY,
                            pixelMapMode: grid.pixelMapMode
                              ? {
                                  ...grid.pixelMapMode,
                                  posX: roundedX,
                                  posY: roundedY,
                                }
                              : grid.pixelMapMode,
                            textElements: grid.textElements?.map(te => ({
                              ...te,
                              x: te.x + dx,
                              y: te.y + dy,
                            })),
                            logoElements: grid.logoElements?.map(le => ({
                              ...le,
                              x: le.x + dx,
                              y: le.y + dy,
                            })),
                          };
                        });
                      });
                    }
                  }}
                  onMouseUp={() => {
                    elementDragRef.current = null;
                    draggedGridRef.current = null;
                    dragOffsetRef.current = null;
                    setIsDragging(false);
                  }}
                  onMouseLeave={() => {
                    elementDragRef.current = null;
                    draggedGridRef.current = null;
                    dragOffsetRef.current = null;
                    setIsDragging(false);
                  }}
                />
                
                {/* Message futuriste quand l'aperçu est désactivé */}
                {disablePreviewWhenExternal && isExternalDisplayConnected && !isMobile && !isExternalDisplay && (
                  <div className="absolute inset-0 flex items-center justify-center z-[5] bg-black bg-opacity-90 pointer-events-auto">
                    <FuturisticMessage
                      onActivate={() => setDisablePreviewWhenExternal(false)}
                    />
                  </div>
                )}
                </div>
              </div>

              {/* Contrôles flottants en bas - masqués sur l'affichage externe */}
              {!isExternalDisplay && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 bg-background/80 backdrop-blur-sm p-2 rounded-full shadow-lg z-50">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setIsAnimating((a) => !a)}
                  className="rounded-full"
                  title={t("multigrid.playPause") || "Lecture/Pause (animations)"}
                >
                  {isAnimating ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                </Button>

                {/* Bouton global Snake Games - uniquement visible s'il y a des grilles Snake */}
                {grids.some(grid => grid.patternType === 'snakeGame') && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={resetAndStartAllSnakeGames}
                    className="rounded-full bg-green-500/20 hover:bg-green-500/30 border-green-500/50"
                    title={t("snake.resetAndStartAll") || "Réinitialiser et démarrer tous les jeux Snake (Espace)"}
                  >
                    <Gamepad2 className="h-5 w-5 text-green-400" />
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleFullscreen}
                  className="rounded-full"
                  title={t("multigrid.fullscreen") || "Mode plein écran"}
                >
                  <Maximize className="h-5 w-5" />
                </Button>

                {/* Bouton Affichage Externe - masqué sur mobile */}
                {!isMobile && !isExternalDisplay && !externalDisplayWindow && (
                  <div className="flex">
                    <Button
                      onClick={openExternalDisplay}
                      variant="outline"
                      size="sm"
                      className={`border-gray-400 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 ${availableScreens.length > 1 ? 'rounded-r-none' : 'rounded'}`}
                    >
                      <ScreenShare className="w-4 h-4 mr-1" />
                      {t("external.button")}
                    </Button>
                    {availableScreens.length > 1 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-gray-400 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 rounded-l-none border-l-0 px-2"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {availableScreens.map((screen, index) => (
                            <DropdownMenuItem
                              key={screen.id}
                              onClick={() => setSelectedScreenIndex(index)}
                              className={selectedScreenIndex === index ? "bg-accent" : ""}
                            >
                              <Monitor className="w-4 h-4 mr-2" />
                              <div className="flex flex-col">
                                <span>{screen.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {screen.width}×{screen.height}
                                </span>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )}

                {/* Bouton de contrôle quand l'affichage externe est ouvert */}
                {!isExternalDisplay && externalDisplayWindow && (
                  !isExternalDisplayConnected ? (
                    <Button
                      onClick={openExternalDisplay}
                      variant="outline"
                      size="sm"
                      className="border-orange-400 text-orange-700 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-300 dark:hover:bg-orange-900/20"
                    >
                      <ScreenShare className="w-4 h-4 mr-1" />
                      {t("external.reconnecting")}
                    </Button>
                  ) : (
                    <Button
                      onClick={closeExternalDisplay}
                      variant="outline"
                      size="sm"
                      className="group relative overflow-hidden 
                         bg-emerald-900 border-emerald-600 text-emerald-100 shadow-lg
                         hover:border-red-500 hover:text-white 
                         transition-all duration-500 ease-out
                         before:absolute before:inset-0 before:bg-gradient-to-b 
                         before:from-white/20 before:via-transparent before:to-transparent
                         before:opacity-60
                         after:absolute after:inset-0 after:bg-red-700 after:z-0
                         after:transform after:-translate-x-full hover:after:translate-x-0
                         after:transition-transform after:duration-500 after:ease-out
                         backdrop-blur-sm"
                      style={{
                        boxShadow: '0 4px 15px rgba(6, 78, 59, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                      }}
                    >
                      <ScreenShare className="w-4 h-4 mr-1 text-emerald-200 group-hover:text-white transition-colors duration-300 relative z-10 group-hover:opacity-0" />
                      <span className="group-hover:opacity-0 transition-opacity duration-300 relative z-10">
                        {t("external.synchronized")}
                      </span>
                      <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                        <ScreenShareOff className="w-4 h-4 mr-1" />
                        {t("external.quit")}
                      </span>
                      {isExternalDisplayFullscreen && (
                        <div className="w-2 h-2 bg-emerald-300 rounded-full ml-2 group-hover:bg-white relative z-10" title="Plein écran actif"></div>
                      )}
                    </Button>
                  )
                )}

                {/* Bouton pour contrôler l'aperçu/performance quand l'affichage externe est actif */}
                {isExternalDisplayConnected && (
                  <Button
                    onClick={() => setDisablePreviewWhenExternal(!disablePreviewWhenExternal)}
                    variant="outline"
                    size="sm"
                    className={`group relative overflow-hidden transition-all duration-500 ease-out ${disablePreviewWhenExternal
                      ? "bg-yellow-600 border-yellow-500 text-white shadow-lg hover:border-blue-500 hover:text-white before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:via-transparent before:to-transparent before:opacity-60 after:absolute after:inset-0 after:bg-blue-600 after:z-0 after:transform after:-translate-x-full hover:after:translate-x-0 after:transition-transform after:duration-500 after:ease-out backdrop-blur-sm"
                      : "bg-blue-600 border-blue-500 text-white shadow-lg hover:border-yellow-500 hover:text-white before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:via-transparent before:to-transparent before:opacity-60 after:absolute after:inset-0 after:bg-yellow-600 after:z-0 after:transform after:-translate-x-full hover:after:translate-x-0 after:transition-transform after:duration-500 after:ease-out backdrop-blur-sm"
                      }`}
                    style={{
                      boxShadow: disablePreviewWhenExternal
                        ? '0 4px 15px rgba(202, 138, 4, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                        : '0 4px 15px rgba(37, 99, 235, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
                    }}
                  >
                    {disablePreviewWhenExternal ? (
                      <>
                        <Zap className="w-4 h-4 text-yellow-200 group-hover:text-white transition-colors duration-300 relative z-10 group-hover:opacity-0" />
                        <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                          <MonitorCog className="w-4 h-4" />
                        </span>
                      </>
                    ) : (
                      <>
                        <MonitorCog className="w-4 h-4 text-blue-200 group-hover:text-white transition-colors duration-300 relative z-10 group-hover:opacity-0" />
                        <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                          <Zap className="w-4 h-4" />
                        </span>
                      </>
                    )}
                  </Button>
                )}

                {/* Outils de navigation */}
                <div className="flex items-center gap-1 bg-background/80 rounded-full">
                  <Button
                    variant={activeTool === 'arrow' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setActiveTool('arrow')}
                    className="rounded-full"
                    title={t("multigrid.arrowTool") || "Outil flèche (sélection)"}
                  >
                    <MousePointer className="h-5 w-5" />
                  </Button>
                  <Button
                    variant={activeTool === 'hand' ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => setActiveTool('hand')}
                    className="rounded-full"
                    title={t("multigrid.handTool") || "Outil main (déplacement)"}
                  >
                    <Hand className="h-5 w-5" />
                  </Button>
                </div>

                {/* Boutons de zoom réorganisés */}
                <div className="flex items-center gap-1 bg-background/80 rounded-full">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setZoom((z) => Math.max(0.1, z - 0.1))}
                    className="rounded-full"
                  >
                    <ZoomOut className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setZoom(1);
                      setPanOffset({ x: 0, y: 0 });
                    }}
                    className="rounded-full px-2"
                    title={t("multigrid.resetZoom") || "Réinitialiser le zoom (100%)"}
                  >
                    {(zoom * 100).toFixed(0)}%
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setZoom((z) => Math.min(5, z + 0.1))}
                    className="rounded-full"
                  >
                    <ZoomIn className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Export Video Dialog - WebCodecs GPU Export */}
        <FfmpegExportDialog
          open={showExportDialog}
          onOpenChange={setShowExportDialog}
          onExport={handleWebCodecsExportWithSettings}
          defaultFps={60}
          canvasWidth={canvasRef.current?.width || 0}
          canvasHeight={canvasRef.current?.height || 0}
          isWebCodecsSupported={isWebCodecsSupported}
          isTauriApp={isTauriApp}
          isMultiGrid={true}
          gridCount={grids.length}
        />

        {/* WebCodecs Export Progress */}
        {isWebCodecsExporting && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-background border rounded-lg p-6 max-w-md w-full mx-4 space-y-4">
              {/* Header with phase-aware icon */}
              <div className="flex items-center gap-3">
                {webCodecsPhase === 'done' ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : webCodecsPhase === 'converting' ? (
                  <Video className="w-6 h-6 text-blue-500 animate-pulse" />
                ) : (
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                )}
                <div>
                  <h3 className="font-semibold text-lg">
                    {webCodecsPhase === 'done' 
                      ? t('export.progress.complete') || 'Export terminé !'
                      : webCodecsPhase === 'converting'
                        ? t('export.progress.converting') || 'Conversion...'
                        : webCodecsPhase === 'saving'
                          ? t('export.progress.saving') || 'Sauvegarde...'
                          : t('export.progress.encoding') || 'Export en cours...'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {webCodecsStatusMessage || webCodecsSelectedFormat}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="w-full bg-secondary rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-primary h-2.5 transition-all duration-300"
                    style={{ width: `${webCodecsProgress}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{Math.round(webCodecsProgress)}%</span>
                  {webCodecsPhase === 'encoding' && (
                    <span>
                      {webCodecsCurrentFrame} / {webCodecsTotalFrames} {t('export.progress.frames') || 'images'}
                    </span>
                  )}
                </div>
              </div>

              {/* Stats - only during encoding */}
              {webCodecsPhase === 'encoding' && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t('export.progress.speed') || 'Vitesse'}</p>
                    <p className="font-medium">{webCodecsExportSpeed.toFixed(1)} fps</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('export.progress.remaining') || 'Temps restant'}</p>
                    <p className="font-medium">
                      {webCodecsTimeRemaining < 60
                        ? `${Math.ceil(webCodecsTimeRemaining)}s`
                        : `${Math.ceil(webCodecsTimeRemaining / 60)}min`}
                    </p>
                  </div>
                </div>
              )}

              {/* Error message */}
              {webCodecsError && (
                <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{webCodecsError}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                {webCodecsPhase === 'done' ? (
                  <Button onClick={closeWebCodecsExport}>
                    {t('export.progress.close') || 'Fermer'}
                  </Button>
                ) : (
                  <Button variant="outline" onClick={cancelWebCodecsExport}>
                    {t('export.progress.cancel') || 'Annuler'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Export Individual Grids Progress Dialog */}
        {isExportingGrids && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-background border rounded-lg p-6 max-w-md w-full mx-4 space-y-4">
              {/* Header */}
              <div className="flex items-center gap-3">
                {gridsExportProgress === 100 ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                )}
                <div>
                  <h3 className="font-semibold text-lg">
                    {gridsExportProgress === 100
                      ? t('export.progress.complete') || 'Export terminé !'
                      : t('export.progress.individualGrids') || 'Export des grilles individuelles'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('export.progress.grid') || 'Grille'} {currentGridExporting} / {totalGridsToExport}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="w-full bg-secondary rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-2.5 transition-all duration-300 ${
                      gridsExportProgress === 100 ? 'bg-green-500' : 'bg-primary'
                    }`}
                    style={{ width: `${gridsExportProgress}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{Math.round(gridsExportProgress)}%</span>
                  <span>{t("export.progress.creatingZip") || "Archive ZIP en création..."}</span>
                </div>
              </div>

              {/* Success message */}
              {gridsExportProgress === 100 && (
                <div className="p-3 bg-green-500/10 border border-green-500/50 rounded-lg text-center">
                  <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                    {t("export.progress.gridsExportedInZip")?.replace("{count}", totalGridsToExport.toString()) || `${totalGridsToExport} grille(s) exportée(s) dans une archive ZIP`}
                  </p>
                </div>
              )}

              {/* Note - cannot cancel individual grid export */}
              {gridsExportProgress < 100 && (
                <p className="text-xs text-muted-foreground text-center">
                  {t("export.progress.dontModify") || "Veuillez ne pas modifier les paramètres pendant l'export..."}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Export PNG Choice Dialog */}
        {showExportPngDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-lg">
              <CardHeader>
                <CardTitle>{t("export.png.dialog.title") || "Choisir le mode d'export PNG"}</CardTitle>
                <CardDescription>
                  {t("export.png.dialog.description") || "Sélectionnez ce que vous souhaitez exporter"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  {/* Option 1: Full Canvas */}
                  <button
                    onClick={handleExportFullCanvas}
                    className="flex items-start gap-4 p-4 border rounded-lg hover:bg-accent hover:border-primary transition-colors text-left"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Grid className="h-6 w-6 text-blue-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base mb-1">
                        {t("export.png.dialog.fullCanvas") || "Canvas complet"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t("export.png.dialog.fullCanvas.desc") || "Exporter tout le canvas en une seule image PNG"}
                      </p>
                    </div>
                  </button>

                  {/* Option 2: Individual Grids */}
                  <button
                    onClick={handleExportAllGrids}
                    disabled={grids.length === 0}
                    className={`flex items-start gap-4 p-4 border rounded-lg transition-colors text-left ${
                      grids.length === 0
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-accent hover:border-primary"
                    }`}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <Grid className="h-6 w-6 text-purple-500" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base mb-1">
                        {t("export.png.dialog.individualGrids") || "Grilles individuelles"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t("export.png.dialog.individualGrids.desc") || "Exporter chaque grille séparément en fichiers PNG"}
                        {grids.length > 0 && (
                          <span className="block mt-1 text-xs font-medium text-primary">
                            ({grids.length} {grids.length === 1 ? t("export.dialog.grid") || "grille" : t("export.dialog.grids") || "grilles"})
                          </span>
                        )}
                      </p>
                      {grids.length === 0 && (
                        <p className="text-xs text-destructive mt-1">
                          {t("multigrid.noGrids") || "Aucune grille à exporter"}
                        </p>
                      )}
                    </div>
                  </button>
                </div>
              </CardContent>
              <div className="px-6 pb-4">
                <Button
                  variant="outline"
                  onClick={() => setShowExportPngDialog(false)}
                  className="w-full"
                >
                  {t("config.cancel") || "Annuler"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Import Saved Configs Dialog */}
        {showImportDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col">
              <CardHeader>
                <CardTitle>{t("config.savedConfigs") || "Configurations sauvegardées"}</CardTitle>
                <CardDescription>
                  {t("config.savedConfigsDesc") || "Choisissez une configuration à charger ou supprimez-en une"}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto">
                {savedConfigs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>{t("config.noSavedConfigs") || "Aucune configuration sauvegardée"}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {savedConfigs.map((config) => (
                      <div
                        key={config.name}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors"
                      >
                        <div className="flex-1">
                          {renamingConfigName === config.name ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={newConfigDisplayName}
                                onChange={(e) => setNewConfigDisplayName(e.target.value)}
                                placeholder={t("config.displayNamePlaceholder") || "Ex: Configuration principale"}
                                className="flex-1"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleRenameSavedConfig(config.name, newConfigDisplayName);
                                  } else if (e.key === "Escape") {
                                    setRenamingConfigName(null);
                                    setNewConfigDisplayName("");
                                  }
                                }}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRenameSavedConfig(config.name, newConfigDisplayName)}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setRenamingConfigName(null);
                                  setNewConfigDisplayName("");
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold">{config.displayName || config.name}</h3>
                                {config.isDefault && (
                                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                                    {t("config.default") || "Par défaut"}
                                  </span>
                                )}
                              </div>
                              {config.date && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {new Date(config.date).toLocaleDateString()}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                        {renamingConfigName !== config.name && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleLoadSavedConfig(config.name)}
                            >
                              {t("config.load") || "Charger"}
                            </Button>
                            {!config.isDefault && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSetDefaultConfig(config.name)}
                                title={t("config.setAsDefault") || "Définir comme défaut"}
                              >
                                <Star className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setRenamingConfigName(config.name);
                                setNewConfigDisplayName(config.displayName || config.name);
                              }}
                              title={t("config.rename") || "Renommer"}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteSavedConfig(config.name, config.displayName)}
                              title={t("config.delete") || "Supprimer"}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
              <div className="px-6 pb-4">
                <Button
                  variant="outline"
                  onClick={() => setShowImportDialog(false)}
                  className="w-full"
                >
                  {t("config.close") || "Fermer"}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Save Config Dialog */}
        {showSaveDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>
                  {isSaveAs 
                    ? (t("config.saveAsTitle") || "Sauvegarder sous un nouveau nom")
                    : (t("config.saveTitle") || "Sauvegarder la configuration")}
                </CardTitle>
                <CardDescription>
                  {isSaveAs
                    ? (t("config.saveAsDesc") || "Créez une nouvelle configuration avec un nom différent")
                    : (t("config.configName") || "Nommez votre configuration")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="configDisplayName">{t("config.displayName") || "Nom d'affichage"}</Label>
                  <Input
                    id="configDisplayName"
                    placeholder={t("config.displayNamePlaceholder") || "Ex: Configuration principale"}
                    value={configDisplayName}
                    onChange={(e) => setConfigDisplayName(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("config.displayNameDesc") || "Nom lisible pour la configuration"}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="makeDefault"
                    checked={makeDefaultConfig}
                    onCheckedChange={(checked) => setMakeDefaultConfig(!!checked)}
                  />
                  <Label htmlFor="makeDefault">{t("config.makeDefault") || "Définir comme configuration par défaut"}</Label>
                </div>
              </CardContent>
              <div className="flex justify-between p-4">
                <Button 
                  variant="outline" 
                  onClick={handleExportConfigFile}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {t("config.exportToFile") || "Exporter en .mgrd"}
                </Button>
                <div className="flex space-x-2">
                <Button variant="outline" onClick={() => {
                  setShowSaveDialog(false);
                  setIsSaveAs(false);
                }}>
                  {t("config.cancel") || "Annuler"}
                </Button>
                <Button onClick={handleSaveConfigConfirm}>
                  {t("config.save") || "Sauvegarder"}
                </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Dialogue d'édition du nom de grille depuis le canvas */}
        <Dialog open={showEditNameDialog} onOpenChange={setShowEditNameDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("multigrid.editGridName") || "Modifier le nom de la grille"}
              </DialogTitle>
              <DialogDescription>
                {t("multigrid.editGridNameDesc") || "Modifiez le nom de la grille qui sera affiché sur la mire."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editGridNameInput">
                  {t("multigrid.gridName") || "Nom"}
                </Label>
                <Input
                  id="editGridNameInput"
                  type="text"
                  value={editingGridName}
                  onChange={(e) => setEditingGridName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && editingGridName.trim() && editingGridIdFromCanvas) {
                      updateGrid(editingGridIdFromCanvas, { name: editingGridName.trim() });
                      renderCanvas();
                      setShowEditNameDialog(false);
                      setEditingGridIdFromCanvas(null);
                      setEditingGridName("");
                    } else if (e.key === "Escape") {
                      setShowEditNameDialog(false);
                      setEditingGridIdFromCanvas(null);
                      setEditingGridName("");
                    }
                  }}
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditNameDialog(false);
                  setEditingGridIdFromCanvas(null);
                  setEditingGridName("");
                }}
              >
                {t("config.cancel") || "Annuler"}
              </Button>
              <Button
                onClick={() => {
                  if (editingGridName.trim() && editingGridIdFromCanvas) {
                    updateGrid(editingGridIdFromCanvas, { name: editingGridName.trim() });
                    renderCanvas();
                    setShowEditNameDialog(false);
                    setEditingGridIdFromCanvas(null);
                    setEditingGridName("");
                  }
                }}
                disabled={!editingGridName.trim()}
              >
                {t("config.save") || "Sauvegarder"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </main>
    </ThemeProvider>
  );
}
